// ============================================================================
// INTELLISPOT ONTOLOGY SCHEMA DEFINITION
// Complete class and property definitions for the Market-Business-Solution triad
// ============================================================================

import type { OntologyClass, OntologyDomain, PropertyDefinition } from '@/types/ontology'
import { NAMESPACES } from './namespaces'

const { io, market, business, solution } = NAMESPACES

// ----------------------------------------------------------------------------
// COMMON PROPERTY HELPERS
// Reusable property definitions for consistency across entities
// ----------------------------------------------------------------------------

// Temporal properties - track when things happen
export const TEMPORAL_PROPERTIES = (domain: string): PropertyDefinition[] => [
  { uri: `${io}createdDate`, localName: 'createdDate', label: 'Created Date', type: 'data', domain, range: 'xsd:dateTime', description: 'When this entity was created' },
  { uri: `${io}updatedDate`, localName: 'updatedDate', label: 'Updated Date', type: 'data', domain, range: 'xsd:dateTime', description: 'When this entity was last updated' },
  { uri: `${io}startDate`, localName: 'startDate', label: 'Start Date', type: 'data', domain, range: 'xsd:dateTime', description: 'When this entity starts or started' },
  { uri: `${io}endDate`, localName: 'endDate', label: 'End Date', type: 'data', domain, range: 'xsd:dateTime', description: 'When this entity ends or ended' },
  { uri: `${io}dueDate`, localName: 'dueDate', label: 'Due Date', type: 'data', domain, range: 'xsd:dateTime', description: 'When this entity is due' },
]

// Status and priority properties - track state and importance
export const STATUS_PROPERTIES = (domain: string): PropertyDefinition[] => [
  { uri: `${io}status`, localName: 'status', label: 'Status', type: 'data', domain, range: 'xsd:string', description: 'Current status of this entity' },
  { uri: `${io}priority`, localName: 'priority', label: 'Priority', type: 'data', domain, range: 'xsd:string', description: 'Priority level (P0/P1/P2/P3 or Low/Medium/High/Critical)' },
  { uri: `${io}phase`, localName: 'phase', label: 'Phase', type: 'data', domain, range: 'xsd:string', description: 'Current phase or stage' },
]

// Quantitative properties - track effort and cost
export const QUANTITATIVE_PROPERTIES = (domain: string): PropertyDefinition[] => [
  { uri: `${io}estimatedCost`, localName: 'estimatedCost', label: 'Estimated Cost', type: 'data', domain, range: 'xsd:decimal', description: 'Estimated cost in currency' },
  { uri: `${io}actualCost`, localName: 'actualCost', label: 'Actual Cost', type: 'data', domain, range: 'xsd:decimal', description: 'Actual cost incurred' },
  { uri: `${io}estimatedEffort`, localName: 'estimatedEffort', label: 'Estimated Effort', type: 'data', domain, range: 'xsd:decimal', description: 'Estimated effort in story points or hours' },
  { uri: `${io}actualEffort`, localName: 'actualEffort', label: 'Actual Effort', type: 'data', domain, range: 'xsd:decimal', description: 'Actual effort spent' },
]

// Descriptive properties - add context and notes
export const DESCRIPTIVE_PROPERTIES = (domain: string): PropertyDefinition[] => [
  { uri: `${io}description`, localName: 'description', label: 'Description', type: 'data', domain, range: 'xsd:string', description: 'Detailed description' },
  { uri: `${io}rationale`, localName: 'rationale', label: 'Rationale', type: 'data', domain, range: 'xsd:string', description: 'Why this entity exists or was created' },
  { uri: `${io}notes`, localName: 'notes', label: 'Notes', type: 'data', domain, range: 'xsd:string', description: 'Additional notes or comments' },
  { uri: `${io}tags`, localName: 'tags', label: 'Tags', type: 'data', domain, range: 'xsd:string', description: 'Tags for categorization' },
]

// Ownership properties - track responsibility
export const OWNERSHIP_PROPERTIES = (domain: string): PropertyDefinition[] => [
  { uri: `${io}owner`, localName: 'owner', label: 'Owner', type: 'data', domain, range: 'xsd:string', description: 'Entity owner' },
  { uri: `${io}assignedTo`, localName: 'assignedTo', label: 'Assigned To', type: 'data', domain, range: 'xsd:string', description: 'Who this is assigned to' },
  { uri: `${io}createdBy`, localName: 'createdBy', label: 'Created By', type: 'data', domain, range: 'xsd:string', description: 'Who created this entity' },
  { uri: `${io}modifiedBy`, localName: 'modifiedBy', label: 'Modified By', type: 'data', domain, range: 'xsd:string', description: 'Who last modified this entity' },
]

// ----------------------------------------------------------------------------
// CORE CLASSES (Cross-domain: Facets and Sources)
// ----------------------------------------------------------------------------

export const CORE_CLASSES: OntologyClass[] = [
  {
    uri: `${io}Facet`,
    localName: 'Facet',
    label: 'Facet',
    domain: 'core' as OntologyDomain,
    description: 'An extracted insight from a source document that may describe one or more entities',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${io}Facet`),
      { uri: `${io}facetType`, localName: 'facetType', label: 'Facet Type', type: 'data', domain: `${io}Facet`, range: 'xsd:string', description: 'The type of facet (e.g., problem, feature_request)' },
      { uri: `${io}content`, localName: 'content', label: 'Content', type: 'data', domain: `${io}Facet`, range: 'xsd:string', description: 'The extracted content/summary' },
      { uri: `${io}evidence`, localName: 'evidence', label: 'Evidence', type: 'data', domain: `${io}Facet`, range: 'xsd:string', description: 'Direct quote from source document' },
      { uri: `${io}confidence`, localName: 'confidence', label: 'Confidence', type: 'data', domain: `${io}Facet`, range: 'xsd:decimal', description: 'AI confidence score 0-1' },
      { uri: `${io}extractedFrom`, localName: 'extractedFrom', label: 'Extracted From', type: 'object', domain: `${io}Facet`, range: `${io}Source`, description: 'The source document this facet was extracted from' },
      { uri: `${io}describesEntity`, localName: 'describesEntity', label: 'Describes Entity', type: 'object', domain: `${io}Facet`, range: 'owl:Thing', description: 'The entity this facet describes (many-to-one relationship)' },
      { uri: `${io}positionStart`, localName: 'positionStart', label: 'Position Start', type: 'data', domain: `${io}Facet`, range: 'xsd:integer', description: 'Start position in source text' },
      { uri: `${io}positionEnd`, localName: 'positionEnd', label: 'Position End', type: 'data', domain: `${io}Facet`, range: 'xsd:integer', description: 'End position in source text' },
      { uri: `${io}status`, localName: 'status', label: 'Status', type: 'data', domain: `${io}Facet`, range: 'xsd:string', description: 'Review status (pending|accepted|rejected|mapped)' },
      { uri: `${io}createdAt`, localName: 'createdAt', label: 'Created At', type: 'data', domain: `${io}Facet`, range: 'xsd:dateTime', description: 'When this facet was extracted' },
    ],
    educational: {
      whatIsThis: 'A facet is an insight or piece of information extracted from a source document by AI analysis. Multiple facets can describe the same entity, enabling many-to-one mapping.',
      whenToUse: [
        'When analyzing interview transcripts',
        'When extracting insights from documents',
        'When linking evidence to entities',
      ],
      goodExamples: [
        'Problem facet: "Users complain about slow response times"',
        'Feature request facet: "Need ability to export to PDF"',
        'Competitor mention: "They switched from Competitor X"',
      ],
      badExamples: [
        'Empty content without evidence',
        'Facet without source linkage',
      ],
      discoveryPhase: 'Analysis',
      relatedConcepts: ['Source', 'Problem', 'Feature'],
    },
  },
  {
    uri: `${io}Source`,
    localName: 'Source',
    label: 'Source',
    domain: 'core' as OntologyDomain,
    description: 'A document, interview, or other source of extracted insights',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${io}Source`),
      { uri: `${io}title`, localName: 'title', label: 'Title', type: 'data', domain: `${io}Source`, range: 'xsd:string', description: 'Source document title' },
      { uri: `${io}contentType`, localName: 'contentType', label: 'Content Type', type: 'data', domain: `${io}Source`, range: 'xsd:string', description: 'Type of content (user_interview, requirements_spec, etc.)' },
      { uri: `${io}sourceUri`, localName: 'sourceUri', label: 'Source URI', type: 'data', domain: `${io}Source`, range: 'xsd:anyURI', description: 'Original source location' },
      { uri: `${io}analyzedAt`, localName: 'analyzedAt', label: 'Analyzed At', type: 'data', domain: `${io}Source`, range: 'xsd:dateTime', description: 'When analysis was performed' },
    ],
    educational: {
      whatIsThis: 'A source is a document or recording from which facets are extracted. It provides provenance for all linked facets and entities.',
      whenToUse: [
        'When uploading interview transcripts',
        'When importing requirements documents',
        'When tracking evidence sources',
      ],
      goodExamples: [
        'User Interview #5 with Product Manager',
        'Q3 Customer Survey Results',
        'Competitor Analysis Report',
      ],
      badExamples: [
        'Untitled document',
        'Source without content',
      ],
      discoveryPhase: 'Analysis',
      relatedConcepts: ['Facet', 'Persona', 'Problem'],
    },
  },
  {
    uri: `${io}ValidationAnnotation`,
    localName: 'ValidationAnnotation',
    label: 'Validation Annotation',
    domain: 'core' as OntologyDomain,
    description: 'SWRL validation properties that can be attached to any entity to indicate rule violations or inferred classifications',
    properties: [
      { uri: `${io}hasViolation`, localName: 'hasViolation', label: 'Has Violation', type: 'data', domain: 'owl:Thing', range: 'xsd:string', description: 'Violation type identifier (e.g., incompletePain, orphanedFeature)' },
      { uri: `${io}violationMessage`, localName: 'violationMessage', label: 'Violation Message', type: 'data', domain: 'owl:Thing', range: 'xsd:string', description: 'Human-readable validation error message' },
      { uri: `${io}suggestedFix`, localName: 'suggestedFix', label: 'Suggested Fix', type: 'data', domain: 'owl:Thing', range: 'xsd:string', description: 'Suggested remediation action for the violation' },
      { uri: `${io}violationSeverity`, localName: 'violationSeverity', label: 'Violation Severity', type: 'data', domain: 'owl:Thing', range: 'xsd:string', description: 'Severity level: error, warning, or info' },
      { uri: `${io}inferredProperty`, localName: 'inferredProperty', label: 'Inferred Property', type: 'data', domain: 'owl:Thing', range: 'xsd:string', description: 'Property inferred by SWRL inference rules' },
      { uri: `${io}hasHighPriority`, localName: 'hasHighPriority', label: 'Has High Priority', type: 'data', domain: 'owl:Thing', range: 'xsd:boolean', description: 'Entity has high priority (inferred from rules)' },
      { uri: `${io}hasStrategicUrgency`, localName: 'hasStrategicUrgency', label: 'Has Strategic Urgency', type: 'data', domain: 'owl:Thing', range: 'xsd:boolean', description: 'Entity has strategic urgency (inferred from hard goals)' },
      { uri: `${io}hasMarketUrgency`, localName: 'hasMarketUrgency', label: 'Has Market Urgency', type: 'data', domain: 'owl:Thing', range: 'xsd:boolean', description: 'Entity has market urgency (inferred from maturing trends)' },
    ],
    educational: {
      whatIsThis: 'Validation annotations are dynamically added by SWRL rules to indicate validation violations, inferred classifications, or derived properties.',
      whenToUse: [
        'Automatically added by validation engine',
        'Used to display inline warnings in UI',
        'Enables auto-fix suggestions',
      ],
      goodExamples: [
        'Feature with hasViolation="unvalidatedFeature" needs pain linkage',
        'Pain inferred as HighValuePain when severe + shared by 2+ personas',
        'Epic gets hasStrategicUrgency=true from hard goal propagation',
      ],
      badExamples: [
        'Manually adding validation properties (should be rule-driven)',
      ],
      discoveryPhase: 'Analysis',
      relatedConcepts: ['EntityRule', 'RuleViolation'],
    },
  },
  // Artifact - workspace asset with dual role (source OR deliverable)
  {
    uri: `${io}Artifact`,
    localName: 'Artifact',
    label: 'Artifact',
    domain: 'core' as OntologyDomain,
    description: 'A workspace artifact - either evidence source (input) or knowledge deliverable (output)',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${io}Artifact`),
      // Classification
      { uri: `${io}artifactType`, localName: 'artifactType', label: 'Artifact Type', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'Canvas | Doc | Plan | Evidence' },
      { uri: `${io}artifactRole`, localName: 'artifactRole', label: 'Artifact Role', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'source | deliverable | hybrid' },
      { uri: `${io}artifactDomain`, localName: 'artifactDomain', label: 'Artifact Domain', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'Market | Business | Solution' },
      { uri: `${io}subType`, localName: 'subType', label: 'Sub Type', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'Specific artifact subtype (e.g., BMC, VPC, Interview Transcript)' },
      // Metadata
      { uri: `${io}artifactTitle`, localName: 'artifactTitle', label: 'Title', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'Artifact display title' },
      { uri: `${io}artifactStatus`, localName: 'artifactStatus', label: 'Status', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'draft | active | archived' },
      { uri: `${io}artifactVersion`, localName: 'artifactVersion', label: 'Version', type: 'data', domain: `${io}Artifact`, range: 'xsd:integer', description: 'Artifact version number' },
      // Source role properties (input artifacts)
      { uri: `${io}artifactContentType`, localName: 'artifactContentType', label: 'Content Type', type: 'data', domain: `${io}Artifact`, range: 'xsd:string', description: 'MIME type or content classification' },
      { uri: `${io}analyzedAt`, localName: 'analyzedAt', label: 'Analyzed At', type: 'data', domain: `${io}Artifact`, range: 'xsd:dateTime', description: 'When this artifact was analyzed for facets' },
      { uri: `${io}extractedFacets`, localName: 'extractedFacets', label: 'Extracted Facets', type: 'object', domain: `${io}Artifact`, range: `${io}Facet`, description: 'Facets extracted from this source artifact' },
      { uri: `${io}artifactFacetCount`, localName: 'artifactFacetCount', label: 'Facet Count', type: 'data', domain: `${io}Artifact`, range: 'xsd:integer', description: 'Number of facets extracted from this artifact' },
      // Deliverable role properties (output artifacts)
      { uri: `${io}synthesizesFrom`, localName: 'synthesizesFrom', label: 'Synthesizes From', type: 'object', domain: `${io}Artifact`, range: `${io}Artifact`, description: 'Source artifacts this deliverable synthesizes' },
      { uri: `${io}linkedEntities`, localName: 'linkedEntities', label: 'Linked Entities', type: 'object', domain: `${io}Artifact`, range: 'owl:Thing', description: 'Entities linked to this artifact' },
      { uri: `${io}completenessScore`, localName: 'completenessScore', label: 'Completeness Score', type: 'data', domain: `${io}Artifact`, range: 'xsd:float', description: 'How complete this deliverable is (0-100)' },
      // Traceability
      { uri: `${io}derivedFrom`, localName: 'derivedFrom', label: 'Derived From', type: 'object', domain: `${io}Artifact`, range: `${io}Artifact`, description: 'Previous artifact version this was derived from' },
      { uri: `${io}supersedes`, localName: 'supersedes', label: 'Supersedes', type: 'object', domain: `${io}Artifact`, range: `${io}Artifact`, description: 'Artifact this one replaces' },
    ],
    educational: {
      whatIsThis: 'An artifact is a workspace asset that either provides evidence (source) or captures decisions (deliverable). Sources like interview transcripts are analyzed for facets. Deliverables like canvases synthesize knowledge into strategic decisions.',
      whenToUse: [
        'Uploading research documents for analysis (source role)',
        'Creating canvases and strategic plans (deliverable role)',
        'Tracking evidence provenance and decision history',
      ],
      goodExamples: [
        'Source: Interview transcript with CFO - analyzed for pain points',
        'Source: Survey responses - extracted customer jobs',
        'Deliverable: Business Model Canvas - synthesizes 12 entity types',
        'Deliverable: Positioning document - links to value propositions',
      ],
      badExamples: [
        'Artifact without role classification',
        'Deliverable without linked entities',
        'Source without analysis',
      ],
      discoveryPhase: 'All phases',
      relatedConcepts: ['Source', 'Facet', 'Interview', 'Canvas'],
    },
  },
]

// ----------------------------------------------------------------------------
// MARKET DOMAIN CLASSES
// ----------------------------------------------------------------------------

export const MARKET_CLASSES: OntologyClass[] = [
  {
    uri: `${market}Industry`,
    localName: 'Industry',
    label: 'Industry',
    domain: 'market',
    description: 'An economic sector defined by NAICS/SIC codes',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Industry`),
      { uri: `${market}hasNAICSCode`, localName: 'hasNAICSCode', label: 'NAICS Code', type: 'data', domain: `${market}Industry`, range: 'xsd:string' },
      { uri: `${market}hasSICCode`, localName: 'hasSICCode', label: 'SIC Code', type: 'data', domain: `${market}Industry`, range: 'xsd:string' },
      { uri: `${market}hasMarketSize`, localName: 'hasMarketSize', label: 'Market Size', type: 'data', domain: `${market}Industry`, range: 'xsd:decimal' },
      { uri: `${market}hasGrowthRate`, localName: 'hasGrowthRate', label: 'Growth Rate', type: 'data', domain: `${market}Industry`, range: 'xsd:float' },
    ],
    educational: {
      whatIsThis: 'An industry is an economic sector where similar products or services are produced. It helps you understand the market landscape and competitive dynamics.',
      whenToUse: [
        'Starting market research for a new venture',
        'Analyzing the competitive landscape',
        'Understanding market size and growth potential',
      ],
      goodExamples: [
        'Enterprise SaaS (NAICS 511210)',
        'FinTech / Digital Banking (NAICS 522110)',
        'Healthcare IT (NAICS 541512)',
      ],
      badExamples: [
        '"Technology" (too broad)',
        '"Startups" (not an industry)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Competitor', 'Trend', 'CustomerSegment'],
    },
  },
  {
    uri: `${market}Competitor`,
    localName: 'Competitor',
    label: 'Competitor',
    domain: 'market',
    description: 'A competing company in the market',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Competitor`),
      { uri: `${market}employsStrategy`, localName: 'employsStrategy', label: 'Strategy', type: 'data', domain: `${market}Competitor`, range: 'xsd:string', description: 'CostLeadership | Differentiation | Focus' },
      { uri: `${market}hasMarketShare`, localName: 'hasMarketShare', label: 'Market Share', type: 'data', domain: `${market}Competitor`, range: 'xsd:float' },
      { uri: `${market}exertsPressure`, localName: 'exertsPressure', label: 'Exerts Pressure', type: 'object', domain: `${market}Competitor`, range: `${market}CompetitiveForce` },
    ],
    educational: {
      whatIsThis: 'A competitor is any company that offers products or services that could satisfy the same customer needs. Understanding competitors helps position your value proposition.',
      whenToUse: [
        'Conducting competitive analysis',
        'Identifying differentiation opportunities',
        'Understanding market positioning',
      ],
      goodExamples: [
        'Salesforce (CRM market leader, differentiation strategy)',
        'HubSpot (mid-market CRM, freemium strategy)',
        'Pipedrive (SMB focus, ease-of-use differentiation)',
      ],
      badExamples: [
        '"All software companies" (too broad)',
        'Companies in unrelated markets',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Industry', 'CompetitiveForce', 'ValueProposition'],
    },
  },
  {
    uri: `${market}CompetitiveForce`,
    localName: 'CompetitiveForce',
    label: 'Competitive Force',
    domain: 'market',
    description: "Porter's Five Forces - external pressure on industry profitability",
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}CompetitiveForce`),
      // Porter's Five Forces classification
      { uri: `${market}forceType`, localName: 'forceType', label: 'Force Type', type: 'data', domain: `${market}CompetitiveForce`, range: 'xsd:string', description: 'Rivalry | NewEntrants | Substitutes | BuyerPower | SupplierPower' },
      { uri: `${market}intensity`, localName: 'intensity', label: 'Intensity', type: 'data', domain: `${market}CompetitiveForce`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${market}trendDirection`, localName: 'trendDirection', label: 'Trend Direction', type: 'data', domain: `${market}CompetitiveForce`, range: 'xsd:string', description: 'Increasing | Stable | Decreasing' },
      { uri: `${market}impactedBy`, localName: 'impactedBy', label: 'Impacted By', type: 'object', domain: `${market}CompetitiveForce`, range: `${market}Trend`, description: 'External trends affecting this force' },
      { uri: `${market}affectsCompetitor`, localName: 'affectsCompetitor', label: 'Affects Competitor', type: 'object', domain: `${market}CompetitiveForce`, range: `${market}Competitor`, description: 'Competitors affected by this force' },
    ],
    educational: {
      whatIsThis: "Porter's Five Forces framework analyzes competitive intensity. Forces include: rivalry, threat of new entrants, supplier power, buyer power, and threat of substitutes.",
      whenToUse: [
        'Assessing industry attractiveness',
        'Understanding competitive dynamics',
        'Strategic planning and positioning',
      ],
      goodExamples: [
        'High rivalry: mature SaaS markets with many offerings',
        'Supplier power: cloud providers controlling costs',
        'Buyer power: enterprise customers with leverage',
      ],
      badExamples: [
        'Internal company factors (those are strengths/weaknesses)',
        'One-time market events',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Competitor', 'Industry', 'Threat'],
    },
  },
  {
    uri: `${market}Trend`,
    localName: 'Trend',
    label: 'Trend',
    domain: 'market',
    description: 'A direction of change in the market',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Trend`),
      { uri: `${market}hasMaturity`, localName: 'hasMaturity', label: 'Maturity', type: 'data', domain: `${market}Trend`, range: 'xsd:string', description: 'Emerging | Growth | Maturing | Declining' },
      { uri: `${market}drivenBy`, localName: 'drivenBy', label: 'Driven By', type: 'data', domain: `${market}Trend`, range: 'xsd:string' },
      { uri: `${market}impactsIndustry`, localName: 'impactsIndustry', label: 'Impacts Industry', type: 'object', domain: `${market}Trend`, range: `${market}Industry` },
    ],
    educational: {
      whatIsThis: 'A trend represents a sustained direction of change in technology, customer behavior, or market conditions. Trends create opportunities and threats.',
      whenToUse: [
        'Identifying market opportunities',
        'Future-proofing your strategy',
        'Understanding shifting customer expectations',
      ],
      goodExamples: [
        'AI/ML adoption in enterprise software (Growth)',
        'Remote-first work culture (Maturing)',
        'Sustainability-driven purchasing (Emerging)',
      ],
      badExamples: [
        '"Technology" (not specific enough)',
        'One-time events or fads',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['MarketSignal', 'Industry', 'Opportunity'],
    },
  },
  {
    uri: `${market}MarketSignal`,
    localName: 'MarketSignal',
    label: 'Market Signal',
    domain: 'market',
    description: 'An indicator of market change (weak signal, strong signal, or wildcard)',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}MarketSignal`),
      { uri: `${market}hasSource`, localName: 'hasSource', label: 'Source', type: 'data', domain: `${market}MarketSignal`, range: 'xsd:string' },
      { uri: `${market}hasSignalStrength`, localName: 'hasSignalStrength', label: 'Signal Strength', type: 'data', domain: `${market}MarketSignal`, range: 'xsd:integer', description: '1-10' },
      { uri: `${market}indicatesTrend`, localName: 'indicatesTrend', label: 'Indicates Trend', type: 'object', domain: `${market}MarketSignal`, range: `${market}Trend` },
    ],
    educational: {
      whatIsThis: 'A market signal is early evidence of an emerging change. Weak signals are early hints, strong signals are clear indicators, wildcards are unpredictable high-impact events.',
      whenToUse: [
        'Monitoring market evolution',
        'Early detection of opportunities/threats',
        'Strategic foresight planning',
      ],
      goodExamples: [
        'Weak: Niche AI startup raises Series A in your space',
        'Strong: Major competitor announces pivot to AI',
        'Wildcard: Regulatory change affecting entire industry',
      ],
      badExamples: [
        'Internal company metrics',
        'Unverified rumors without sources',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Trend', 'Opportunity', 'Threat'],
    },
  },
  {
    uri: `${market}CustomerSegment`,
    localName: 'CustomerSegment',
    label: 'Customer Segment',
    domain: 'market',
    description: 'A homogeneous group of customers with similar needs',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerSegment`),
      { uri: `${market}hasProfile`, localName: 'hasProfile', label: 'Profile', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:string' },
      { uri: `${market}locatedIn`, localName: 'locatedIn', label: 'Location', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:string' },
      // Market sizing and economics
      { uri: `${market}estimatedSize`, localName: 'estimatedSize', label: 'Estimated Size', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:integer', description: 'Estimated number of potential customers' },
      { uri: `${market}growthRate`, localName: 'growthRate', label: 'Growth Rate', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:float', description: 'Annual segment growth rate (0-1)' },
      { uri: `${market}penetrationRate`, localName: 'penetrationRate', label: 'Penetration Rate', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:float', description: 'Current market penetration (0-1)' },
      { uri: `${market}avgDealSize`, localName: 'avgDealSize', label: 'Average Deal Size', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:decimal', description: 'Average deal size in currency' },
      { uri: `${market}salesCycleLength`, localName: 'salesCycleLength', label: 'Sales Cycle Length', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:integer', description: 'Average sales cycle in days' },
      { uri: `${market}acquisitionCost`, localName: 'acquisitionCost', label: 'Acquisition Cost', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:decimal', description: 'CAC for this segment' },
      { uri: `${market}lifetimeValue`, localName: 'lifetimeValue', label: 'Lifetime Value', type: 'data', domain: `${market}CustomerSegment`, range: 'xsd:decimal', description: 'LTV for this segment' },
    ],
    educational: {
      whatIsThis: 'A customer segment is a group of customers with similar characteristics, needs, and behaviors. Segmentation helps focus your value proposition on specific targets.',
      whenToUse: [
        'Defining your target market',
        'Before creating Personas',
        'Prioritizing which customers to serve',
      ],
      goodExamples: [
        'Mid-market CFOs at companies with 50-200 employees in DACH',
        'E-commerce retailers with €1-5M revenue',
        'Series A B2B SaaS founders',
      ],
      badExamples: [
        '"Small businesses" (too vague)',
        '"Everyone who uses software" (too broad)',
        '"People who like quality" (not actionable)',
      ],
      discoveryPhase: 'Customer Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Persona', 'ValueProposition', 'BusinessModel'],
    },
  },
  {
    uri: `${market}Persona`,
    localName: 'Persona',
    label: 'Persona',
    domain: 'market',
    description: 'An archetypal user or buyer representation',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Persona`),
      { uri: `${market}representsSegment`, localName: 'representsSegment', label: 'Represents Segment', type: 'object', domain: `${market}Persona`, range: `${market}CustomerSegment` },
      { uri: `${market}hasPain`, localName: 'hasPain', label: 'Has Pain', type: 'object', domain: `${market}Persona`, range: `${market}CustomerPain` },
      { uri: `${market}hasGain`, localName: 'hasGain', label: 'Has Gain', type: 'object', domain: `${market}Persona`, range: `${market}CustomerGain` },
      { uri: `${market}hasJob`, localName: 'hasJob', label: 'Has Job', type: 'object', domain: `${market}Persona`, range: `${market}CustomerJob` },
      // Persona characteristics
      { uri: `${market}personaName`, localName: 'personaName', label: 'Persona Name', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'Display name for this persona (e.g., "CFO Clara")' },
      { uri: `${market}roleTitle`, localName: 'roleTitle', label: 'Role Title', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'Job title or role' },
      { uri: `${market}decisionAuthority`, localName: 'decisionAuthority', label: 'Decision Authority', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'DecisionMaker | Influencer | User | Gatekeeper' },
      { uri: `${market}techSavviness`, localName: 'techSavviness', label: 'Tech Savviness', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${market}primaryMotivation`, localName: 'primaryMotivation', label: 'Primary Motivation', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'What drives this persona?' },
      { uri: `${market}keyObjection`, localName: 'keyObjection', label: 'Key Objection', type: 'data', domain: `${market}Persona`, range: 'xsd:string', description: 'Main objection to purchasing' },
      { uri: `${market}preferredChannel`, localName: 'preferredChannel', label: 'Preferred Channel', type: 'object', domain: `${market}Persona`, range: `${business}Channel`, description: 'Preferred communication channel' },
    ],
    educational: {
      whatIsThis: 'A persona is a fictional but realistic representation of your ideal customer. It humanizes segment data and guides product decisions with empathy.',
      whenToUse: [
        'After defining Customer Segments',
        'Creating user stories and features',
        'Communicating customer needs to the team',
      ],
      goodExamples: [
        'CFO Clara: 45, manages 3-person finance team, frustrated by manual reconciliation',
        'Startup Steve: First-time founder, needs to move fast, budget-conscious',
      ],
      badExamples: [
        '"Male, 25-45" (demographics only, no goals/pains)',
        '"Tech-savvy user" (too generic)',
      ],
      discoveryPhase: 'Customer Discovery',
      discoveryRequired: true,
      relatedConcepts: ['CustomerSegment', 'CustomerPain', 'CustomerJob', 'UserStory'],
    },
  },
  {
    uri: `${market}Person`,
    localName: 'Person',
    label: 'Person',
    domain: 'market',
    description: 'A real individual involved in the discovery process - could be an interviewee, stakeholder, decision-maker, or customer contact',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Person`),
      { uri: `${market}hasRole`, localName: 'hasRole', label: 'Role', type: 'data', domain: `${market}Person`, range: 'xsd:string', description: 'The role or title of this person' },
      { uri: `${market}hasOrganization`, localName: 'hasOrganization', label: 'Organization', type: 'data', domain: `${market}Person`, range: 'xsd:string', description: 'The organization this person belongs to' },
      { uri: `${market}representedByPersona`, localName: 'representedByPersona', label: 'Represented By Persona', type: 'object', domain: `${market}Person`, range: `${market}Persona` },
      { uri: `${market}belongsToSegment`, localName: 'belongsToSegment', label: 'Belongs To Segment', type: 'object', domain: `${market}Person`, range: `${market}CustomerSegment` },
      { uri: `${market}hasPain`, localName: 'hasPain', label: 'Has Pain', type: 'object', domain: `${market}Person`, range: `${market}CustomerPain` },
      { uri: `${market}hasJob`, localName: 'hasJob', label: 'Has Job', type: 'object', domain: `${market}Person`, range: `${market}CustomerJob` },
    ],
    educational: {
      whatIsThis: 'A Person represents a real individual you have interacted with during customer discovery. Unlike Personas (which are fictional archetypes), Persons are actual interviewees, stakeholders, or contacts.',
      whenToUse: [
        'Recording interview participants',
        'Tracking stakeholder inputs',
        'Linking insights to specific individuals',
        'Building relationships over multiple conversations',
      ],
      goodExamples: [
        'Geoffrey - VP of Sales at Acme Corp',
        'Sarah Chen - Customer Success Manager',
        'Dr. James Wilson - Technical Decision Maker',
      ],
      badExamples: [
        '"The customer" (too vague)',
        '"Someone from marketing" (no specific identity)',
      ],
      discoveryPhase: 'Customer Discovery',
      discoveryRequired: false,
      relatedConcepts: ['Persona', 'CustomerSegment', 'CustomerPain'],
    },
  },
  {
    uri: `${market}Stakeholder`,
    localName: 'Stakeholder',
    label: 'Stakeholder',
    domain: 'market',
    description: 'An individual or group with interest in, influence over, or dependency on a product decision or outcome',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Stakeholder`),
      { uri: `${market}hasStakeholderType`, localName: 'hasStakeholderType', label: 'Stakeholder Type', type: 'data', domain: `${market}Stakeholder`, range: 'xsd:string', description: 'Internal | External | Customer | Partner | Regulatory' },
      { uri: `${market}hasInfluenceLevel`, localName: 'hasInfluenceLevel', label: 'Influence Level', type: 'data', domain: `${market}Stakeholder`, range: 'xsd:string', description: 'High | Medium | Low' },
      { uri: `${market}hasInterest`, localName: 'hasInterest', label: 'Interest', type: 'data', domain: `${market}Stakeholder`, range: 'xsd:string', description: 'What does this stakeholder care about?' },
      { uri: `${market}representedBy`, localName: 'representedBy', label: 'Represented By', type: 'object', domain: `${market}Stakeholder`, range: `${market}Person` },
    ],
    educational: {
      whatIsThis: 'A stakeholder is anyone who has a stake in your product decisions - they may influence decisions, be affected by outcomes, or have approval authority.',
      whenToUse: [
        'Mapping decision-making authority',
        'Identifying who to involve in discovery',
        'Understanding approval chains',
        'Managing expectations and communication',
      ],
      goodExamples: [
        'IT Security Team - must approve all new software',
        'CFO - controls budget for new tools',
        'End Users - will be affected by the solution',
      ],
      badExamples: [
        '"Management" (too broad)',
        '"Everyone" (not specific)',
      ],
      discoveryPhase: 'Customer Discovery',
      discoveryRequired: false,
      relatedConcepts: ['Person', 'Decision', 'Constraint'],
    },
  },
  {
    uri: `${market}Problem`,
    localName: 'Problem',
    label: 'Problem',
    domain: 'market',
    description: 'A fundamental issue, challenge, or gap in functionality or process that exists objectively in the customer\'s environment',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Problem`),
      { uri: `${market}hasCategory`, localName: 'hasCategory', label: 'Category', type: 'data', domain: `${market}Problem`, range: 'xsd:string', description: 'Process | Technology | Knowledge | Resource | Communication' },
      { uri: `${market}hasScope`, localName: 'hasScope', label: 'Scope', type: 'data', domain: `${market}Problem`, range: 'xsd:string', description: 'Individual | Team | Organization | Industry' },
      { uri: `${market}hasContext`, localName: 'hasContext', label: 'Context', type: 'data', domain: `${market}Problem`, range: 'xsd:string', description: 'When/where does this problem occur?' },
      { uri: `${market}hasFrequency`, localName: 'hasFrequency', label: 'Frequency', type: 'data', domain: `${market}Problem`, range: 'xsd:string', description: 'Rare | Occasional | Frequent | Constant' },
      { uri: `${market}hasRootCause`, localName: 'hasRootCause', label: 'Root Cause', type: 'data', domain: `${market}Problem`, range: 'xsd:string' },
      { uri: `${market}causesPain`, localName: 'causesPain', label: 'Causes Pain', type: 'object', domain: `${market}Problem`, range: `${market}CustomerPain` },
      { uri: `${market}affectsJob`, localName: 'affectsJob', label: 'Affects Job', type: 'object', domain: `${market}Problem`, range: `${market}CustomerJob` },
      { uri: `${market}affectsSegment`, localName: 'affectsSegment', label: 'Affects Segment', type: 'object', domain: `${market}Problem`, range: `${market}CustomerSegment`, description: 'Who has this problem most often?' },
    ],
    educational: {
      whatIsThis: 'A problem is the objective situation, obstacle, or gap that exists in the customer\'s environment. Unlike CustomerPain (the emotional/financial consequence), a Problem describes the root issue itself.',
      whenToUse: [
        'Identifying root causes before symptoms',
        'Structuring problem-solution fit analysis',
        'Distinguishing objective issues from subjective experiences',
      ],
      goodExamples: [
        'No automated reconciliation between bank feeds and invoices (Process)',
        'Data silos between sales CRM and finance systems (Technology)',
        'Manual approval workflows with no audit trail (Process)',
      ],
      badExamples: [
        '"Frustrated with data entry" (that\'s a Pain, not Problem)',
        '"Wants better software" (that\'s a solution desire)',
        '"Unhappy with current tool" (that\'s a Pain)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: true,
      relatedConcepts: ['CustomerPain', 'CustomerJob', 'Persona', 'ValueProposition'],
    },
  },
  {
    uri: `${market}CustomerPain`,
    localName: 'CustomerPain',
    label: 'Customer Pain',
    domain: 'market',
    description: 'The negative experience, discomfort, frustration, or cost a customer feels because of a Problem',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerPain`),
      { uri: `${market}causedBy`, localName: 'causedBy', label: 'Caused By', type: 'object', domain: `${market}CustomerPain`, range: `${market}Problem` },
      { uri: `${market}hasPainType`, localName: 'hasPainType', label: 'Pain Type', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:string', description: 'emotional | quantifiable | operational' },
      { uri: `${market}hasSeverity`, localName: 'hasSeverity', label: 'Severity', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:string' },
      { uri: `${market}hasEmotionalImpact`, localName: 'hasEmotionalImpact', label: 'Emotional Impact', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:string', description: 'How does the customer feel?' },
      { uri: `${market}hasQuantifiableImpact`, localName: 'hasQuantifiableImpact', label: 'Quantifiable Impact', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:string', description: 'Measurable impact with units (e.g., "40 hours/month")' },
      // Evidence strength tracking
      { uri: `${market}mentionCount`, localName: 'mentionCount', label: 'Mention Count', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:integer', description: 'How many times mentioned across interviews' },
      { uri: `${market}segmentCoverage`, localName: 'segmentCoverage', label: 'Segment Coverage', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:integer', description: 'How many segments report this' },
      { uri: `${market}evidenceStrength`, localName: 'evidenceStrength', label: 'Evidence Strength', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:string', description: 'None | Weak (1-2) | Moderate (3-5) | Strong (6+)' },
      { uri: `${market}firstMentionedDate`, localName: 'firstMentionedDate', label: 'First Mentioned Date', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:dateTime', description: 'When this pain was first mentioned' },
      { uri: `${market}lastValidatedDate`, localName: 'lastValidatedDate', label: 'Last Validated Date', type: 'data', domain: `${market}CustomerPain`, range: 'xsd:dateTime', description: 'When this pain was last validated' },
    ],
    educational: {
      whatIsThis: 'A customer pain is the negative experience or consequence that results from a Problem. Unlike Problem (the objective situation), Pain captures the emotional, financial, or operational impact on the customer.',
      whenToUse: [
        'Validating problem-solution fit',
        'Prioritizing features to build',
        'Crafting compelling marketing messages',
      ],
      goodExamples: [
        'Spending 4+ hours weekly on manual data entry (High)',
        'Losing deals due to slow quote turnaround (High)',
        'Difficulty comparing vendor options (Medium)',
      ],
      badExamples: [
        '"No automated reconciliation" (that\'s the Problem, not Pain)',
        '"Wants better software" (solution, not pain)',
        '"Needs AI" (technology, not pain)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Problem', 'Persona', 'ValueProposition', 'Feature'],
    },
  },
  {
    uri: `${market}CustomerGain`,
    localName: 'CustomerGain',
    label: 'Customer Gain',
    domain: 'market',
    description: 'A desired outcome or benefit for a customer',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerGain`),
      { uri: `${market}hasImportance`, localName: 'hasImportance', label: 'Importance', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:string' },
      { uri: `${market}gainType`, localName: 'gainType', label: 'Gain Type', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:string', description: 'Expected | Desired | Unexpected' },
      // Evidence strength tracking
      { uri: `${market}mentionCount`, localName: 'mentionCount', label: 'Mention Count', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:integer', description: 'How many times mentioned across interviews' },
      { uri: `${market}segmentCoverage`, localName: 'segmentCoverage', label: 'Segment Coverage', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:integer', description: 'How many segments report this' },
      { uri: `${market}evidenceStrength`, localName: 'evidenceStrength', label: 'Evidence Strength', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:string', description: 'None | Weak (1-2) | Moderate (3-5) | Strong (6+)' },
      { uri: `${market}firstMentionedDate`, localName: 'firstMentionedDate', label: 'First Mentioned Date', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:dateTime', description: 'When this gain was first mentioned' },
      { uri: `${market}lastValidatedDate`, localName: 'lastValidatedDate', label: 'Last Validated Date', type: 'data', domain: `${market}CustomerGain`, range: 'xsd:dateTime', description: 'When this gain was last validated' },
    ],
    educational: {
      whatIsThis: 'A customer gain is a positive outcome or benefit that customers desire. Gains can be expected, desired, or unexpected delights.',
      whenToUse: [
        'Identifying value proposition opportunities',
        'Differentiating from competitors',
        'Prioritizing feature development',
      ],
      goodExamples: [
        'Save 10+ hours per week on reporting (Expected)',
        'Look competent to leadership (Social gain)',
        'Peace of mind that data is secure (Emotional)',
      ],
      badExamples: [
        '"Good software" (not specific)',
        '"Happiness" (too abstract)',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['Persona', 'CustomerPain', 'ValueProposition'],
    },
  },
  {
    uri: `${market}CustomerJob`,
    localName: 'CustomerJob',
    label: 'Customer Job',
    domain: 'market',
    description: 'A task the customer needs to accomplish (Jobs-to-be-Done)',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerJob`),
      { uri: `${market}hasJobType`, localName: 'hasJobType', label: 'Job Type', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'Functional | Social | Emotional' },
      // JTBD (Jobs-to-be-Done) properties
      { uri: `${market}jobStatement`, localName: 'jobStatement', label: 'Job Statement', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'When [situation], I want to [motivation], so I can [outcome]' },
      { uri: `${market}functionalJob`, localName: 'functionalJob', label: 'Functional', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'What needs to happen — the practical, observable outcome' },
      { uri: `${market}emotionalJob`, localName: 'emotionalJob', label: 'Emotional', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'How they want to feel while doing the job' },
      { uri: `${market}socialJob`, localName: 'socialJob', label: 'Social', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'How they want to be perceived by others' },
      { uri: `${market}hasPain`, localName: 'hasPain', label: 'Has Pain', type: 'object', domain: `${market}CustomerJob`, range: `${market}CustomerPain`, description: 'Pains scoped to this job' },
      { uri: `${market}hasGain`, localName: 'hasGain', label: 'Has Gain', type: 'object', domain: `${market}CustomerJob`, range: `${market}CustomerGain`, description: 'Gains scoped to this job' },
      { uri: `${market}jobContext`, localName: 'jobContext', label: 'Job Context', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'The situation or context when this job arises' },
      { uri: `${market}desiredOutcome`, localName: 'desiredOutcome', label: 'Desired Outcome', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'What the customer hopes to achieve' },
      { uri: `${market}frequency`, localName: 'frequency', label: 'Frequency', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'Daily | Weekly | Monthly | Occasional' },
      { uri: `${market}importance`, localName: 'importance', label: 'Importance', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'Critical | Important | NiceToHave' },
      { uri: `${market}currentSolution`, localName: 'currentSolution', label: 'Current Solution', type: 'object', domain: `${market}CustomerJob`, range: `${market}Alternative`, description: 'How the customer currently accomplishes this job' },
      { uri: `${market}satisfactionLevel`, localName: 'satisfactionLevel', label: 'Satisfaction Level', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:integer', description: 'Customer satisfaction with current solution (1-10)' },
      { uri: `${market}opportunityScore`, localName: 'opportunityScore', label: 'Opportunity Score', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:decimal', description: 'ODI: importance + (importance - satisfaction)' },
      // Evidence strength tracking
      { uri: `${market}mentionCount`, localName: 'mentionCount', label: 'Mention Count', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:integer', description: 'How many times mentioned across interviews' },
      { uri: `${market}segmentCoverage`, localName: 'segmentCoverage', label: 'Segment Coverage', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:integer', description: 'How many segments report this' },
      { uri: `${market}evidenceStrength`, localName: 'evidenceStrength', label: 'Evidence Strength', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:string', description: 'None | Weak (1-2) | Moderate (3-5) | Strong (6+)' },
      { uri: `${market}firstMentionedDate`, localName: 'firstMentionedDate', label: 'First Mentioned Date', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:dateTime', description: 'When this job was first mentioned' },
      { uri: `${market}lastValidatedDate`, localName: 'lastValidatedDate', label: 'Last Validated Date', type: 'data', domain: `${market}CustomerJob`, range: 'xsd:dateTime', description: 'When this job was last validated' },
    ],
    educational: {
      whatIsThis: 'A customer job is what customers are trying to accomplish. Jobs-to-be-Done (JTBD) framework focuses on the progress customers seek, not your solution.',
      whenToUse: [
        'Understanding customer motivations',
        'Finding innovation opportunities',
        'Creating user stories',
      ],
      goodExamples: [
        'Functional: Close the books by end of month',
        'Social: Appear data-driven to the board',
        'Emotional: Feel confident about financial decisions',
      ],
      badExamples: [
        '"Use accounting software" (solution, not job)',
        '"Save money" (outcome, not job)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Persona', 'CustomerPain', 'CustomerGain'],
    },
  },
  // ----------------------------------------------------------------------------
  // ALTERNATIVES (for Problem Statement Canvas)
  // ----------------------------------------------------------------------------
  {
    uri: `${market}Alternative`,
    localName: 'Alternative',
    label: 'Alternative',
    domain: 'market',
    description: 'Any solution customers use to address a problem, including competitors, workarounds, manual processes, or doing nothing',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Alternative`),
      { uri: `${market}hasAlternativeType`, localName: 'hasAlternativeType', label: 'Alternative Type', type: 'data', domain: `${market}Alternative`, range: 'xsd:string', description: 'competitor | workaround | manual_process | diy | do_nothing' },
      { uri: `${market}addressesProblem`, localName: 'addressesProblem', label: 'Addresses Problem', type: 'object', domain: `${market}Alternative`, range: `${market}Problem` },
      { uri: `${market}hasAdvantage`, localName: 'hasAdvantage', label: 'Has Advantage', type: 'object', domain: `${market}Alternative`, range: `${market}AlternativeAdvantage` },
      { uri: `${market}hasDisadvantage`, localName: 'hasDisadvantage', label: 'Has Disadvantage', type: 'object', domain: `${market}Alternative`, range: `${market}AlternativeDisadvantage` },
      // Alternative analysis
      { uri: `${market}marketShare`, localName: 'marketShare', label: 'Market Share', type: 'data', domain: `${market}Alternative`, range: 'xsd:float', description: 'Estimated market share (0-1)' },
      { uri: `${market}priceRange`, localName: 'priceRange', label: 'Price Range', type: 'data', domain: `${market}Alternative`, range: 'xsd:string', description: 'Price range or pricing model' },
      { uri: `${market}switchingCost`, localName: 'switchingCost', label: 'Switching Cost', type: 'data', domain: `${market}Alternative`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${market}customerSatisfaction`, localName: 'customerSatisfaction', label: 'Customer Satisfaction', type: 'data', domain: `${market}Alternative`, range: 'xsd:integer', description: 'Satisfaction score (1-10)' },
      { uri: `${market}usedBySegment`, localName: 'usedBySegment', label: 'Used By Segment', type: 'object', domain: `${market}Alternative`, range: `${market}CustomerSegment`, description: 'Segment that uses this alternative' },
    ],
    educational: {
      whatIsThis: 'An alternative is any solution customers currently use to address a problem. This includes competing products, manual workarounds, DIY solutions, or even doing nothing.',
      whenToUse: [
        'Mapping the competitive landscape broadly',
        'Understanding what customers do today',
        'Identifying opportunities from alternative shortcomings',
      ],
      goodExamples: [
        'Competitor: "They use Salesforce for CRM"',
        'Workaround: "They built a complex spreadsheet system"',
        'Manual process: "They manually copy-paste data between systems"',
        'Do nothing: "They accept the inefficiency and work overtime"',
      ],
      badExamples: [
        '"They need our product" (solution-centric)',
        '"Competition" (too vague, name specific alternatives)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Problem', 'Competitor', 'AlternativeDisadvantage', 'CustomerPain'],
    },
  },
  {
    uri: `${market}AlternativeAdvantage`,
    localName: 'AlternativeAdvantage',
    label: 'Alternative Advantage',
    domain: 'market',
    description: 'A benefit or strength of an alternative solution that makes customers stick with it',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}AlternativeAdvantage`),
      { uri: `${market}ofAlternative`, localName: 'ofAlternative', label: 'Of Alternative', type: 'object', domain: `${market}AlternativeAdvantage`, range: `${market}Alternative` },
    ],
    educational: {
      whatIsThis: 'An advantage is why customers stick with their current alternative despite its problems. Understanding advantages helps you identify table-stakes features.',
      whenToUse: [
        'Understanding switching barriers',
        'Identifying must-have features',
        'Competitive positioning',
      ],
      goodExamples: [
        '"Salesforce integrates with everything they already use"',
        '"Their spreadsheet is free and they know how to use it"',
        '"Manual process gives them full control and flexibility"',
      ],
      badExamples: [
        '"It works" (too generic)',
        '"It\'s okay" (not specific enough)',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['Alternative', 'AlternativeDisadvantage', 'ValueProposition'],
    },
  },
  {
    uri: `${market}AlternativeDisadvantage`,
    localName: 'AlternativeDisadvantage',
    label: 'Alternative Shortcoming',
    domain: 'market',
    description: 'A limitation, drawback, or shortcoming of an alternative solution that creates opportunity',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}AlternativeDisadvantage`),
      { uri: `${market}ofAlternative`, localName: 'ofAlternative', label: 'Of Alternative', type: 'object', domain: `${market}AlternativeDisadvantage`, range: `${market}Alternative` },
      { uri: `${market}createsPain`, localName: 'createsPain', label: 'Creates Pain', type: 'object', domain: `${market}AlternativeDisadvantage`, range: `${market}CustomerPain`, description: 'The customer pain this shortcoming causes' },
    ],
    educational: {
      whatIsThis: 'A shortcoming is a limitation of the current alternative that creates pain for customers. Shortcomings represent your opportunity to differentiate.',
      whenToUse: [
        'Identifying differentiation opportunities',
        'Building value proposition',
        'Prioritizing features that address real gaps',
      ],
      goodExamples: [
        '"Salesforce is too complex for their small team" → creates frustration',
        '"Spreadsheet doesn\'t scale, takes 20+ hours/month to maintain" → creates time waste',
        '"Manual process is error-prone, causes costly mistakes" → creates financial risk',
      ],
      badExamples: [
        '"It\'s bad" (not specific)',
        '"They don\'t like it" (no clear pain)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Alternative', 'AlternativeAdvantage', 'CustomerPain', 'ValueProposition'],
    },
  },

  {
    uri: `${market}Workaround`,
    localName: 'Workaround',
    label: 'Workaround',
    domain: 'market',
    description: 'Temporary solution or hack users employ to solve a problem in absence of a proper solution',
    properties: [
      { uri: `${market}addressesProblem`, localName: 'addressesProblem', label: 'Addresses Problem', type: 'object', domain: `${market}Workaround`, range: `${market}Problem` },
      { uri: `${market}effectiveness`, localName: 'effectiveness', label: 'Effectiveness', type: 'data', domain: `${market}Workaround`, range: 'xsd:string', description: 'Low/Medium/High' },
      { uri: `${market}effort`, localName: 'effort', label: 'Effort Required', type: 'data', domain: `${market}Workaround`, range: 'xsd:string', description: 'How much effort needed' },
      ...DESCRIPTIVE_PROPERTIES(`${market}Workaround`),
      ...STATUS_PROPERTIES(`${market}Workaround`),
    ],
    educational: {
      whatIsThis: 'A workaround is a temporary or makeshift solution users employ when a proper tool doesn\'t exist. It reveals unmet needs and pain points.',
      whenToUse: [
        'Understanding current customer behavior',
        'Identifying pain from manual workarounds',
        'Finding opportunities for automation',
      ],
      goodExamples: [
        '"They manually copy-paste data between Excel and their CRM"',
        '"Engineers built a Python script to automate the process"',
        '"They use sticky notes to track status because the tool doesn\'t support it"',
      ],
      badExamples: [
        '"They use a competitor product" (that\'s an Alternative, not a workaround)',
        '"Manual process" (too vague - describe the specific workaround)',
      ],
      discoveryPhase: 'Problem Discovery',
      discoveryRequired: false,
      relatedConcepts: ['Problem', 'Alternative', 'CustomerPain'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW MARKET LAYER CLASSES - HIGH PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${market}Market`,
    localName: 'Market',
    label: 'Market',
    domain: 'market',
    description: 'An addressable market containing multiple industries and segments with quantified size',
    properties: [
      { uri: `${market}hasTAM`, localName: 'hasTAM', label: 'Total Addressable Market (TAM)', type: 'data', domain: `${market}Market`, range: 'xsd:decimal', description: 'Total addressable market size in currency' },
      { uri: `${market}hasSAM`, localName: 'hasSAM', label: 'Serviceable Addressable Market (SAM)', type: 'data', domain: `${market}Market`, range: 'xsd:decimal', description: 'Serviceable addressable market size' },
      { uri: `${market}hasSOM`, localName: 'hasSOM', label: 'Serviceable Obtainable Market (SOM)', type: 'data', domain: `${market}Market`, range: 'xsd:decimal', description: 'Realistically obtainable market size' },
      { uri: `${market}marketGrowthRate`, localName: 'marketGrowthRate', label: 'Market Growth Rate', type: 'data', domain: `${market}Market`, range: 'xsd:float', description: 'Annual market growth rate (%)' },
      { uri: `${market}containsIndustry`, localName: 'containsIndustry', label: 'Contains Industry', type: 'object', domain: `${market}Market`, range: `${market}Industry`, description: 'Industries contained in this market' },
      ...STATUS_PROPERTIES(`${market}Market`),
      ...DESCRIPTIVE_PROPERTIES(`${market}Market`),
      ...TEMPORAL_PROPERTIES(`${market}Market`),
    ],
    educational: {
      whatIsThis: 'A market represents the total addressable opportunity for products/services, quantified as TAM (total market), SAM (serviceable market), and SOM (obtainable market share).',
      whenToUse: [
        'Quantifying market opportunity for investors',
        'Strategic planning and market sizing',
        'Understanding market potential vs. realistic targets',
      ],
      goodExamples: [
        'Enterprise SaaS Market: TAM $50B, SAM $15B, SOM $500M',
        'SMB Accounting Software: TAM $10B, SAM $3B, SOM $150M',
        'Healthcare AI Market: TAM $100B, SAM $25B, SOM $1B',
      ],
      badExamples: [
        '"Technology market" (too broad, not quantified)',
        'No TAM/SAM/SOM breakdown',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Industry', 'CustomerSegment', 'MarketOpportunity'],
    },
  },
  {
    uri: `${market}MarketOpportunity`,
    localName: 'MarketOpportunity',
    label: 'Market Opportunity',
    domain: 'market',
    description: 'A quantified market opportunity arising from trends, unmet needs, or market gaps',
    properties: [
      { uri: `${market}opportunitySize`, localName: 'opportunitySize', label: 'Opportunity Size', type: 'data', domain: `${market}MarketOpportunity`, range: 'xsd:decimal', description: 'Quantified size of the opportunity in currency' },
      { uri: `${market}opportunityType`, localName: 'opportunityType', label: 'Opportunity Type', type: 'data', domain: `${market}MarketOpportunity`, range: 'xsd:string', description: 'NewMarket | MarketExpansion | Disruption | Niche' },
      { uri: `${market}timeWindow`, localName: 'timeWindow', label: 'Time Window', type: 'data', domain: `${market}MarketOpportunity`, range: 'xsd:string', description: 'Time window to capture opportunity' },
      { uri: `${market}confidenceLevel`, localName: 'confidenceLevel', label: 'Confidence Level', type: 'data', domain: `${market}MarketOpportunity`, range: 'xsd:decimal', description: 'Confidence level 0.0-1.0' },
      { uri: `${market}competitorCount`, localName: 'competitorCount', label: 'Competitor Count', type: 'data', domain: `${market}MarketOpportunity`, range: 'xsd:integer', description: 'Number of existing competitors' },
      { uri: `${market}inMarket`, localName: 'inMarket', label: 'In Market', type: 'object', domain: `${market}MarketOpportunity`, range: `${market}Market`, description: 'The market this opportunity exists in' },
      { uri: `${market}drivenByTrend`, localName: 'drivenByTrend', label: 'Driven By Trend', type: 'object', domain: `${market}MarketOpportunity`, range: `${market}Trend`, description: 'Trend driving this opportunity' },
      ...STATUS_PROPERTIES(`${market}MarketOpportunity`),
      ...DESCRIPTIVE_PROPERTIES(`${market}MarketOpportunity`),
      ...TEMPORAL_PROPERTIES(`${market}MarketOpportunity`),
    ],
    educational: {
      whatIsThis: 'A market opportunity is a specific, quantified opportunity to capture market share, often driven by trends, technological shifts, or unmet customer needs.',
      whenToUse: [
        'Identifying new business opportunities from market analysis',
        'Prioritizing which opportunities to pursue',
        'Communicating opportunity sizing to stakeholders',
      ],
      goodExamples: [
        'AI-powered finance automation: $2B opportunity, 6-12 month window, only 3 competitors',
        'Remote work collaboration tools: $5B opportunity, emerging trend, high confidence',
        'Privacy-compliant analytics: $800M niche, regulatory driver, 18-month window',
      ],
      badExamples: [
        '"Big market opportunity" (not quantified)',
        'No time window or competitive analysis',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Market', 'Trend', 'MarketSignal', 'Strategy'],
    },
  },
  {
    uri: `${market}CustomerJourney`,
    localName: 'CustomerJourney',
    label: 'Customer Journey',
    domain: 'market',
    description: 'The complete journey a customer takes from awareness through purchase to advocacy',
    properties: [
      { uri: `${market}journeyType`, localName: 'journeyType', label: 'Journey Type', type: 'data', domain: `${market}CustomerJourney`, range: 'xsd:string', description: 'B2B | B2C | SaaS | Ecommerce' },
      { uri: `${market}forSegment`, localName: 'forSegment', label: 'For Segment', type: 'object', domain: `${market}CustomerJourney`, range: `${market}CustomerSegment`, description: 'Customer segment this journey applies to' },
      { uri: `${market}hasStage`, localName: 'hasStage', label: 'Has Stage', type: 'object', domain: `${market}CustomerJourney`, range: `${market}JourneyStage`, description: 'Stages in this journey' },
      ...STATUS_PROPERTIES(`${market}CustomerJourney`),
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerJourney`),
      ...TEMPORAL_PROPERTIES(`${market}CustomerJourney`),
    ],
    educational: {
      whatIsThis: 'A customer journey maps the complete path customers take, from becoming aware of their problem through becoming advocates of your solution. It includes all stages and touchpoints.',
      whenToUse: [
        'Understanding customer behavior and decision-making',
        'Identifying friction points in the buying process',
        'Designing better customer experiences across touchpoints',
      ],
      goodExamples: [
        'Enterprise SaaS journey: Awareness → Consideration → Trial → Purchase → Onboarding → Adoption → Expansion → Advocacy',
        'B2C journey: Problem Recognition → Research → Comparison → Purchase → Use → Loyalty',
      ],
      badExamples: [
        'Only mapping post-purchase stages',
        'Generic journey not specific to your segments',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['CustomerSegment', 'JourneyStage', 'Touchpoint', 'Channel'],
    },
  },
  {
    uri: `${market}JourneyStage`,
    localName: 'JourneyStage',
    label: 'Journey Stage',
    domain: 'market',
    description: 'A specific phase in the customer journey with defined goals and touchpoints',
    properties: [
      { uri: `${market}stageName`, localName: 'stageName', label: 'Stage Name', type: 'data', domain: `${market}JourneyStage`, range: 'xsd:string', description: 'Awareness | Consideration | Decision | Purchase | Retention | Advocacy' },
      { uri: `${market}stageGoal`, localName: 'stageGoal', label: 'Stage Goal', type: 'data', domain: `${market}JourneyStage`, range: 'xsd:string', description: 'What customers are trying to achieve in this stage' },
      { uri: `${market}averageDuration`, localName: 'averageDuration', label: 'Average Duration', type: 'data', domain: `${market}JourneyStage`, range: 'xsd:integer', description: 'Average duration in days' },
      { uri: `${market}conversionRate`, localName: 'conversionRate', label: 'Conversion Rate', type: 'data', domain: `${market}JourneyStage`, range: 'xsd:float', description: 'Conversion rate to next stage (%)' },
      { uri: `${market}partOfJourney`, localName: 'partOfJourney', label: 'Part Of Journey', type: 'object', domain: `${market}JourneyStage`, range: `${market}CustomerJourney`, description: 'The journey this stage belongs to' },
      { uri: `${market}hasTouchpoint`, localName: 'hasTouchpoint', label: 'Has Touchpoint', type: 'object', domain: `${market}JourneyStage`, range: `${market}Touchpoint`, description: 'Touchpoints in this stage' },
      ...STATUS_PROPERTIES(`${market}JourneyStage`),
      ...DESCRIPTIVE_PROPERTIES(`${market}JourneyStage`),
    ],
    educational: {
      whatIsThis: 'A journey stage represents a distinct phase in the customer journey where customers have specific goals, behaviors, and needs. Each stage typically has multiple touchpoints.',
      whenToUse: [
        'Breaking down customer journey into actionable phases',
        'Identifying stage-specific challenges and opportunities',
        'Optimizing conversion between stages',
      ],
      goodExamples: [
        'Awareness Stage: Goal = Learn about solutions, Duration = 7 days, Touchpoints = Blog, Search, Social',
        'Consideration Stage: Goal = Evaluate options, Duration = 14 days, Conversion = 30%',
        'Decision Stage: Goal = Choose vendor, Duration = 21 days, Touchpoints = Demo, Pricing, Security review',
      ],
      badExamples: [
        'Combining multiple distinct stages into one',
        'No clear goals or success metrics per stage',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['CustomerJourney', 'Touchpoint', 'CustomerPain', 'Channel'],
    },
  },
  {
    uri: `${market}Touchpoint`,
    localName: 'Touchpoint',
    label: 'Touchpoint',
    domain: 'market',
    description: 'A specific point of interaction between customer and company in the journey',
    properties: [
      { uri: `${market}touchpointType`, localName: 'touchpointType', label: 'Touchpoint Type', type: 'data', domain: `${market}Touchpoint`, range: 'xsd:string', description: 'Digital | Physical | Human | Automated' },
      { uri: `${market}channelUsed`, localName: 'channelUsed', label: 'Channel Used', type: 'data', domain: `${market}Touchpoint`, range: 'xsd:string', description: 'Specific channel (Website, Email, Phone, etc.)' },
      { uri: `${market}customerSentiment`, localName: 'customerSentiment', label: 'Customer Sentiment', type: 'data', domain: `${market}Touchpoint`, range: 'xsd:string', description: 'Positive | Neutral | Negative | Frustration' },
      { uri: `${market}frictionLevel`, localName: 'frictionLevel', label: 'Friction Level', type: 'data', domain: `${market}Touchpoint`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${market}inStage`, localName: 'inStage', label: 'In Stage', type: 'object', domain: `${market}Touchpoint`, range: `${market}JourneyStage`, description: 'Journey stage this touchpoint occurs in' },
      ...STATUS_PROPERTIES(`${market}Touchpoint`),
      ...DESCRIPTIVE_PROPERTIES(`${market}Touchpoint`),
    ],
    educational: {
      whatIsThis: 'A touchpoint is any interaction point between a customer and your company across the journey. Understanding touchpoints helps identify where to improve customer experience.',
      whenToUse: [
        'Mapping all customer interaction points',
        'Identifying high-friction or negative touchpoints',
        'Designing better omnichannel experiences',
      ],
      goodExamples: [
        'Website pricing page: Digital, High traffic, High friction (confusion about tiers)',
        'Sales demo call: Human, Positive sentiment, Low friction',
        'Onboarding email sequence: Automated, Neutral sentiment, Medium friction',
      ],
      badExamples: [
        'Generic "website" (not specific enough)',
        'No sentiment or friction data',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['JourneyStage', 'Channel', 'CustomerPain', 'CustomerJourney'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW MARKET LAYER CLASSES - MEDIUM PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${market}CustomerNeed`,
    localName: 'CustomerNeed',
    label: 'Customer Need',
    domain: 'market',
    description: 'A fundamental customer need, broader than specific pains or gains',
    properties: [
      { uri: `${market}needCategory`, localName: 'needCategory', label: 'Need Category', type: 'data', domain: `${market}CustomerNeed`, range: 'xsd:string', description: 'Functional | Social | Emotional | Aspirational' },
      { uri: `${market}needPriority`, localName: 'needPriority', label: 'Need Priority', type: 'data', domain: `${market}CustomerNeed`, range: 'xsd:string', description: 'MustHave | ShouldHave | NiceToHave' },
      { uri: `${market}needIntensity`, localName: 'needIntensity', label: 'Need Intensity', type: 'data', domain: `${market}CustomerNeed`, range: 'xsd:string', description: 'Low | Medium | High | Critical' },
      { uri: `${market}manifestsAsPain`, localName: 'manifestsAsPain', label: 'Manifests As Pain', type: 'object', domain: `${market}CustomerNeed`, range: `${market}CustomerPain`, description: 'Specific pains arising from unmet need' },
      ...STATUS_PROPERTIES(`${market}CustomerNeed`),
      ...DESCRIPTIVE_PROPERTIES(`${market}CustomerNeed`),
    ],
    educational: {
      whatIsThis: 'A customer need is a fundamental requirement or desire that drives behavior. Needs are broader than pains - unmet needs manifest as pains. Understanding needs helps find root causes.',
      whenToUse: [
        'Identifying deeper motivations beyond surface pains',
        'Understanding "jobs to be done" framework',
        'Finding adjacent opportunities in unmet needs',
      ],
      goodExamples: [
        'Functional need: "Need to close books accurately and on time"',
        'Social need: "Need to be seen as competent by peers"',
        'Emotional need: "Need to feel confident in financial decisions"',
      ],
      badExamples: [
        'Confusing needs with solutions ("need better software")',
        'Too specific (that\'s a pain, not a fundamental need)',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['CustomerPain', 'CustomerJob', 'Persona'],
    },
  },
  {
    uri: `${market}Regulation`,
    localName: 'Regulation',
    label: 'Regulation',
    domain: 'market',
    description: 'A legal or regulatory requirement affecting the market or business',
    properties: [
      { uri: `${market}regulationType`, localName: 'regulationType', label: 'Regulation Type', type: 'data', domain: `${market}Regulation`, range: 'xsd:string', description: 'DataPrivacy | Financial | Healthcare | Environmental | Industry' },
      { uri: `${market}jurisdiction`, localName: 'jurisdiction', label: 'Jurisdiction', type: 'data', domain: `${market}Regulation`, range: 'xsd:string', description: 'Geographic or regulatory jurisdiction' },
      { uri: `${market}complianceDeadline`, localName: 'complianceDeadline', label: 'Compliance Deadline', type: 'data', domain: `${market}Regulation`, range: 'xsd:dateTime', description: 'When compliance is required' },
      { uri: `${market}penaltySeverity`, localName: 'penaltySeverity', label: 'Penalty Severity', type: 'data', domain: `${market}Regulation`, range: 'xsd:string', description: 'Low | Medium | High | Severe' },
      { uri: `${market}affectsIndustry`, localName: 'affectsIndustry', label: 'Affects Industry', type: 'object', domain: `${market}Regulation`, range: `${market}Industry`, description: 'Industries affected by this regulation' },
      ...STATUS_PROPERTIES(`${market}Regulation`),
      ...DESCRIPTIVE_PROPERTIES(`${market}Regulation`),
      ...TEMPORAL_PROPERTIES(`${market}Regulation`),
    ],
    educational: {
      whatIsThis: 'Regulations are legal requirements that create constraints and opportunities. Compliance requirements can drive product features and market opportunities.',
      whenToUse: [
        'Understanding regulatory drivers for product features',
        'Identifying compliance-driven market opportunities',
        'Planning features required for regulated industries',
      ],
      goodExamples: [
        'GDPR: Data privacy, EU jurisdiction, May 2018 deadline, severe penalties',
        'SOX: Financial reporting, US public companies, ongoing compliance',
        'HIPAA: Healthcare privacy, US healthcare industry, patient data protection',
      ],
      badExamples: [
        'Internal company policies (not external regulations)',
        'Best practices or standards (not legal requirements)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Industry', 'Constraint', 'Requirement'],
    },
  },
  {
    uri: `${market}Demographic`,
    localName: 'Demographic',
    label: 'Demographic',
    domain: 'market',
    description: 'Demographic characteristics used for customer segmentation',
    properties: [
      { uri: `${market}ageRange`, localName: 'ageRange', label: 'Age Range', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'Age range (B2C) or company age (B2B)' },
      { uri: `${market}incomeLevel`, localName: 'incomeLevel', label: 'Income Level', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'Income bracket or company revenue' },
      { uri: `${market}education`, localName: 'education', label: 'Education', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'Education level of target customers' },
      { uri: `${market}geography`, localName: 'geography', label: 'Geography', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'Geographic location or market' },
      { uri: `${market}companySize`, localName: 'companySize', label: 'Company Size', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'For B2B: SMB | MidMarket | Enterprise' },
      { uri: `${market}industry`, localName: 'industry', label: 'Industry', type: 'data', domain: `${market}Demographic`, range: 'xsd:string', description: 'Industry vertical' },
      { uri: `${market}describesSegment`, localName: 'describesSegment', label: 'Describes Segment', type: 'object', domain: `${market}Demographic`, range: `${market}CustomerSegment`, description: 'Segment this demographic profile describes' },
      ...DESCRIPTIVE_PROPERTIES(`${market}Demographic`),
    ],
    educational: {
      whatIsThis: 'Demographics are quantifiable population characteristics used to segment markets. For B2B, demographics include company size, industry, revenue. For B2C, age, income, education, location.',
      whenToUse: [
        'Defining customer segment characteristics',
        'Targeting marketing and sales efforts',
        'Understanding who your customers are',
      ],
      goodExamples: [
        'B2B: Mid-market (100-1000 employees), $10M-$100M revenue, Financial Services, North America',
        'B2C: Age 25-40, $75K-$150K income, College educated, Urban areas',
      ],
      badExamples: [
        'Behavioral traits (those are psychographics)',
        'Too broad ("everyone" is not a demographic)',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['CustomerSegment', 'Psychographic', 'Persona'],
    },
  },
  {
    uri: `${market}Psychographic`,
    localName: 'Psychographic',
    label: 'Psychographic',
    domain: 'market',
    description: 'Behavioral and psychological attributes for deeper customer understanding',
    properties: [
      { uri: `${market}values`, localName: 'values', label: 'Values', type: 'data', domain: `${market}Psychographic`, range: 'xsd:string', description: 'Core values that drive decisions' },
      { uri: `${market}attitudes`, localName: 'attitudes', label: 'Attitudes', type: 'data', domain: `${market}Psychographic`, range: 'xsd:string', description: 'Attitudes toward technology, change, risk' },
      { uri: `${market}lifestyle`, localName: 'lifestyle', label: 'Lifestyle', type: 'data', domain: `${market}Psychographic`, range: 'xsd:string', description: 'Lifestyle characteristics' },
      { uri: `${market}buyingBehavior`, localName: 'buyingBehavior', label: 'Buying Behavior', type: 'data', domain: `${market}Psychographic`, range: 'xsd:string', description: 'EarlyAdopter | Pragmatist | Conservative | PriceConscious' },
      { uri: `${market}decisionDrivers`, localName: 'decisionDrivers', label: 'Decision Drivers', type: 'data', domain: `${market}Psychographic`, range: 'xsd:string', description: 'What primarily drives their decisions' },
      { uri: `${market}describesPersona`, localName: 'describesPersona', label: 'Describes Persona', type: 'object', domain: `${market}Psychographic`, range: `${market}Persona`, description: 'Persona this psychographic profile describes' },
      ...DESCRIPTIVE_PROPERTIES(`${market}Psychographic`),
    ],
    educational: {
      whatIsThis: 'Psychographics describe psychological and behavioral characteristics: values, attitudes, lifestyle, buying behavior. Goes deeper than demographics to understand motivations.',
      whenToUse: [
        'Creating detailed persona profiles',
        'Understanding why customers make decisions',
        'Tailoring messaging to resonate with mindsets',
      ],
      goodExamples: [
        'Early adopter: Values innovation, risk-tolerant, FOMO-driven, willing to pay premium',
        'Pragmatist: Values proven ROI, risk-averse, consensus-driven, price-conscious',
        'Visionary: Values transformation, ambitious, status-driven, willing to pioneer',
      ],
      badExamples: [
        'Demographics like age or income (those aren\'t psychographics)',
        'Generic traits that apply to everyone',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['Persona', 'Demographic', 'CustomerSegment'],
    },
  },
  // Interview - atomic unit of customer discovery
  {
    uri: `${market}Interview`,
    localName: 'Interview',
    label: 'Interview',
    domain: 'market',
    description: 'A customer interview - the atomic unit of discovery evidence',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Interview`),
      // Interview classification
      { uri: `${market}interviewType`, localName: 'interviewType', label: 'Interview Type', type: 'data', domain: `${market}Interview`, range: 'xsd:string', description: 'Discovery | Problem | Solution | Usability | Sales | Support' },
      { uri: `${market}interviewDate`, localName: 'interviewDate', label: 'Interview Date', type: 'data', domain: `${market}Interview`, range: 'xsd:dateTime', description: 'When the interview was conducted' },
      { uri: `${market}duration`, localName: 'duration', label: 'Duration', type: 'data', domain: `${market}Interview`, range: 'xsd:integer', description: 'Interview duration in minutes' },
      { uri: `${market}interviewFormat`, localName: 'interviewFormat', label: 'Interview Format', type: 'data', domain: `${market}Interview`, range: 'xsd:string', description: 'InPerson | Video | Phone | Written' },
      // Participant linkage
      { uri: `${market}withPerson`, localName: 'withPerson', label: 'With Person', type: 'object', domain: `${market}Interview`, range: `${market}Person`, description: 'The person interviewed' },
      { uri: `${market}representsSegment`, localName: 'representsSegment', label: 'Represents Segment', type: 'object', domain: `${market}Interview`, range: `${market}CustomerSegment`, description: 'The segment this interviewee represents' },
      { uri: `${market}hasPersona`, localName: 'hasPersona', label: 'Has Persona', type: 'object', domain: `${market}Interview`, range: `${market}Persona`, description: 'The persona this interviewee matches' },
      // Content and output
      { uri: `${market}summary`, localName: 'summary', label: 'Summary', type: 'data', domain: `${market}Interview`, range: 'xsd:string', description: 'Brief summary of the interview' },
      { uri: `${market}keyQuotes`, localName: 'keyQuotes', label: 'Key Quotes', type: 'data', domain: `${market}Interview`, range: 'xsd:string', description: 'Notable quotes from the interview' },
      { uri: `${market}sourceArtifact`, localName: 'sourceArtifact', label: 'Source Artifact', type: 'object', domain: `${market}Interview`, range: `${io}Artifact`, description: 'The artifact (transcript, recording) containing this interview' },
      { uri: `${market}extractedFacets`, localName: 'extractedFacets', label: 'Extracted Facets', type: 'object', domain: `${market}Interview`, range: `${io}Facet`, description: 'Facets extracted from this interview' },
      // Discovered entities
      { uri: `${market}discoveredPains`, localName: 'discoveredPains', label: 'Discovered Pains', type: 'object', domain: `${market}Interview`, range: `${market}CustomerPain`, description: 'Pains discovered in this interview' },
      { uri: `${market}discoveredGains`, localName: 'discoveredGains', label: 'Discovered Gains', type: 'object', domain: `${market}Interview`, range: `${market}CustomerGain`, description: 'Gains discovered in this interview' },
      { uri: `${market}discoveredJobs`, localName: 'discoveredJobs', label: 'Discovered Jobs', type: 'object', domain: `${market}Interview`, range: `${market}CustomerJob`, description: 'Jobs discovered in this interview' },
      // Quality metrics
      { uri: `${market}interviewQuality`, localName: 'interviewQuality', label: 'Interview Quality', type: 'data', domain: `${market}Interview`, range: 'xsd:string', description: 'Low | Medium | High - quality of insights obtained' },
      { uri: `${market}facetCount`, localName: 'facetCount', label: 'Facet Count', type: 'data', domain: `${market}Interview`, range: 'xsd:integer', description: 'Number of facets extracted' },
    ],
    educational: {
      whatIsThis: 'A customer interview is the atomic unit of discovery. Each interview captures direct customer evidence that can be traced through facets to entities and insights.',
      whenToUse: [
        'Recording customer discovery sessions',
        'Tracking evidence provenance',
        'Building validated customer understanding',
      ],
      goodExamples: [
        'Discovery Interview with CFO Clara - 45min video call exploring reconciliation pain points',
        'Usability Test with SMB Owner - 30min in-person session testing invoice upload',
        'Sales Call with Enterprise Prospect - captured objections and feature requests',
      ],
      badExamples: [
        'Internal meeting notes (not customer evidence)',
        'Assumptions without customer validation',
      ],
      discoveryPhase: 'Customer Discovery',
      relatedConcepts: ['Person', 'Persona', 'CustomerPain', 'Facet', 'Artifact'],
    },
  },
  // Insight - synthesized learning from multiple evidence points
  {
    uri: `${market}Insight`,
    localName: 'Insight',
    label: 'Insight',
    domain: 'market',
    description: 'A synthesized learning connecting multiple evidence points into actionable knowledge',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${market}Insight`),
      // Insight classification
      { uri: `${market}insightType`, localName: 'insightType', label: 'Insight Type', type: 'data', domain: `${market}Insight`, range: 'xsd:string', description: 'PainInsight | BehaviorInsight | MotivationInsight | WorkaroundInsight | OpportunityInsight' },
      { uri: `${market}insightStatement`, localName: 'insightStatement', label: 'Insight Statement', type: 'data', domain: `${market}Insight`, range: 'xsd:string', description: 'Clear statement of what was learned' },
      // Evidence linkage
      { uri: `${market}supportingFacets`, localName: 'supportingFacets', label: 'Supporting Facets', type: 'object', domain: `${market}Insight`, range: `${io}Facet`, description: 'Facets that support this insight' },
      { uri: `${market}supportingInterviews`, localName: 'supportingInterviews', label: 'Supporting Interviews', type: 'object', domain: `${market}Insight`, range: `${market}Interview`, description: 'Interviews that contributed to this insight' },
      // Validation
      { uri: `${market}insightEvidenceStrength`, localName: 'insightEvidenceStrength', label: 'Evidence Strength', type: 'data', domain: `${market}Insight`, range: 'xsd:string', description: 'None | Weak | Moderate | Strong' },
      { uri: `${market}crossSegmentValidation`, localName: 'crossSegmentValidation', label: 'Cross-Segment Validation', type: 'data', domain: `${market}Insight`, range: 'xsd:boolean', description: 'Whether this insight holds across multiple segments' },
      { uri: `${market}confidenceLevel`, localName: 'confidenceLevel', label: 'Confidence Level', type: 'data', domain: `${market}Insight`, range: 'xsd:decimal', description: 'Confidence in this insight (0-1)' },
      // Strategic linkage
      { uri: `${market}leadsToOpportunity`, localName: 'leadsToOpportunity', label: 'Leads To Opportunity', type: 'object', domain: `${market}Insight`, range: `${business}Opportunity`, description: 'Business opportunity this insight reveals' },
    ],
    educational: {
      whatIsThis: 'An insight synthesizes multiple facets and interviews into actionable knowledge. Insights connect raw evidence to business opportunities and strategic decisions.',
      whenToUse: [
        'Synthesizing customer research findings',
        'Building evidence-based strategy',
        'Identifying high-confidence opportunities',
      ],
      goodExamples: [
        'PainInsight: CFOs spend 40% of month-end on manual reconciliation (supported by 8 interviews across 3 segments)',
        'BehaviorInsight: Users prefer uploading PDFs over API integration (5/6 usability tests)',
        'OpportunityInsight: Mid-market has unmet need for compliance automation (cross-segment validated)',
      ],
      badExamples: [
        'Single data point presented as insight',
        'Assumption without supporting evidence',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['Facet', 'Interview', 'Opportunity', 'CustomerPain'],
    },
  },
]

// ----------------------------------------------------------------------------
// BUSINESS DOMAIN CLASSES (BMC + Strategy)
// ----------------------------------------------------------------------------

export const BUSINESS_CLASSES: OntologyClass[] = [
  // Company - the central entity that owns everything else
  {
    uri: `${business}Company`,
    localName: 'Company',
    label: 'Company',
    domain: 'business',
    description: 'An organization or business entity that operates in markets, has products, and serves customers',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Company`),
      { uri: `${business}hasBusinessModel`, localName: 'hasBusinessModel', label: 'Has Business Model', type: 'object', domain: `${business}Company`, range: `${business}BusinessModel`, description: 'The business model this company operates' },
      { uri: `${business}operatesInIndustry`, localName: 'operatesInIndustry', label: 'Operates In Industry', type: 'object', domain: `${business}Company`, range: `${market}Industry`, description: 'The industry/market this company operates in' },
      { uri: `${business}offersProduct`, localName: 'offersProduct', label: 'Offers Product', type: 'object', domain: `${business}Company`, range: `${solution}Product`, description: 'Products or services this company offers' },
      { uri: `${business}targetsSegment`, localName: 'targetsSegment', label: 'Targets Segment', type: 'object', domain: `${business}Company`, range: `${market}CustomerSegment`, description: 'Customer segments this company targets' },
      { uri: `${business}usesChannel`, localName: 'usesChannel', label: 'Uses Channel', type: 'object', domain: `${business}Company`, range: `${business}Channel`, description: 'Channels used to reach customers' },
      { uri: `${business}competesAgainst`, localName: 'competesAgainst', label: 'Competes Against', type: 'object', domain: `${business}Company`, range: `${market}Competitor`, description: 'Competitors this company faces' },
      { uri: `${business}hasStrategy`, localName: 'hasStrategy', label: 'Has Strategy', type: 'object', domain: `${business}Company`, range: `${business}Strategy`, description: 'Strategic direction of the company' },
      { uri: `${business}hasFoundedYear`, localName: 'hasFoundedYear', label: 'Founded Year', type: 'data', domain: `${business}Company`, range: 'xsd:integer', description: 'Year the company was founded' },
      { uri: `${business}hasEmployeeCount`, localName: 'hasEmployeeCount', label: 'Employee Count', type: 'data', domain: `${business}Company`, range: 'xsd:integer', description: 'Number of employees' },
      { uri: `${business}hasWebsite`, localName: 'hasWebsite', label: 'Website', type: 'data', domain: `${business}Company`, range: 'xsd:anyURI', description: 'Company website URL' },
    ],
    educational: {
      whatIsThis: 'A Company is an organization that operates in a market, offers products/services, and has a business model. It is the central entity that ties together all business elements.',
      whenToUse: [
        'When analyzing your own company (the subject of analysis)',
        'When mentioning competitor companies',
        'When discussing partnership or customer companies',
      ],
      goodExamples: [
        'Sferio - the company being analyzed',
        'Salesforce - a competitor in the CRM space',
        'Stripe - a payment processing partner',
      ],
      badExamples: [
        '"The competition" (too vague, name specific companies)',
        '"A software company" (not a specific company)',
      ],
      discoveryPhase: 'Business Discovery',
      relatedConcepts: ['BusinessModel', 'Industry', 'Product', 'Competitor'],
    },
  },
  {
    uri: `${business}BusinessModel`,
    localName: 'BusinessModel',
    label: 'Business Model',
    domain: 'business',
    description: 'A representation of how the organization creates, delivers, and captures value',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}BusinessModel`),
      { uri: `${business}hasValueProposition`, localName: 'hasValueProposition', label: 'Has Value Proposition', type: 'object', domain: `${business}BusinessModel`, range: `${business}ValueProposition` },
      { uri: `${business}hasCustomerSegment`, localName: 'hasCustomerSegment', label: 'Has Customer Segment', type: 'object', domain: `${business}BusinessModel`, range: `${market}CustomerSegment` },
      { uri: `${business}hasChannel`, localName: 'hasChannel', label: 'Has Channel', type: 'object', domain: `${business}BusinessModel`, range: `${business}Channel` },
      { uri: `${business}hasRevenueStream`, localName: 'hasRevenueStream', label: 'Has Revenue Stream', type: 'object', domain: `${business}BusinessModel`, range: `${business}RevenueStream` },
      { uri: `${business}hasCostStructure`, localName: 'hasCostStructure', label: 'Has Cost Structure', type: 'object', domain: `${business}BusinessModel`, range: `${business}CostStructure` },
    ],
    educational: {
      whatIsThis: 'The Business Model Canvas (BMC) represents how your organization creates, delivers, and captures value. It connects all 9 building blocks of your business.',
      whenToUse: [
        'Defining your overall business strategy',
        'Communicating business model to stakeholders',
        'Identifying gaps in your business design',
      ],
      goodExamples: [
        'SaaS subscription model for mid-market finance teams',
        'Marketplace connecting suppliers and retailers',
        'Freemium model with premium enterprise features',
      ],
      badExamples: [
        '"We sell software" (not a complete model)',
        'Just describing the product (missing revenue, channels)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['ValueProposition', 'Channel', 'RevenueStream', 'CustomerSegment'],
    },
  },
  {
    uri: `${business}ValueProposition`,
    localName: 'ValueProposition',
    label: 'Value Proposition',
    domain: 'business',
    description: 'The bundle of value offered to a customer segment',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}ValueProposition`),
      { uri: `${business}offers`, localName: 'offers', label: 'Offers', type: 'object', domain: `${business}ValueProposition`, range: `${solution}Product` },
      { uri: `${business}targets`, localName: 'targets', label: 'Targets', type: 'object', domain: `${business}ValueProposition`, range: `${market}CustomerSegment` },
      { uri: `${business}solves`, localName: 'solves', label: 'Solves', type: 'object', domain: `${business}ValueProposition`, range: `${market}CustomerPain` },
      // VPC (Value Proposition Canvas) properties
      { uri: `${business}relievesPain`, localName: 'relievesPain', label: 'Relieves Pain', type: 'object', domain: `${business}ValueProposition`, range: `${market}CustomerPain`, description: 'VPC: Pain Relievers - which pains does this VP address?' },
      { uri: `${business}createsGain`, localName: 'createsGain', label: 'Creates Gain', type: 'object', domain: `${business}ValueProposition`, range: `${market}CustomerGain`, description: 'VPC: Gain Creators - which gains does this VP create?' },
      { uri: `${business}enablesJob`, localName: 'enablesJob', label: 'Enables Job', type: 'object', domain: `${business}ValueProposition`, range: `${market}CustomerJob`, description: 'VPC: Job enablement - which jobs does this VP help accomplish?' },
      { uri: `${business}vpStatement`, localName: 'vpStatement', label: 'VP Statement', type: 'data', domain: `${business}ValueProposition`, range: 'xsd:string', description: 'Concise value proposition statement' },
      { uri: `${business}validationStatus`, localName: 'validationStatus', label: 'Validation Status', type: 'data', domain: `${business}ValueProposition`, range: 'xsd:string', description: 'Assumed | Testing | Validated' },
      { uri: `${business}painCoverage`, localName: 'painCoverage', label: 'Pain Coverage', type: 'data', domain: `${business}ValueProposition`, range: 'xsd:decimal', description: 'Percentage of target pains addressed (0-100)' },
    ],
    educational: {
      whatIsThis: 'A value proposition describes the unique value you offer to a specific customer segment. It explains why customers should buy from you instead of competitors.',
      whenToUse: [
        'After identifying customer pains and gains',
        'Before building product features',
        'Creating marketing and sales messaging',
      ],
      goodExamples: [
        'Reduce monthly close time by 60% through automated reconciliation',
        'Help CFOs make confident decisions with real-time financial insights',
        'Eliminate manual data entry with AI-powered document processing',
      ],
      badExamples: [
        '"Best software in the market" (no specific value)',
        '"AI-powered solution" (feature, not value)',
      ],
      discoveryPhase: 'Solution Discovery',
      discoveryRequired: true,
      relatedConcepts: ['CustomerSegment', 'CustomerPain', 'Product', 'Feature'],
    },
  },
  {
    uri: `${business}Channel`,
    localName: 'Channel',
    label: 'Channel',
    domain: 'business',
    description: 'Touchpoints for delivering value to customers',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Channel`),
      { uri: `${business}delivers`, localName: 'delivers', label: 'Delivers', type: 'object', domain: `${business}Channel`, range: `${business}ValueProposition` },
      { uri: `${business}hasPhase`, localName: 'hasPhase', label: 'Phase', type: 'data', domain: `${business}Channel`, range: 'xsd:string', description: 'Awareness | Evaluation | Purchase | Delivery | AfterSales' },
      // Channel performance metrics
      { uri: `${business}channelEffectiveness`, localName: 'channelEffectiveness', label: 'Channel Effectiveness', type: 'data', domain: `${business}Channel`, range: 'xsd:float', description: 'Effectiveness score (0-1)' },
      { uri: `${business}costPerAcquisition`, localName: 'costPerAcquisition', label: 'CAC', type: 'data', domain: `${business}Channel`, range: 'xsd:decimal', description: 'Cost Per Acquisition via this channel' },
      { uri: `${business}conversionRate`, localName: 'conversionRate', label: 'Conversion Rate', type: 'data', domain: `${business}Channel`, range: 'xsd:float', description: 'Conversion rate (0-1)' },
      { uri: `${business}preferredBySegment`, localName: 'preferredBySegment', label: 'Preferred By Segment', type: 'object', domain: `${business}Channel`, range: `${market}CustomerSegment`, description: 'Segment that prefers this channel' },
    ],
    educational: {
      whatIsThis: 'Channels are the touchpoints through which you reach customers and deliver your value proposition. They cover the entire customer journey.',
      whenToUse: [
        'Planning go-to-market strategy',
        'Mapping customer journey',
        'Optimizing customer acquisition',
      ],
      goodExamples: [
        'Awareness: LinkedIn ads, content marketing',
        'Purchase: Self-service website, sales team',
        'Delivery: Web app, mobile app, API',
      ],
      badExamples: [
        '"Internet" (too vague)',
        'Only listing one phase of the journey',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['ValueProposition', 'CustomerSegment', 'CustomerRelationship'],
    },
  },
  {
    uri: `${business}CustomerRelationship`,
    localName: 'CustomerRelationship',
    label: 'Customer Relationship',
    domain: 'business',
    description: 'Type of customer interaction',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}CustomerRelationship`),
      { uri: `${business}hasRelationType`, localName: 'hasRelationType', label: 'Relation Type', type: 'data', domain: `${business}CustomerRelationship`, range: 'xsd:string' },
      // Relationship metrics
      { uri: `${business}costToMaintain`, localName: 'costToMaintain', label: 'Cost to Maintain', type: 'data', domain: `${business}CustomerRelationship`, range: 'xsd:decimal', description: 'Monthly cost to maintain this relationship' },
      { uri: `${business}touchpointFrequency`, localName: 'touchpointFrequency', label: 'Touchpoint Frequency', type: 'data', domain: `${business}CustomerRelationship`, range: 'xsd:string', description: 'Daily | Weekly | Monthly | Quarterly' },
      { uri: `${business}automationLevel`, localName: 'automationLevel', label: 'Automation Level', type: 'data', domain: `${business}CustomerRelationship`, range: 'xsd:string', description: 'Manual | SemiAutomated | FullyAutomated' },
      { uri: `${business}satisfactionScore`, localName: 'satisfactionScore', label: 'Satisfaction Score', type: 'data', domain: `${business}CustomerRelationship`, range: 'xsd:float', description: 'Customer satisfaction score (0-10)' },
      { uri: `${business}forSegment`, localName: 'forSegment', label: 'For Segment', type: 'object', domain: `${business}CustomerRelationship`, range: `${market}CustomerSegment`, description: 'Customer segment this relationship serves' },
    ],
    educational: {
      whatIsThis: 'Customer relationships define how you interact with each customer segment. Types range from automated self-service to dedicated personal assistance.',
      whenToUse: [
        'Designing customer experience',
        'Planning support and success strategy',
        'Scaling customer interactions',
      ],
      goodExamples: [
        'Self-service: Knowledge base, community forums',
        'Personal: Dedicated account manager for enterprise',
        'Automated: AI chatbot for tier-1 support',
      ],
      badExamples: [
        '"Good customer service" (not specific)',
        'Mixing up relationship type with channel',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Channel', 'CustomerSegment', 'KeyActivity'],
    },
  },
  {
    uri: `${business}RevenueStream`,
    localName: 'RevenueStream',
    label: 'Revenue Stream',
    domain: 'business',
    description: 'How income is generated from value propositions',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}RevenueStream`),
      { uri: `${business}generatedBy`, localName: 'generatedBy', label: 'Generated By', type: 'object', domain: `${business}RevenueStream`, range: `${business}ValueProposition` },
      { uri: `${business}hasPricingMechanism`, localName: 'hasPricingMechanism', label: 'Pricing Mechanism', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:string' },
      // Revenue metrics
      { uri: `${business}monthlyRecurringRevenue`, localName: 'monthlyRecurringRevenue', label: 'MRR', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:decimal', description: 'Monthly Recurring Revenue' },
      { uri: `${business}annualContractValue`, localName: 'annualContractValue', label: 'ACV', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:decimal', description: 'Annual Contract Value' },
      { uri: `${business}revenueType`, localName: 'revenueType', label: 'Revenue Type', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:string', description: 'Subscription | Transaction | Usage | Licensing' },
      { uri: `${business}churnRate`, localName: 'churnRate', label: 'Churn Rate', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:float', description: 'Monthly churn rate (0-1)' },
      { uri: `${business}expansionRate`, localName: 'expansionRate', label: 'Expansion Rate', type: 'data', domain: `${business}RevenueStream`, range: 'xsd:float', description: 'Net revenue expansion rate (0-1+)' },
      { uri: `${business}fromSegment`, localName: 'fromSegment', label: 'From Segment', type: 'object', domain: `${business}RevenueStream`, range: `${market}CustomerSegment`, description: 'Customer segment generating this revenue' },
    ],
    educational: {
      whatIsThis: 'Revenue streams represent how your company earns money from each customer segment. Different segments may have different pricing mechanisms.',
      whenToUse: [
        'Designing pricing strategy',
        'Validating business viability',
        'Planning revenue diversification',
      ],
      goodExamples: [
        'SaaS subscription: $99/month per user',
        'Usage-based: $0.01 per API call',
        'Freemium: Free tier + $299/month Pro',
      ],
      badExamples: [
        '"We will make money" (no mechanism)',
        '"Ads" without specificity',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['ValueProposition', 'CustomerSegment', 'CostStructure'],
    },
  },
  {
    uri: `${business}KeyResource`,
    localName: 'KeyResource',
    label: 'Key Resource',
    domain: 'business',
    description: 'Assets required to operate the business model',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}KeyResource`),
      { uri: `${business}hasResourceType`, localName: 'hasResourceType', label: 'Resource Type', type: 'data', domain: `${business}KeyResource`, range: 'xsd:string', description: 'Physical | Intellectual | Human | Financial' },
      { uri: `${business}enables`, localName: 'enables', label: 'Enables', type: 'object', domain: `${business}KeyResource`, range: `${business}KeyActivity` },
    ],
    educational: {
      whatIsThis: 'Key resources are the strategic assets required to make your business model work. They enable you to create and deliver your value proposition.',
      whenToUse: [
        'Identifying strategic assets',
        'Planning resource acquisition',
        'Assessing competitive moats',
      ],
      goodExamples: [
        'Intellectual: Proprietary ML models, patents',
        'Human: Engineering team, domain experts',
        'Financial: Series A funding, credit lines',
      ],
      badExamples: [
        '"Office space" (not strategic for most software)',
        'Listing every resource (focus on key ones)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['KeyActivity', 'KeyPartnership', 'CostStructure', 'Strength'],
    },
  },
  {
    uri: `${business}KeyActivity`,
    localName: 'KeyActivity',
    label: 'Key Activity',
    domain: 'business',
    description: 'Essential business actions',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}KeyActivity`),
      { uri: `${business}operationalizes`, localName: 'operationalizes', label: 'Operationalizes', type: 'object', domain: `${business}KeyActivity`, range: `${business}Strategy` },
      { uri: `${business}requires`, localName: 'requires', label: 'Requires', type: 'object', domain: `${business}KeyActivity`, range: `${business}KeyResource` },
      { uri: `${business}leverages`, localName: 'leverages', label: 'Leverages', type: 'object', domain: `${business}KeyActivity`, range: `${business}KeyPartnership` },
    ],
    educational: {
      whatIsThis: 'Key activities are the critical actions your company must perform to operate successfully. They directly enable your value proposition.',
      whenToUse: [
        'Defining operational priorities',
        'Allocating resources and budget',
        'Identifying outsourcing opportunities',
      ],
      goodExamples: [
        'Platform development and maintenance',
        'Customer success and onboarding',
        'ML model training and improvement',
      ],
      badExamples: [
        '"Running the business" (too generic)',
        'Administrative tasks (not strategic)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['KeyResource', 'KeyPartnership', 'Epic', 'Strategy'],
    },
  },
  {
    uri: `${business}KeyPartnership`,
    localName: 'KeyPartnership',
    label: 'Key Partnership',
    domain: 'business',
    description: 'Network of suppliers and partners',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}KeyPartnership`),
      { uri: `${business}providesResource`, localName: 'providesResource', label: 'Provides Resource', type: 'object', domain: `${business}KeyPartnership`, range: `${business}KeyResource` },
    ],
    educational: {
      whatIsThis: 'Key partnerships are the network of suppliers, partners, and alliances that help your business model work. They provide resources or perform activities.',
      whenToUse: [
        'Identifying strategic alliances',
        'Reducing risk through partnerships',
        'Accessing resources you cannot build',
      ],
      goodExamples: [
        'Cloud provider: AWS, GCP for infrastructure',
        'Integration partner: Salesforce for CRM data',
        'Channel partner: Consulting firms for implementation',
      ],
      badExamples: [
        '"Vendors" (too generic)',
        'Listing every supplier (focus on strategic ones)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['KeyResource', 'KeyActivity', 'CostStructure'],
    },
  },
  {
    uri: `${business}CostStructure`,
    localName: 'CostStructure',
    label: 'Cost Structure',
    domain: 'business',
    description: 'Costs incurred by the business model',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}CostStructure`),
      { uri: `${business}drivenBy`, localName: 'drivenBy', label: 'Driven By', type: 'object', domain: `${business}CostStructure`, range: `${business}KeyActivity` },
      { uri: `${business}hasCostType`, localName: 'hasCostType', label: 'Cost Type', type: 'data', domain: `${business}CostStructure`, range: 'xsd:string', description: 'Fixed | Variable' },
      // Cost metrics
      { uri: `${business}costAmount`, localName: 'costAmount', label: 'Cost Amount', type: 'data', domain: `${business}CostStructure`, range: 'xsd:decimal', description: 'Cost amount in currency' },
      { uri: `${business}costFrequency`, localName: 'costFrequency', label: 'Cost Frequency', type: 'data', domain: `${business}CostStructure`, range: 'xsd:string', description: 'OneTime | Monthly | Annual' },
      { uri: `${business}isVariableCost`, localName: 'isVariableCost', label: 'Is Variable Cost', type: 'data', domain: `${business}CostStructure`, range: 'xsd:boolean', description: 'Does cost scale with usage?' },
      { uri: `${business}percentageOfRevenue`, localName: 'percentageOfRevenue', label: 'Percentage of Revenue', type: 'data', domain: `${business}CostStructure`, range: 'xsd:float', description: 'Cost as percentage of revenue (0-100)' },
    ],
    educational: {
      whatIsThis: 'Cost structure describes the major costs incurred while operating your business model. Costs can be fixed (rent, salaries) or variable (cloud compute, transactions).',
      whenToUse: [
        'Building financial models',
        'Pricing decisions',
        'Identifying cost optimization opportunities',
      ],
      goodExamples: [
        'Fixed: Engineering salaries, office rent',
        'Variable: Cloud infrastructure, payment processing',
        'Semi-variable: Customer support (scales with users)',
      ],
      badExamples: [
        '"Expenses" (too generic)',
        'Ignoring hidden costs',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['KeyActivity', 'KeyResource', 'RevenueStream'],
    },
  },
  {
    uri: `${business}Strategy`,
    localName: 'Strategy',
    label: 'Strategy',
    domain: 'business',
    description: 'Strategic direction and vision',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Strategy`),
      { uri: `${business}hasVision`, localName: 'hasVision', label: 'Vision', type: 'data', domain: `${business}Strategy`, range: 'xsd:string' },
      { uri: `${business}defines`, localName: 'defines', label: 'Defines', type: 'object', domain: `${business}Strategy`, range: `${business}StrategicGoal` },
    ],
    educational: {
      whatIsThis: 'Strategy defines your long-term direction and how you will achieve your vision. It guides all business decisions and resource allocation.',
      whenToUse: [
        'Setting company direction',
        'Aligning teams around common goals',
        'Making trade-off decisions',
      ],
      goodExamples: [
        'Vision: Become the leading AI-powered finance platform for mid-market',
        'Strategy: Land-and-expand in DACH, then expand to EU',
        'Focus: Differentiate on ease-of-use and time-to-value',
      ],
      badExamples: [
        '"Make money" (not a strategy)',
        '"Be the best" (not actionable)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['StrategicGoal', 'Theme', 'KeyActivity'],
    },
  },
  {
    uri: `${business}StrategicGoal`,
    localName: 'StrategicGoal',
    label: 'Strategic Goal',
    domain: 'business',
    description: 'A strategic objective (hard or soft goal)',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}StrategicGoal`),
      { uri: `${business}hasGoalType`, localName: 'hasGoalType', label: 'Goal Type', type: 'data', domain: `${business}StrategicGoal`, range: 'xsd:string', description: 'HardGoal | SoftGoal' },
      { uri: `${business}operationalizedBy`, localName: 'operationalizedBy', label: 'Operationalized By', type: 'object', domain: `${business}StrategicGoal`, range: `${solution}Theme` },
      // Goal tracking metrics
      { uri: `${business}targetValue`, localName: 'targetValue', label: 'Target Value', type: 'data', domain: `${business}StrategicGoal`, range: 'xsd:decimal', description: 'Target value for this goal' },
      { uri: `${business}currentValue`, localName: 'currentValue', label: 'Current Value', type: 'data', domain: `${business}StrategicGoal`, range: 'xsd:decimal', description: 'Current progress value' },
      { uri: `${business}deadline`, localName: 'deadline', label: 'Deadline', type: 'data', domain: `${business}StrategicGoal`, range: 'xsd:dateTime', description: 'Target completion date' },
      { uri: `${business}progressPercentage`, localName: 'progressPercentage', label: 'Progress Percentage', type: 'data', domain: `${business}StrategicGoal`, range: 'xsd:float', description: 'Progress towards goal (0-100)' },
      { uri: `${business}ownerPersona`, localName: 'ownerPersona', label: 'Owner Persona', type: 'object', domain: `${business}StrategicGoal`, range: `${market}Persona`, description: 'Persona responsible for this goal' },
    ],
    educational: {
      whatIsThis: 'Strategic goals are specific objectives that support your strategy. Hard goals are measurable (OKRs), soft goals are qualitative (improve UX).',
      whenToUse: [
        'Breaking strategy into actionable objectives',
        'Setting quarterly/annual OKRs',
        'Aligning product roadmap with business goals',
      ],
      goodExamples: [
        'Hard: Achieve 1000 paying customers by Q4',
        'Hard: Reduce churn to <5% annually',
        'Soft: Establish thought leadership in AI finance',
      ],
      badExamples: [
        '"Grow" (not specific)',
        '"Be successful" (not measurable)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Strategy', 'Theme', 'Epic'],
    },
  },
  {
    uri: `${business}Decision`,
    localName: 'Decision',
    label: 'Decision',
    domain: 'business',
    description: 'A strategic, tactical, or operational decision made by the organization',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Decision`),
      { uri: `${business}hasDecisionType`, localName: 'hasDecisionType', label: 'Decision Type', type: 'data', domain: `${business}Decision`, range: 'xsd:string', description: 'Strategic | Tactical | Technical | Process | Tradeoff' },
      { uri: `${business}hasRationale`, localName: 'hasRationale', label: 'Rationale', type: 'data', domain: `${business}Decision`, range: 'xsd:string', description: 'Why was this decision made?' },
      { uri: `${business}hasReversibility`, localName: 'hasReversibility', label: 'Reversibility', type: 'data', domain: `${business}Decision`, range: 'xsd:string', description: 'OneWayDoor | TwoWayDoor' },
      { uri: `${business}decidedBy`, localName: 'decidedBy', label: 'Decided By', type: 'object', domain: `${business}Decision`, range: `${market}Persona` },
      { uri: `${business}relatedToGoal`, localName: 'relatedToGoal', label: 'Related To Goal', type: 'object', domain: `${business}Decision`, range: `${business}StrategicGoal` },
      // Decision context and evidence
      { uri: `${business}alternativesConsidered`, localName: 'alternativesConsidered', label: 'Alternatives Considered', type: 'data', domain: `${business}Decision`, range: 'xsd:string', description: 'Other options that were considered' },
      { uri: `${business}supportingEvidence`, localName: 'supportingEvidence', label: 'Supporting Evidence', type: 'object', domain: `${business}Decision`, range: `${io}Facet`, description: 'Evidence supporting this decision' },
      { uri: `${business}assumptionsDependsOn`, localName: 'assumptionsDependsOn', label: 'Depends On Assumptions', type: 'object', domain: `${business}Decision`, range: `${business}Assumption`, description: 'Assumptions this decision depends on' },
      { uri: `${business}impactedFeatures`, localName: 'impactedFeatures', label: 'Impacted Features', type: 'object', domain: `${business}Decision`, range: `${solution}Feature`, description: 'Features affected by this decision' },
      { uri: `${business}reviewDate`, localName: 'reviewDate', label: 'Review Date', type: 'data', domain: `${business}Decision`, range: 'xsd:dateTime', description: 'When to review this decision' },
    ],
    educational: {
      whatIsThis: 'A decision captures choices made by the organization along with the rationale, context, and considerations involved.',
      whenToUse: [
        'Documenting important choices and their reasoning',
        'Creating an architectural decision record (ADR)',
        'Tracking tradeoffs and alternatives considered',
      ],
      goodExamples: [
        'Decided to use PostgreSQL for primary database due to JSON support',
        'Chose microservices architecture to enable team autonomy',
        'Selected Stripe over homegrown billing to reduce time-to-market',
      ],
      badExamples: [
        '"Use good technology" (not a specific decision)',
        'Missing rationale for why the decision was made',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['StrategicGoal', 'Constraint', 'Risk'],
    },
  },
  {
    uri: `${business}BusinessContext`,
    localName: 'BusinessContext',
    label: 'Business Context',
    domain: 'business',
    description: 'Strategic business context including goals, metrics, and organizational information',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}BusinessContext`),
      { uri: `${business}hasContextType`, localName: 'hasContextType', label: 'Context Type', type: 'data', domain: `${business}BusinessContext`, range: 'xsd:string', description: 'Goal | Metric | Constraint | Initiative | Background' },
      { uri: `${business}hasTimeframe`, localName: 'hasTimeframe', label: 'Timeframe', type: 'data', domain: `${business}BusinessContext`, range: 'xsd:string', description: 'When is this relevant?' },
      { uri: `${business}relatedToStrategy`, localName: 'relatedToStrategy', label: 'Related To Strategy', type: 'object', domain: `${business}BusinessContext`, range: `${business}Strategy` },
    ],
    educational: {
      whatIsThis: 'Business context captures strategic information like company goals, KPIs, initiatives, or organizational constraints that provide backdrop for decisions.',
      whenToUse: [
        'Recording strategic context from stakeholder interviews',
        'Documenting business drivers and success metrics',
        'Capturing organizational constraints and priorities',
      ],
      goodExamples: [
        'Company goal to reach $10M ARR by end of year',
        'Initiative to expand into European market',
        'Budget constraint of $50K for new tooling',
      ],
      badExamples: [
        '"Company is doing well" (too vague)',
        'Missing timeframe or measurability',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['StrategicGoal', 'Strategy', 'Decision'],
    },
  },
  // SWOT
  {
    uri: `${business}Strength`,
    localName: 'Strength',
    label: 'Strength',
    domain: 'business',
    description: 'Internal strength (SWOT)',
    subClassOf: `${business}SWOTElement`,
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Strength`),
      { uri: `${business}linkedTo`, localName: 'linkedTo', label: 'Linked To', type: 'object', domain: `${business}Strength`, range: `${business}KeyResource` },
    ],
    educational: {
      whatIsThis: 'A strength is an internal positive attribute that gives you competitive advantage. Strengths are things you do well or resources you have.',
      whenToUse: [
        'SWOT analysis',
        'Identifying competitive advantages',
        'Building on existing capabilities',
      ],
      goodExamples: [
        'Proprietary ML technology with 95% accuracy',
        'Team with deep domain expertise in finance',
        'Strong brand recognition in target market',
      ],
      badExamples: [
        '"Good team" (not specific)',
        'External factors (those are opportunities)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Weakness', 'KeyResource', 'ValueProposition'],
    },
  },
  {
    uri: `${business}Weakness`,
    localName: 'Weakness',
    label: 'Weakness',
    domain: 'business',
    description: 'Internal weakness (SWOT)',
    subClassOf: `${business}SWOTElement`,
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Weakness`),
      // SWOT weakness classification
      { uri: `${business}weaknessType`, localName: 'weaknessType', label: 'Weakness Type', type: 'data', domain: `${business}Weakness`, range: 'xsd:string', description: 'Resource | Capability | Process | Brand | Financial' },
      { uri: `${business}severity`, localName: 'severity', label: 'Severity', type: 'data', domain: `${business}Weakness`, range: 'xsd:string', description: 'Low | Medium | High | Critical' },
      { uri: `${business}mitigationPlan`, localName: 'mitigationPlan', label: 'Mitigation Plan', type: 'data', domain: `${business}Weakness`, range: 'xsd:string', description: 'Plan to address or mitigate this weakness' },
      { uri: `${business}mitigationStatus`, localName: 'mitigationStatus', label: 'Mitigation Status', type: 'data', domain: `${business}Weakness`, range: 'xsd:string', description: 'NotStarted | InProgress | Resolved' },
      { uri: `${business}createsVulnerabilityTo`, localName: 'createsVulnerabilityTo', label: 'Creates Vulnerability To', type: 'object', domain: `${business}Weakness`, range: `${business}Threat`, description: 'Threats this weakness makes more likely' },
    ],
    educational: {
      whatIsThis: 'A weakness is an internal negative attribute that puts you at a disadvantage. Weaknesses are areas where you underperform or lack resources.',
      whenToUse: [
        'SWOT analysis',
        'Identifying improvement areas',
        'Risk assessment',
      ],
      goodExamples: [
        'Limited engineering bandwidth for new features',
        'No presence in US market yet',
        'High customer acquisition cost',
      ],
      badExamples: [
        '"Competition" (external, that is a threat)',
        '"Need more money" (too generic)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Strength', 'Threat', 'KeyResource'],
    },
  },
  {
    uri: `${business}Opportunity`,
    localName: 'Opportunity',
    label: 'Opportunity',
    domain: 'business',
    description: 'External opportunity (SWOT)',
    subClassOf: `${business}SWOTElement`,
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Opportunity`),
      { uri: `${business}linkedToTrend`, localName: 'linkedToTrend', label: 'Linked To Trend', type: 'object', domain: `${business}Opportunity`, range: `${market}Trend` },
      // OST (Opportunity Solution Tree) properties
      { uri: `${business}parentOpportunity`, localName: 'parentOpportunity', label: 'Parent Opportunity', type: 'object', domain: `${business}Opportunity`, range: `${business}Opportunity`, description: 'Parent opportunity in the OST hierarchy' },
      { uri: `${business}childOpportunities`, localName: 'childOpportunities', label: 'Child Opportunities', type: 'object', domain: `${business}Opportunity`, range: `${business}Opportunity`, description: 'Child opportunities in the OST hierarchy' },
      { uri: `${business}linkedOutcome`, localName: 'linkedOutcome', label: 'Linked Outcome', type: 'object', domain: `${business}Opportunity`, range: `${business}StrategicGoal`, description: 'Strategic goal this opportunity supports' },
      { uri: `${business}consideredSolutions`, localName: 'consideredSolutions', label: 'Considered Solutions', type: 'object', domain: `${business}Opportunity`, range: `${solution}Feature`, description: 'Features being considered to address this opportunity' },
      { uri: `${business}selectedSolution`, localName: 'selectedSolution', label: 'Selected Solution', type: 'object', domain: `${business}Opportunity`, range: `${solution}Feature`, description: 'Feature selected to address this opportunity' },
      { uri: `${business}discoveryStatus`, localName: 'discoveryStatus', label: 'Discovery Status', type: 'data', domain: `${business}Opportunity`, range: 'xsd:string', description: 'Identified | Exploring | Validated | Parked' },
      { uri: `${business}priorityScore`, localName: 'priorityScore', label: 'Priority Score', type: 'data', domain: `${business}Opportunity`, range: 'xsd:decimal', description: 'Prioritization score for this opportunity' },
    ],
    educational: {
      whatIsThis: 'An opportunity is an external factor you can exploit to your advantage. Opportunities arise from market trends, competitor weaknesses, or regulatory changes.',
      whenToUse: [
        'SWOT analysis',
        'Strategic planning',
        'Identifying growth vectors',
      ],
      goodExamples: [
        'Growing demand for AI automation in finance',
        'Competitor exiting the mid-market segment',
        'New regulations requiring better compliance tools',
      ],
      badExamples: [
        '"We are good at X" (internal, that is a strength)',
        '"Market exists" (too generic)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Threat', 'Trend', 'MarketSignal'],
    },
  },
  {
    uri: `${business}Threat`,
    localName: 'Threat',
    label: 'Threat',
    domain: 'business',
    description: 'External threat (SWOT)',
    subClassOf: `${business}SWOTElement`,
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Threat`),
      { uri: `${business}linkedToCompetitor`, localName: 'linkedToCompetitor', label: 'Linked To Competitor', type: 'object', domain: `${business}Threat`, range: `${market}Competitor` },
    ],
    educational: {
      whatIsThis: 'A threat is an external factor that could negatively impact your business. Threats come from competitors, market changes, or economic conditions.',
      whenToUse: [
        'SWOT analysis',
        'Risk management',
        'Defensive strategy planning',
      ],
      goodExamples: [
        'Large competitor entering your market',
        'Economic downturn reducing customer budgets',
        'New regulations increasing compliance costs',
      ],
      badExamples: [
        '"We lack resources" (internal, that is a weakness)',
        '"Competition exists" (too generic)',
      ],
      discoveryPhase: 'Market Discovery',
      relatedConcepts: ['Opportunity', 'Competitor', 'CompetitiveForce'],
    },
  },
  // GTM & Strategic Planning
  {
    uri: `${business}GoToMarketStrategy`,
    localName: 'GoToMarketStrategy',
    label: 'Go-to-Market Strategy',
    domain: 'business',
    description: 'The plan for launching and selling a product to the market',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}GoToMarketStrategy`),
      { uri: `${business}hasPositioning`, localName: 'hasPositioning', label: 'Has Positioning', type: 'object', domain: `${business}GoToMarketStrategy`, range: `${business}Positioning` },
      { uri: `${business}targetsSegment`, localName: 'targetsSegment', label: 'Targets Segment', type: 'object', domain: `${business}GoToMarketStrategy`, range: `${market}CustomerSegment` },
      { uri: `${business}usesChannel`, localName: 'usesChannel', label: 'Uses Channel', type: 'object', domain: `${business}GoToMarketStrategy`, range: `${business}Channel` },
      { uri: `${business}hasLaunchType`, localName: 'hasLaunchType', label: 'Launch Type', type: 'data', domain: `${business}GoToMarketStrategy`, range: 'xsd:string', description: 'SoftLaunch | Beta | GA | Enterprise' },
      // GTM strategy details
      { uri: `${business}gtmMotion`, localName: 'gtmMotion', label: 'GTM Motion', type: 'data', domain: `${business}GoToMarketStrategy`, range: 'xsd:string', description: 'PLG | SalesLed | ChannelLed | Hybrid' },
      { uri: `${business}primaryChannel`, localName: 'primaryChannel', label: 'Primary Channel', type: 'object', domain: `${business}GoToMarketStrategy`, range: `${business}Channel`, description: 'Main acquisition channel' },
      { uri: `${business}launchDate`, localName: 'launchDate', label: 'Launch Date', type: 'data', domain: `${business}GoToMarketStrategy`, range: 'xsd:dateTime', description: 'Target launch date' },
      { uri: `${business}targetMRR`, localName: 'targetMRR', label: 'Target MRR', type: 'data', domain: `${business}GoToMarketStrategy`, range: 'xsd:decimal', description: 'Target Monthly Recurring Revenue at launch' },
    ],
    educational: {
      whatIsThis: 'A Go-to-Market (GTM) strategy is your plan for launching a product to the market. It covers positioning, channels, pricing, and launch sequencing.',
      whenToUse: [
        'Before launching a new product',
        'Entering a new market segment',
        'Repositioning an existing product',
      ],
      goodExamples: [
        'Beta launch to 20 design partners, then soft launch via Product Hunt',
        'Enterprise-first GTM with direct sales in DACH, then expand to channel partners',
        'PLG motion: free tier → conversion to paid via in-app upgrade',
      ],
      badExamples: [
        '"Launch it and see" (no strategy)',
        '"Marketing will figure it out" (not integrated)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Positioning', 'Channel', 'LaunchPlan', 'CustomerSegment'],
    },
  },
  {
    uri: `${business}Positioning`,
    localName: 'Positioning',
    label: 'Positioning',
    domain: 'business',
    description: 'How the product is perceived in the market relative to competitors',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Positioning`),
      { uri: `${business}hasPositioningStatement`, localName: 'hasPositioningStatement', label: 'Positioning Statement', type: 'data', domain: `${business}Positioning`, range: 'xsd:string' },
      { uri: `${business}differentiatesFrom`, localName: 'differentiatesFrom', label: 'Differentiates From', type: 'object', domain: `${business}Positioning`, range: `${market}Competitor` },
      { uri: `${business}hasCategory`, localName: 'hasCategory', label: 'Market Category', type: 'data', domain: `${business}Positioning`, range: 'xsd:string' },
      // Positioning details
      { uri: `${business}targetAudience`, localName: 'targetAudience', label: 'Target Audience', type: 'object', domain: `${business}Positioning`, range: `${market}CustomerSegment`, description: 'Primary target audience' },
      { uri: `${business}uniqueValue`, localName: 'uniqueValue', label: 'Unique Value', type: 'data', domain: `${business}Positioning`, range: 'xsd:string', description: 'What makes this offering unique?' },
      { uri: `${business}proofPoints`, localName: 'proofPoints', label: 'Proof Points', type: 'data', domain: `${business}Positioning`, range: 'xsd:string', description: 'Evidence supporting the positioning' },
      { uri: `${business}competitorDifferentiation`, localName: 'competitorDifferentiation', label: 'Competitor Differentiation', type: 'data', domain: `${business}Positioning`, range: 'xsd:string', description: 'How we differ from competitors' },
    ],
    educational: {
      whatIsThis: 'Positioning defines how your product occupies a distinct place in the minds of your target customers relative to competitors.',
      whenToUse: [
        'Defining your market category',
        'Creating marketing messaging',
        'Sales enablement and pitch decks',
      ],
      goodExamples: [
        'For [mid-market CFOs] who [struggle with manual reconciliation], [FinanceAI] is a [automated finance platform] that [reduces close time by 60%]',
        'Unlike [Salesforce], we focus on [ease of use for SMBs]',
      ],
      badExamples: [
        '"We are the best" (not differentiated)',
        '"AI-powered solution" (feature, not position)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['GoToMarketStrategy', 'ValueProposition', 'Competitor'],
    },
  },
  {
    uri: `${business}Milestone`,
    localName: 'Milestone',
    label: 'Milestone',
    domain: 'business',
    description: 'A significant checkpoint in the product lifecycle',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Milestone`),
      { uri: `${business}hasMilestoneType`, localName: 'hasMilestoneType', label: 'Milestone Type', type: 'data', domain: `${business}Milestone`, range: 'xsd:string', description: 'Discovery | Shape | Launch | Growth' },
      { uri: `${business}hasTargetDate`, localName: 'hasTargetDate', label: 'Target Date', type: 'data', domain: `${business}Milestone`, range: 'xsd:date' },
      { uri: `${business}hasStatus`, localName: 'hasStatus', label: 'Status', type: 'data', domain: `${business}Milestone`, range: 'xsd:string', description: 'NotStarted | InProgress | Completed | Blocked' },
      { uri: `${business}achieves`, localName: 'achieves', label: 'Achieves', type: 'object', domain: `${business}Milestone`, range: `${business}StrategicGoal` },
    ],
    educational: {
      whatIsThis: 'A milestone is a significant checkpoint that marks progress toward a goal. Milestones help track progress and align teams.',
      whenToUse: [
        'Project planning and tracking',
        'Communicating progress to stakeholders',
        'Go/no-go decision points',
      ],
      goodExamples: [
        'Discovery Complete: PMF score > 70%',
        'Beta Launch: 20 active users with NPS > 30',
        'GA Launch: $10K MRR achieved',
      ],
      badExamples: [
        '"Make progress" (not measurable)',
        'Tasks instead of outcomes',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['StrategicGoal', 'LaunchPlan', 'Epic'],
    },
  },
  {
    uri: `${business}Risk`,
    localName: 'Risk',
    label: 'Risk',
    domain: 'business',
    description: 'A potential event that could negatively impact the project',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}Risk`),
      { uri: `${business}hasRiskType`, localName: 'hasRiskType', label: 'Risk Type', type: 'data', domain: `${business}Risk`, range: 'xsd:string', description: 'Technical | Market | Financial | Regulatory | Operational' },
      { uri: `${business}hasProbability`, localName: 'hasProbability', label: 'Probability', type: 'data', domain: `${business}Risk`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${business}hasImpact`, localName: 'hasImpact', label: 'Impact', type: 'data', domain: `${business}Risk`, range: 'xsd:string', description: 'Low | Medium | High' },
      { uri: `${business}hasMitigation`, localName: 'hasMitigation', label: 'Mitigation', type: 'data', domain: `${business}Risk`, range: 'xsd:string' },
      // Risk management properties
      { uri: `${business}riskScore`, localName: 'riskScore', label: 'Risk Score', type: 'data', domain: `${business}Risk`, range: 'xsd:decimal', description: 'Calculated risk score (probability x impact)' },
      { uri: `${business}contingencyPlan`, localName: 'contingencyPlan', label: 'Contingency Plan', type: 'data', domain: `${business}Risk`, range: 'xsd:string', description: 'Backup plan if risk materializes' },
      { uri: `${business}owner`, localName: 'owner', label: 'Owner', type: 'object', domain: `${business}Risk`, range: `${market}Persona`, description: 'Person responsible for monitoring this risk' },
      { uri: `${business}monitoringFrequency`, localName: 'monitoringFrequency', label: 'Monitoring Frequency', type: 'data', domain: `${business}Risk`, range: 'xsd:string', description: 'Daily | Weekly | Monthly | Quarterly' },
      { uri: `${business}lastReviewed`, localName: 'lastReviewed', label: 'Last Reviewed', type: 'data', domain: `${business}Risk`, range: 'xsd:dateTime', description: 'When this risk was last reviewed' },
    ],
    educational: {
      whatIsThis: 'A risk is a potential future event that could negatively impact your project. Risks are tracked with probability, impact, and mitigation strategies.',
      whenToUse: [
        'Project planning and risk assessment',
        'Go/no-go decisions',
        'Stakeholder communication',
      ],
      goodExamples: [
        'Technical: ML model accuracy may not reach 90% target (High probability, High impact)',
        'Market: Competitor may launch similar feature (Medium, Medium)',
        'Regulatory: GDPR compliance requirements unclear (Low, High)',
      ],
      badExamples: [
        '"Things might go wrong" (not specific)',
        'Opportunities confused with risks',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Threat', 'Constraint', 'Milestone'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW BUSINESS LAYER CLASSES - HIGH PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${business}Metric`,
    localName: 'Metric',
    label: 'Metric',
    domain: 'business',
    description: 'A measurable indicator of business performance (KPI)',
    properties: [
      { uri: `${business}metricType`, localName: 'metricType', label: 'Metric Type', type: 'data', domain: `${business}Metric`, range: 'xsd:string', description: 'Leading | Lagging | Input | Output | Outcome' },
      { uri: `${business}metricCategory`, localName: 'metricCategory', label: 'Metric Category', type: 'data', domain: `${business}Metric`, range: 'xsd:string', description: 'Revenue | Growth | Efficiency | Quality | Customer | Product' },
      { uri: `${business}unit`, localName: 'unit', label: 'Unit', type: 'data', domain: `${business}Metric`, range: 'xsd:string', description: 'Unit of measurement (%, $, count, etc.)' },
      { uri: `${business}targetValue`, localName: 'targetValue', label: 'Target Value', type: 'data', domain: `${business}Metric`, range: 'xsd:decimal', description: 'Target or goal value' },
      { uri: `${business}currentValue`, localName: 'currentValue', label: 'Current Value', type: 'data', domain: `${business}Metric`, range: 'xsd:decimal', description: 'Current actual value' },
      { uri: `${business}measurementFrequency`, localName: 'measurementFrequency', label: 'Measurement Frequency', type: 'data', domain: `${business}Metric`, range: 'xsd:string', description: 'Daily | Weekly | Monthly | Quarterly | Annual' },
      { uri: `${business}measuresGoal`, localName: 'measuresGoal', label: 'Measures Goal', type: 'object', domain: `${business}Metric`, range: `${business}StrategicGoal`, description: 'Strategic goal this metric measures' },
      ...STATUS_PROPERTIES(`${business}Metric`),
      ...DESCRIPTIVE_PROPERTIES(`${business}Metric`),
      ...TEMPORAL_PROPERTIES(`${business}Metric`),
    ],
    educational: {
      whatIsThis: 'A metric (KPI) is a quantifiable measure used to track business performance and goal achievement. Leading metrics predict future results; lagging metrics measure past results.',
      whenToUse: [
        'Tracking progress toward strategic goals',
        'Measuring business health and performance',
        'Making data-driven decisions',
      ],
      goodExamples: [
        'MRR (Monthly Recurring Revenue): $500K target, $450K current, tracked monthly',
        'NPS (Net Promoter Score): 50 target, 45 current, lagging indicator',
        'Trial-to-Paid Conversion: 25% target, 20% current, leading indicator',
      ],
      badExamples: [
        'Vanity metrics without targets or actions',
        'Metrics not linked to strategic goals',
      ],
      discoveryPhase: 'Strategy',
      relatedConcepts: ['StrategicGoal', 'Strategy', 'Milestone'],
    },
  },

  {
    uri: `${business}SuccessMetric`,
    localName: 'SuccessMetric',
    label: 'Success Metric',
    domain: 'business',
    description: 'Metric used to measure success of a goal, initiative, or feature',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${business}SuccessMetric`),
      { uri: `${business}measuresGoal`, localName: 'measuresGoal', label: 'Measures Goal', type: 'object', domain: `${business}SuccessMetric`, range: `${business}StrategicGoal` },
      { uri: `${business}measuresFeature`, localName: 'measuresFeature', label: 'Measures Feature', type: 'object', domain: `${business}SuccessMetric`, range: `${solution}Feature` },
      { uri: `${business}target`, localName: 'target', label: 'Target Value', type: 'data', domain: `${business}SuccessMetric`, range: 'xsd:decimal' },
      { uri: `${business}current`, localName: 'current', label: 'Current Value', type: 'data', domain: `${business}SuccessMetric`, range: 'xsd:decimal' },
      { uri: `${business}unit`, localName: 'unit', label: 'Unit', type: 'data', domain: `${business}SuccessMetric`, range: 'xsd:string', description: 'e.g., users, %, $' },
      { uri: `${business}metricType`, localName: 'metricType', label: 'Metric Type', type: 'data', domain: `${business}SuccessMetric`, range: 'xsd:string', description: 'leading | lagging' },
      ...QUANTITATIVE_PROPERTIES(`${business}SuccessMetric`),
      ...STATUS_PROPERTIES(`${business}SuccessMetric`),
      ...TEMPORAL_PROPERTIES(`${business}SuccessMetric`),
    ],
    educational: {
      whatIsThis: 'A success metric defines how you measure the success of a strategic goal, product feature, or business initiative. Good metrics are specific, measurable, and tied to outcomes.',
      whenToUse: [
        'Defining how to measure goal achievement',
        'Tracking feature adoption and impact',
        'Establishing OKRs and KPIs',
      ],
      goodExamples: [
        '"Increase MAU by 20% within 6 months" (goal metric)',
        '"Reduce support tickets by 30% after feature launch" (feature metric)',
        '"Achieve 90% customer satisfaction score" (health metric)',
      ],
      badExamples: [
        '"Make users happy" (not measurable)',
        '"Improve performance" (no target or unit)',
      ],
      discoveryPhase: 'Validation',
      discoveryRequired: true,
      relatedConcepts: ['StrategicGoal', 'Feature', 'Metric', 'Experiment'],
    },
  },

  {
    uri: `${business}Assumption`,
    localName: 'Assumption',
    label: 'Assumption',
    domain: 'business',
    description: 'An untested hypothesis that needs validation before building',
    properties: [
      { uri: `${business}assumptionType`, localName: 'assumptionType', label: 'Assumption Type', type: 'data', domain: `${business}Assumption`, range: 'xsd:string', description: 'Customer | Problem | Solution | Market | Business | Technical' },
      { uri: `${business}riskLevel`, localName: 'riskLevel', label: 'Risk Level', type: 'data', domain: `${business}Assumption`, range: 'xsd:string', description: 'Low | Medium | High | Critical' },
      { uri: `${business}validationStatus`, localName: 'validationStatus', label: 'Validation Status', type: 'data', domain: `${business}Assumption`, range: 'xsd:string', description: 'Untested | Testing | Validated | Invalidated' },
      { uri: `${business}validatedBy`, localName: 'validatedBy', label: 'Validated By', type: 'object', domain: `${business}Assumption`, range: `${business}Experiment`, description: 'Experiment that validates this assumption' },
      { uri: `${business}relatedToVP`, localName: 'relatedToVP', label: 'Related To VP', type: 'object', domain: `${business}Assumption`, range: `${business}ValueProposition`, description: 'Value proposition this assumption underpins' },
      // Evidence and testing properties
      { uri: `${business}confidenceLevel`, localName: 'confidenceLevel', label: 'Confidence Level', type: 'data', domain: `${business}Assumption`, range: 'xsd:decimal', description: 'Confidence in this assumption (0-1)' },
      { uri: `${business}evidenceStrength`, localName: 'evidenceStrength', label: 'Evidence Strength', type: 'data', domain: `${business}Assumption`, range: 'xsd:string', description: 'None | Weak | Moderate | Strong' },
      { uri: `${business}testCost`, localName: 'testCost', label: 'Test Cost', type: 'data', domain: `${business}Assumption`, range: 'xsd:decimal', description: 'Estimated cost to test this assumption' },
      { uri: `${business}testTime`, localName: 'testTime', label: 'Test Time', type: 'data', domain: `${business}Assumption`, range: 'xsd:integer', description: 'Estimated days to test this assumption' },
      { uri: `${business}relatedToPain`, localName: 'relatedToPain', label: 'Related To Pain', type: 'object', domain: `${business}Assumption`, range: `${market}CustomerPain`, description: 'Customer pain this assumption relates to' },
      { uri: `${business}supportingFacets`, localName: 'supportingFacets', label: 'Supporting Facets', type: 'object', domain: `${business}Assumption`, range: `${io}Facet`, description: 'Facets that support or refute this assumption' },
      ...STATUS_PROPERTIES(`${business}Assumption`),
      ...DESCRIPTIVE_PROPERTIES(`${business}Assumption`),
      ...TEMPORAL_PROPERTIES(`${business}Assumption`),
    ],
    educational: {
      whatIsThis: 'Assumptions are untested beliefs about customers, problems, or solutions. Lean Startup methodology requires testing riskiest assumptions first before investing in building.',
      whenToUse: [
        'Identifying what you believe to be true but haven\'t validated',
        'Prioritizing what to test before building',
        'Reducing risk through systematic validation',
      ],
      goodExamples: [
        'Customer assumption: "CFOs will pay for real-time insights" - High risk, needs validation',
        'Problem assumption: "Manual reconciliation causes errors > 5% of time" - Medium risk',
        'Solution assumption: "AI can achieve 95% accuracy" - Critical technical risk',
      ],
      badExamples: [
        'Known facts stated as assumptions',
        'Assumptions too vague to test',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['Experiment', 'ValueProposition', 'Risk'],
    },
  },
  {
    uri: `${business}Experiment`,
    localName: 'Experiment',
    label: 'Experiment',
    domain: 'business',
    description: 'A test designed to validate or invalidate an assumption',
    properties: [
      { uri: `${business}experimentType`, localName: 'experimentType', label: 'Experiment Type', type: 'data', domain: `${business}Experiment`, range: 'xsd:string', description: 'Interview | Survey | MVP | Prototype | ABTest | Concierge | WizardOfOz' },
      { uri: `${business}hypothesis`, localName: 'hypothesis', label: 'Hypothesis', type: 'data', domain: `${business}Experiment`, range: 'xsd:string', description: 'Specific testable hypothesis' },
      { uri: `${business}successCriteria`, localName: 'successCriteria', label: 'Success Criteria', type: 'data', domain: `${business}Experiment`, range: 'xsd:string', description: 'How to measure success/failure' },
      { uri: `${business}result`, localName: 'result', label: 'Result', type: 'data', domain: `${business}Experiment`, range: 'xsd:string', description: 'NotStarted | InProgress | Success | Failure | Inconclusive' },
      { uri: `${business}learnings`, localName: 'learnings', label: 'Learnings', type: 'data', domain: `${business}Experiment`, range: 'xsd:string', description: 'Key learnings from experiment' },
      { uri: `${business}testsAssumption`, localName: 'testsAssumption', label: 'Tests Assumption', type: 'object', domain: `${business}Experiment`, range: `${business}Assumption`, description: 'Assumption being tested' },
      // Statistical and measurement properties
      { uri: `${business}sampleSize`, localName: 'sampleSize', label: 'Sample Size', type: 'data', domain: `${business}Experiment`, range: 'xsd:integer', description: 'Number of participants/observations' },
      { uri: `${business}statisticalSignificance`, localName: 'statisticalSignificance', label: 'Statistical Significance', type: 'data', domain: `${business}Experiment`, range: 'xsd:decimal', description: 'P-value or confidence level of results' },
      { uri: `${business}validatesFeature`, localName: 'validatesFeature', label: 'Validates Feature', type: 'object', domain: `${business}Experiment`, range: `${solution}Feature`, description: 'Feature being validated by this experiment' },
      { uri: `${business}costActual`, localName: 'costActual', label: 'Actual Cost', type: 'data', domain: `${business}Experiment`, range: 'xsd:decimal', description: 'Actual cost incurred for this experiment' },
      { uri: `${business}duration`, localName: 'duration', label: 'Duration', type: 'data', domain: `${business}Experiment`, range: 'xsd:integer', description: 'Actual duration in days' },
      { uri: `${business}learningVelocity`, localName: 'learningVelocity', label: 'Learning Velocity', type: 'data', domain: `${business}Experiment`, range: 'xsd:decimal', description: 'Learnings per dollar spent (efficiency metric)' },
      ...STATUS_PROPERTIES(`${business}Experiment`),
      ...QUANTITATIVE_PROPERTIES(`${business}Experiment`),
      ...DESCRIPTIVE_PROPERTIES(`${business}Experiment`),
      ...TEMPORAL_PROPERTIES(`${business}Experiment`),
    ],
    educational: {
      whatIsThis: 'An experiment is a structured test to validate assumptions with minimum investment. Different experiment types have different costs and confidence levels.',
      whenToUse: [
        'Before building features based on assumptions',
        'Testing market demand and willingness to pay',
        'Validating problem-solution fit',
      ],
      goodExamples: [
        'Customer interviews: Test if problem exists, 10 interviews, success = 7/10 confirm pain',
        'Landing page MVP: Test willingness to pay, 1000 visitors, success = 5% sign-up rate',
        'Concierge test: Manually deliver service, 5 customers, success = 4/5 renew',
      ],
      badExamples: [
        'Building full product without hypothesis',
        'No clear success criteria defined upfront',
      ],
      discoveryPhase: 'Problem Discovery',
      relatedConcepts: ['Assumption', 'ValueProposition', 'Metric'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW BUSINESS LAYER CLASSES - MEDIUM PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${business}Partner`,
    localName: 'Partner',
    label: 'Partner',
    domain: 'business',
    description: 'A strategic partner company or organization',
    properties: [
      { uri: `${business}partnerType`, localName: 'partnerType', label: 'Partner Type', type: 'data', domain: `${business}Partner`, range: 'xsd:string', description: 'Technology | Distribution | Reseller | Integration | Strategic | Supplier' },
      { uri: `${business}partnershipStatus`, localName: 'partnershipStatus', label: 'Partnership Status', type: 'data', domain: `${business}Partner`, range: 'xsd:string', description: 'Prospect | Negotiating | Active | Inactive | Terminated' },
      { uri: `${business}contractValue`, localName: 'contractValue', label: 'Contract Value', type: 'data', domain: `${business}Partner`, range: 'xsd:decimal', description: 'Annual contract value' },
      { uri: `${business}providesResource`, localName: 'providesResource', label: 'Provides Resource', type: 'object', domain: `${business}Partner`, range: `${business}KeyResource`, description: 'Key resources provided by partner' },
      { uri: `${business}partOfPartnership`, localName: 'partOfPartnership', label: 'Part Of Partnership', type: 'object', domain: `${business}Partner`, range: `${business}KeyPartnership`, description: 'Partnership this partner is part of' },
      ...STATUS_PROPERTIES(`${business}Partner`),
      ...DESCRIPTIVE_PROPERTIES(`${business}Partner`),
      ...TEMPORAL_PROPERTIES(`${business}Partner`),
    ],
    educational: {
      whatIsThis: 'Partners are external organizations that provide resources, capabilities, or market access. Strategic partnerships extend your capabilities beyond internal resources.',
      whenToUse: [
        'Managing key business relationships',
        'Tracking partner-provided resources',
        'Planning partnership strategy',
      ],
      goodExamples: [
        'AWS (Technology partner): Cloud infrastructure, $100K/year, Active',
        'Stripe (Integration partner): Payment processing, strategic integration',
        'Accenture (Reseller): Enterprise distribution channel, 20% revenue share',
      ],
      badExamples: [
        'Vendors with transactional relationships only',
        'Individual consultants (use Person instead)',
      ],
      discoveryPhase: 'Strategy',
      relatedConcepts: ['KeyPartnership', 'KeyResource', 'Channel'],
    },
  },
  {
    uri: `${business}PricingModel`,
    localName: 'PricingModel',
    label: 'Pricing Model',
    domain: 'business',
    description: 'How the company charges customers for products/services',
    properties: [
      { uri: `${business}pricingStrategy`, localName: 'pricingStrategy', label: 'Pricing Strategy', type: 'data', domain: `${business}PricingModel`, range: 'xsd:string', description: 'Subscription | Usage | Freemium | OneTime | Tiered | ValueBased | Custom' },
      { uri: `${business}basePrice`, localName: 'basePrice', label: 'Base Price', type: 'data', domain: `${business}PricingModel`, range: 'xsd:decimal', description: 'Base price amount' },
      { uri: `${business}currency`, localName: 'currency', label: 'Currency', type: 'data', domain: `${business}PricingModel`, range: 'xsd:string', description: 'Currency code (USD, EUR, etc.)' },
      { uri: `${business}billingFrequency`, localName: 'billingFrequency', label: 'Billing Frequency', type: 'data', domain: `${business}PricingModel`, range: 'xsd:string', description: 'Monthly | Annual | Quarterly | PerUse' },
      { uri: `${business}appliesTo`, localName: 'appliesTo', label: 'Applies To', type: 'object', domain: `${business}PricingModel`, range: `${solution}Product`, description: 'Product this pricing applies to' },
      ...STATUS_PROPERTIES(`${business}PricingModel`),
      ...DESCRIPTIVE_PROPERTIES(`${business}PricingModel`),
    ],
    educational: {
      whatIsThis: 'Pricing model defines how you charge customers. Different models align with different value propositions and customer segments.',
      whenToUse: [
        'Defining how customers pay for your product',
        'Testing different pricing strategies',
        'Optimizing monetization',
      ],
      goodExamples: [
        'SaaS Subscription: $99/month per user, billed monthly, 3 tiers (Starter/Pro/Enterprise)',
        'Usage-based: $0.01 per API call, pay-as-you-go, no minimum',
        'Freemium: Free up to 5 users, $29/month above, annual discount 20%',
      ],
      badExamples: [
        'No clear pricing structure or tiers',
        'Pricing not aligned with value delivered',
      ],
      discoveryPhase: 'Business Model',
      relatedConcepts: ['RevenueStream', 'ValueProposition', 'Product'],
    },
  },
  {
    uri: `${business}CustomerAcquisition`,
    localName: 'CustomerAcquisition',
    label: 'Customer Acquisition',
    domain: 'business',
    description: 'Strategy and metrics for acquiring new customers',
    properties: [
      { uri: `${business}acquisitionChannel`, localName: 'acquisitionChannel', label: 'Acquisition Channel', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:string', description: 'Primary channel for acquisition' },
      { uri: `${business}customerAcquisitionCost`, localName: 'customerAcquisitionCost', label: 'CAC', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:decimal', description: 'Customer acquisition cost' },
      { uri: `${business}conversionRate`, localName: 'conversionRate', label: 'Conversion Rate', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:float', description: 'Conversion rate (%)' },
      { uri: `${business}timeToConvert`, localName: 'timeToConvert', label: 'Time To Convert', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:integer', description: 'Average time to convert in days' },
      { uri: `${business}ltv`, localName: 'ltv', label: 'LTV', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:decimal', description: 'Customer lifetime value' },
      { uri: `${business}ltvCacRatio`, localName: 'ltvCacRatio', label: 'LTV:CAC Ratio', type: 'data', domain: `${business}CustomerAcquisition`, range: 'xsd:float', description: 'LTV to CAC ratio (target > 3.0)' },
      { uri: `${business}targetsSegment`, localName: 'targetsSegment', label: 'Targets Segment', type: 'object', domain: `${business}CustomerAcquisition`, range: `${market}CustomerSegment`, description: 'Customer segment being acquired' },
      ...STATUS_PROPERTIES(`${business}CustomerAcquisition`),
      ...DESCRIPTIVE_PROPERTIES(`${business}CustomerAcquisition`),
    ],
    educational: {
      whatIsThis: 'Customer acquisition strategy defines how you attract and convert customers. CAC (cost to acquire) and LTV (lifetime value) are critical SaaS metrics.',
      whenToUse: [
        'Optimizing customer acquisition efficiency',
        'Comparing acquisition channel effectiveness',
        'Ensuring unit economics work (LTV:CAC > 3)',
      ],
      goodExamples: [
        'Inbound SEO: $500 CAC, 5% conversion, $3000 LTV, 6.0 LTV:CAC ratio',
        'Outbound sales: $2000 CAC, 15% conversion, $12000 LTV, 6.0 LTV:CAC ratio',
        'Paid ads: $800 CAC, 3% conversion, $1800 LTV, 2.25 LTV:CAC ratio (needs improvement)',
      ],
      badExamples: [
        'Not tracking CAC by channel',
        'LTV:CAC ratio < 3.0 (unprofitable)',
      ],
      discoveryPhase: 'Go-to-Market',
      relatedConcepts: ['Channel', 'CustomerSegment', 'Metric', 'GoToMarketStrategy'],
    },
  },
  {
    uri: `${business}RetentionStrategy`,
    localName: 'RetentionStrategy',
    label: 'Retention Strategy',
    domain: 'business',
    description: 'Strategy for retaining and expanding existing customers',
    properties: [
      { uri: `${business}retentionTactic`, localName: 'retentionTactic', label: 'Retention Tactic', type: 'data', domain: `${business}RetentionStrategy`, range: 'xsd:string', description: 'OnboardingProgram | CustomerSuccess | LoyaltyProgram | CommunityBuilding | ProductExcellence' },
      { uri: `${business}churnRate`, localName: 'churnRate', label: 'Churn Rate', type: 'data', domain: `${business}RetentionStrategy`, range: 'xsd:float', description: 'Monthly churn rate (%)' },
      { uri: `${business}retentionRate`, localName: 'retentionRate', label: 'Retention Rate', type: 'data', domain: `${business}RetentionStrategy`, range: 'xsd:float', description: 'Monthly retention rate (%)' },
      { uri: `${business}nrr`, localName: 'nrr', label: 'Net Revenue Retention (NRR)', type: 'data', domain: `${business}RetentionStrategy`, range: 'xsd:float', description: 'Net revenue retention % (target > 100%)' },
      { uri: `${business}expansionRevenue`, localName: 'expansionRevenue', label: 'Expansion Revenue', type: 'data', domain: `${business}RetentionStrategy`, range: 'xsd:decimal', description: 'Revenue from upsells and expansions' },
      { uri: `${business}forSegment`, localName: 'forSegment', label: 'For Segment', type: 'object', domain: `${business}RetentionStrategy`, range: `${market}CustomerSegment`, description: 'Segment this retention strategy targets' },
      ...STATUS_PROPERTIES(`${business}RetentionStrategy`),
      ...DESCRIPTIVE_PROPERTIES(`${business}RetentionStrategy`),
      ...TEMPORAL_PROPERTIES(`${business}RetentionStrategy`),
    ],
    educational: {
      whatIsThis: 'Retention strategy focuses on keeping customers and expanding their spending. NRR > 100% means you grow revenue from existing customers faster than losing it to churn.',
      whenToUse: [
        'Reducing customer churn',
        'Increasing customer lifetime value',
        'Planning upsell and expansion strategy',
      ],
      goodExamples: [
        'White-glove onboarding: 5% churn, 95% retention, 120% NRR, $500K expansion revenue',
        'Community-led growth: 3% churn, strong engagement, 130% NRR',
        'Customer success program: Proactive outreach, 7% churn, 110% NRR',
      ],
      badExamples: [
        'High churn (> 10% monthly) without retention plan',
        'No expansion strategy (NRR < 100%)',
      ],
      discoveryPhase: 'Go-to-Market',
      relatedConcepts: ['CustomerSegment', 'Metric', 'CustomerRelationship'],
    },
  },
]

// ----------------------------------------------------------------------------
// SOLUTION DOMAIN CLASSES (Agile + SPM)
// ----------------------------------------------------------------------------

export const SOLUTION_CLASSES: OntologyClass[] = [
  {
    uri: `${solution}Product`,
    localName: 'Product',
    label: 'Product',
    domain: 'solution',
    description: 'A software or service product',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Product`),
      { uri: `${solution}hasProductType`, localName: 'hasProductType', label: 'Product Type', type: 'data', domain: `${solution}Product`, range: 'xsd:string', description: 'SoftwareProduct | ServiceProduct' },
      { uri: `${solution}hasVersion`, localName: 'hasVersion', label: 'Version', type: 'data', domain: `${solution}Product`, range: 'xsd:string' },
      { uri: `${solution}implements`, localName: 'implements', label: 'Implements', type: 'object', domain: `${solution}Product`, range: `${business}ValueProposition` },
      { uri: `${solution}hasFeature`, localName: 'hasFeature', label: 'Has Feature', type: 'object', domain: `${solution}Product`, range: `${solution}Feature` },
      // Product lifecycle and health metrics
      { uri: `${solution}lifecycleStage`, localName: 'lifecycleStage', label: 'Lifecycle Stage', type: 'data', domain: `${solution}Product`, range: 'xsd:string', description: 'Concept | Development | Launch | Growth | Maturity | Decline' },
      { uri: `${solution}launchDate`, localName: 'launchDate', label: 'Launch Date', type: 'data', domain: `${solution}Product`, range: 'xsd:dateTime', description: 'When the product was or will be launched' },
      { uri: `${solution}activeUserCount`, localName: 'activeUserCount', label: 'Active User Count', type: 'data', domain: `${solution}Product`, range: 'xsd:integer', description: 'Number of active users' },
      { uri: `${solution}healthScore`, localName: 'healthScore', label: 'Health Score', type: 'data', domain: `${solution}Product`, range: 'xsd:float', description: 'Overall product health (0-100)' },
      { uri: `${solution}pmfScore`, localName: 'pmfScore', label: 'PMF Score', type: 'data', domain: `${solution}Product`, range: 'xsd:float', description: 'Product-market fit score' },
    ],
    educational: {
      whatIsThis: 'A product is the tangible offering that implements your value proposition. It can be software, a service, or a combination.',
      whenToUse: [
        'Defining what you are building',
        'Planning product roadmap',
        'Managing product portfolio',
      ],
      goodExamples: [
        'FinanceAI Platform - SaaS product for automated reconciliation',
        'Consulting Service - Implementation and training',
        'API Product - Developer platform for integrations',
      ],
      badExamples: [
        '"Solution" (too vague)',
        'Confusing product with company',
      ],
      discoveryPhase: 'Solution Discovery',
      discoveryRequired: true,
      relatedConcepts: ['ValueProposition', 'Feature', 'Theme'],
    },
  },
  {
    uri: `${solution}Theme`,
    localName: 'Theme',
    label: 'Theme',
    domain: 'solution',
    description: 'A high-level strategic objective for the product',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Theme`),
      { uri: `${solution}supports`, localName: 'supports', label: 'Supports', type: 'object', domain: `${solution}Theme`, range: `${business}StrategicGoal` },
      { uri: `${solution}contains`, localName: 'contains', label: 'Contains', type: 'object', domain: `${solution}Theme`, range: `${solution}Epic` },
      // Theme planning and tracking
      { uri: `${solution}timeframe`, localName: 'timeframe', label: 'Timeframe', type: 'data', domain: `${solution}Theme`, range: 'xsd:string', description: 'Q1 | Q2 | Q3 | Q4 | H1 | H2' },
      { uri: `${solution}themeHealthScore`, localName: 'themeHealthScore', label: 'Health Score', type: 'data', domain: `${solution}Theme`, range: 'xsd:float', description: 'Overall theme health (0-100)' },
      { uri: `${solution}completionPercentage`, localName: 'completionPercentage', label: 'Completion Percentage', type: 'data', domain: `${solution}Theme`, range: 'xsd:float', description: 'Progress toward completion (0-100)' },
      { uri: `${solution}strategicPriority`, localName: 'strategicPriority', label: 'Strategic Priority', type: 'data', domain: `${solution}Theme`, range: 'xsd:string', description: 'P0 | P1 | P2 | P3' },
      { uri: `${solution}ownerPersona`, localName: 'ownerPersona', label: 'Owner Persona', type: 'object', domain: `${solution}Theme`, range: `${market}Persona`, description: 'The persona responsible for this theme' },
    ],
    educational: {
      whatIsThis: 'A theme is a high-level strategic initiative that groups related epics. Themes connect product work directly to business strategy.',
      whenToUse: [
        'Organizing product roadmap',
        'Aligning development with strategy',
        'Communicating priorities to stakeholders',
      ],
      goodExamples: [
        'AI-Powered Automation - Enable intelligent document processing',
        'Enterprise Readiness - Security, compliance, and scale',
        'Self-Service Expansion - Reduce onboarding friction',
      ],
      badExamples: [
        '"New features" (not strategic)',
        '"Q3 work" (time-based, not strategic)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['StrategicGoal', 'Epic', 'Product'],
    },
  },
  {
    uri: `${solution}Epic`,
    localName: 'Epic',
    label: 'Epic',
    domain: 'solution',
    description: 'A large body of work delivering substantial value',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Epic`),
      { uri: `${solution}belongsTo`, localName: 'belongsTo', label: 'Belongs To', type: 'object', domain: `${solution}Epic`, range: `${solution}Theme` },
      { uri: `${solution}realizes`, localName: 'realizes', label: 'Realizes', type: 'object', domain: `${solution}Epic`, range: `${business}KeyActivity` },
      { uri: `${solution}hasRoadmapStatus`, localName: 'hasRoadmapStatus', label: 'Roadmap Status', type: 'data', domain: `${solution}Epic`, range: 'xsd:string', description: 'Backlog | Planned | InProgress | Done' },
      { uri: `${solution}hasCostEstimate`, localName: 'hasCostEstimate', label: 'Cost Estimate', type: 'data', domain: `${solution}Epic`, range: 'xsd:decimal' },
      { uri: `${solution}delivers`, localName: 'delivers', label: 'Delivers', type: 'object', domain: `${solution}Epic`, range: `${solution}Feature` },
      // Epic tracking and alignment
      { uri: `${solution}epicOwner`, localName: 'epicOwner', label: 'Epic Owner', type: 'data', domain: `${solution}Epic`, range: 'xsd:string', description: 'Person responsible for this epic' },
      { uri: `${solution}epicCompletionPercentage`, localName: 'epicCompletionPercentage', label: 'Completion Percentage', type: 'data', domain: `${solution}Epic`, range: 'xsd:float', description: 'Progress toward completion (0-100)' },
      { uri: `${solution}velocity`, localName: 'velocity', label: 'Velocity', type: 'data', domain: `${solution}Epic`, range: 'xsd:float', description: 'Story points completed per sprint' },
      { uri: `${solution}blockedBy`, localName: 'blockedBy', label: 'Blocked By', type: 'object', domain: `${solution}Epic`, range: `${business}Risk`, description: 'Risk blocking this epic' },
      { uri: `${solution}strategicAlignmentScore`, localName: 'strategicAlignmentScore', label: 'Strategic Alignment Score', type: 'data', domain: `${solution}Epic`, range: 'xsd:float', description: 'How well this epic aligns with strategy (0-100)' },
    ],
    educational: {
      whatIsThis: 'An epic is a large body of work that can be broken down into features and user stories. Epics typically take multiple sprints to complete.',
      whenToUse: [
        'Planning major initiatives',
        'Breaking down themes into deliverables',
        'Managing development capacity',
      ],
      goodExamples: [
        'Invoice Processing Automation - Auto-extract and reconcile invoices',
        'SSO Integration - Support enterprise identity providers',
        'Mobile App MVP - Core features on iOS and Android',
      ],
      badExamples: [
        '"Bug fixes" (not an epic)',
        '"Improve performance" (not specific enough)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Theme', 'Feature', 'KeyActivity'],
    },
  },
  {
    uri: `${solution}Feature`,
    localName: 'Feature',
    label: 'Feature',
    domain: 'solution',
    description: 'A specific capability of the product',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Feature`),
      { uri: `${solution}partOf`, localName: 'partOf', label: 'Part Of', type: 'object', domain: `${solution}Feature`, range: `${solution}Epic` },
      { uri: `${solution}satisfies`, localName: 'satisfies', label: 'Satisfies', type: 'object', domain: `${solution}Feature`, range: `${solution}Requirement` },
      { uri: `${solution}mitigates`, localName: 'mitigates', label: 'Mitigates', type: 'object', domain: `${solution}Feature`, range: `${market}CustomerPain` },
      { uri: `${solution}hasCost`, localName: 'hasCost', label: 'Cost', type: 'data', domain: `${solution}Feature`, range: 'xsd:decimal' },
      { uri: `${solution}hasUserStory`, localName: 'hasUserStory', label: 'Has User Story', type: 'object', domain: `${solution}Feature`, range: `${solution}UserStory` },
      // Discovery and validation properties
      { uri: `${solution}addressesPain`, localName: 'addressesPain', label: 'Addresses Pain', type: 'object', domain: `${solution}Feature`, range: `${market}CustomerPain`, description: 'Which customer pains does this feature address?' },
      { uri: `${solution}addressesOpportunity`, localName: 'addressesOpportunity', label: 'Addresses Opportunity', type: 'object', domain: `${solution}Feature`, range: `${business}Opportunity`, description: 'Which opportunity does this feature address?' },
      { uri: `${solution}painCoverage`, localName: 'painCoverage', label: 'Pain Coverage', type: 'data', domain: `${solution}Feature`, range: 'xsd:float', description: 'Percentage of target pains addressed (0-100)' },
      { uri: `${solution}riceScore`, localName: 'riceScore', label: 'RICE Score', type: 'data', domain: `${solution}Feature`, range: 'xsd:float', description: 'RICE prioritization score (Reach x Impact x Confidence / Effort)' },
      { uri: `${solution}validationStatus`, localName: 'validationStatus', label: 'Validation Status', type: 'data', domain: `${solution}Feature`, range: 'xsd:string', description: 'Idea | Validated | Building | Released' },
      { uri: `${solution}validatedBy`, localName: 'validatedBy', label: 'Validated By', type: 'object', domain: `${solution}Feature`, range: `${business}Experiment`, description: 'Experiment that validated this feature' },
    ],
    educational: {
      whatIsThis: 'A feature is a specific capability that provides value to users. Features directly address customer pains and can be broken into user stories.',
      whenToUse: [
        'Defining product capabilities',
        'Prioritizing what to build',
        'Tracing value back to customer needs',
      ],
      goodExamples: [
        'Auto-categorization - ML-based transaction categorization',
        'Approval Workflow - Multi-level approval routing',
        'Dashboard Builder - Custom report creation',
      ],
      badExamples: [
        '"Better UX" (not a specific feature)',
        '"Use AI" (technology, not feature)',
      ],
      discoveryPhase: 'Solution Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Epic', 'UserStory', 'CustomerPain', 'Requirement'],
    },
  },
  {
    uri: `${solution}UserStory`,
    localName: 'UserStory',
    label: 'User Story',
    domain: 'solution',
    description: 'A feature from the end user perspective',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}UserStory`),
      { uri: `${solution}forPersona`, localName: 'forPersona', label: 'For Persona', type: 'object', domain: `${solution}UserStory`, range: `${market}Persona` },
      { uri: `${solution}hasAcceptanceCriteria`, localName: 'hasAcceptanceCriteria', label: 'Acceptance Criteria', type: 'data', domain: `${solution}UserStory`, range: 'xsd:string' },
      { uri: `${solution}hasStoryPoints`, localName: 'hasStoryPoints', label: 'Story Points', type: 'data', domain: `${solution}UserStory`, range: 'xsd:integer' },
    ],
    educational: {
      whatIsThis: 'A user story describes a feature from the end user\'s perspective. It follows the format: "As a [persona], I want [goal], so that [benefit]."',
      whenToUse: [
        'Breaking features into implementable units',
        'Sprint planning',
        'Communicating requirements to developers',
      ],
      goodExamples: [
        'As CFO Clara, I want to see cash flow forecast, so that I can plan ahead',
        'As a finance manager, I want to approve expenses on mobile, so that I can respond faster',
      ],
      badExamples: [
        '"Add button" (task, not story)',
        '"As a user..." (not specific persona)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Feature', 'Persona', 'Requirement'],
    },
  },
  {
    uri: `${solution}Requirement`,
    localName: 'Requirement',
    label: 'Requirement',
    domain: 'solution',
    description: 'A functional or non-functional requirement',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Requirement`),
      { uri: `${solution}hasRequirementType`, localName: 'hasRequirementType', label: 'Requirement Type', type: 'data', domain: `${solution}Requirement`, range: 'xsd:string', description: 'Functional | NonFunctional' },
      { uri: `${solution}implementedBy`, localName: 'implementedBy', label: 'Implemented By', type: 'object', domain: `${solution}Requirement`, range: `${solution}Feature` },
      { uri: `${solution}constrainedBy`, localName: 'constrainedBy', label: 'Constrained By', type: 'object', domain: `${solution}Requirement`, range: `${solution}Constraint` },
    ],
    educational: {
      whatIsThis: 'Requirements specify what the system must do (functional) or qualities it must have (non-functional like performance, security).',
      whenToUse: [
        'Documenting system specifications',
        'Ensuring completeness',
        'Compliance and audit trails',
      ],
      goodExamples: [
        'Functional: System shall process 1000 invoices per hour',
        'Non-functional: Response time < 200ms for 95th percentile',
        'Non-functional: Data encrypted at rest and in transit',
      ],
      badExamples: [
        '"Fast" (not measurable)',
        '"Secure" (not specific)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Feature', 'Constraint', 'UserStory'],
    },
  },
  {
    uri: `${solution}Constraint`,
    localName: 'Constraint',
    label: 'Constraint',
    domain: 'solution',
    description: 'A regulatory, technical, or business constraint',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Constraint`),
      { uri: `${solution}hasConstraintType`, localName: 'hasConstraintType', label: 'Constraint Type', type: 'data', domain: `${solution}Constraint`, range: 'xsd:string', description: 'Regulatory | Technical | Business' },
      { uri: `${solution}restricts`, localName: 'restricts', label: 'Restricts', type: 'object', domain: `${solution}Constraint`, range: `${solution}Feature` },
    ],
    educational: {
      whatIsThis: 'Constraints are limitations that affect how you can design and build features. They come from regulations, technology, or business decisions.',
      whenToUse: [
        'Documenting external limitations',
        'Compliance requirements',
        'Architecture decisions',
      ],
      goodExamples: [
        'Regulatory: GDPR data residency requirements',
        'Technical: Must integrate with legacy SAP system',
        'Business: MVP must launch within 6 months',
      ],
      badExamples: [
        '"Budget" alone (need specific impact)',
        'Self-imposed preferences',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Requirement', 'Feature'],
    },
  },
  {
    uri: `${solution}Bug`,
    localName: 'Bug',
    label: 'Bug',
    domain: 'solution',
    description: 'A defect or issue in the product',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Bug`),
      { uri: `${solution}affects`, localName: 'affects', label: 'Affects', type: 'object', domain: `${solution}Bug`, range: `${solution}Feature` },
      { uri: `${solution}hasSeverity`, localName: 'hasSeverity', label: 'Severity', type: 'data', domain: `${solution}Bug`, range: 'xsd:string' },
    ],
    educational: {
      whatIsThis: 'A bug is a defect where the product does not work as intended. Bugs have severity levels that determine priority.',
      whenToUse: [
        'Tracking product defects',
        'Prioritizing fixes',
        'Quality management',
      ],
      goodExamples: [
        'Critical: Payment processing fails for all users',
        'High: Report export corrupts data',
        'Medium: UI alignment issue on mobile',
      ],
      badExamples: [
        '"Looks weird" (not specific)',
        'Feature requests (those are not bugs)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Feature', 'Requirement'],
    },
  },
  // Strategic Solution Planning
  {
    uri: `${solution}SolutionVision`,
    localName: 'SolutionVision',
    label: 'Solution Vision',
    domain: 'solution',
    description: 'A high-level description of what the solution will become',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}SolutionVision`),
      { uri: `${solution}hasVisionStatement`, localName: 'hasVisionStatement', label: 'Vision Statement', type: 'data', domain: `${solution}SolutionVision`, range: 'xsd:string' },
      { uri: `${solution}addressesPain`, localName: 'addressesPain', label: 'Addresses Pain', type: 'object', domain: `${solution}SolutionVision`, range: `${market}CustomerPain` },
      { uri: `${solution}targetedAt`, localName: 'targetedAt', label: 'Targeted At', type: 'object', domain: `${solution}SolutionVision`, range: `${market}CustomerSegment` },
      { uri: `${solution}guidesProduct`, localName: 'guidesProduct', label: 'Guides Product', type: 'object', domain: `${solution}SolutionVision`, range: `${solution}Product` },
    ],
    educational: {
      whatIsThis: 'A solution vision is a high-level, inspirational description of what your product will become. It guides strategic decisions before detailed features are defined.',
      whenToUse: [
        'Early in Discovery phase before detailed product work',
        'Aligning stakeholders on product direction',
        'Communicating the "north star" to the team',
      ],
      goodExamples: [
        'An AI-powered finance platform that gives mid-market CFOs the insights of a Fortune 500 finance team',
        'The Figma of financial modeling - real-time collaboration for distributed finance teams',
        'Zero-touch reconciliation: from invoice to closed books in minutes, not days',
      ],
      badExamples: [
        '"A good accounting software" (not inspirational)',
        '"Feature X, Y, Z" (too detailed for vision)',
        '"Best in market" (not specific)',
      ],
      discoveryPhase: 'Customer Discovery',
      discoveryRequired: true,
      relatedConcepts: ['Product', 'ValueProposition', 'CustomerPain', 'SolutionStrategy'],
    },
  },
  {
    uri: `${solution}SolutionStrategy`,
    localName: 'SolutionStrategy',
    label: 'Solution Strategy',
    domain: 'solution',
    description: 'The approach and principles for how the solution will be built',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}SolutionStrategy`),
      { uri: `${solution}hasApproach`, localName: 'hasApproach', label: 'Approach', type: 'data', domain: `${solution}SolutionStrategy`, range: 'xsd:string', description: 'BuildFirst | BuyFirst | Hybrid | Platform | Service' },
      { uri: `${solution}hasTechStack`, localName: 'hasTechStack', label: 'Tech Stack', type: 'data', domain: `${solution}SolutionStrategy`, range: 'xsd:string' },
      { uri: `${solution}hasArchitectureStyle`, localName: 'hasArchitectureStyle', label: 'Architecture Style', type: 'data', domain: `${solution}SolutionStrategy`, range: 'xsd:string', description: 'Monolith | Microservices | Serverless | Hybrid' },
      { uri: `${solution}implementsVision`, localName: 'implementsVision', label: 'Implements Vision', type: 'object', domain: `${solution}SolutionStrategy`, range: `${solution}SolutionVision` },
    ],
    educational: {
      whatIsThis: 'Solution strategy defines HOW you will build the product - technical approach, build vs buy decisions, and architectural principles.',
      whenToUse: [
        'Shape phase technical planning',
        'Making build vs buy decisions',
        'Defining architectural boundaries',
      ],
      goodExamples: [
        'API-first platform approach: core ML engine with extensible integrations',
        'Buy for commodity (auth, payments), build for differentiation (ML models)',
        'Start monolith, extract services as scale requires',
      ],
      badExamples: [
        '"Use the latest tech" (not strategic)',
        '"Microservices because everyone does it" (not context-aware)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['SolutionVision', 'Product', 'Theme', 'Constraint'],
    },
  },
  {
    uri: `${solution}LaunchPlan`,
    localName: 'LaunchPlan',
    label: 'Launch Plan',
    domain: 'solution',
    description: 'The plan for bringing the product to market',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}LaunchPlan`),
      { uri: `${solution}hasLaunchPhase`, localName: 'hasLaunchPhase', label: 'Launch Phase', type: 'data', domain: `${solution}LaunchPlan`, range: 'xsd:string', description: 'Alpha | ClosedBeta | OpenBeta | SoftLaunch | GA' },
      { uri: `${solution}hasTargetDate`, localName: 'hasTargetDate', label: 'Target Date', type: 'data', domain: `${solution}LaunchPlan`, range: 'xsd:date' },
      { uri: `${solution}executesGTM`, localName: 'executesGTM', label: 'Executes GTM', type: 'object', domain: `${solution}LaunchPlan`, range: `${business}GoToMarketStrategy` },
      { uri: `${solution}includesMilestone`, localName: 'includesMilestone', label: 'Includes Milestone', type: 'object', domain: `${solution}LaunchPlan`, range: `${business}Milestone` },
      { uri: `${solution}hasSuccessCriteria`, localName: 'hasSuccessCriteria', label: 'Success Criteria', type: 'data', domain: `${solution}LaunchPlan`, range: 'xsd:string' },
    ],
    educational: {
      whatIsThis: 'A launch plan coordinates all activities needed to bring a product to market - from alpha testing to general availability.',
      whenToUse: [
        'Shape phase planning',
        'Coordinating cross-functional launch activities',
        'Setting go/no-go decision points',
      ],
      goodExamples: [
        'Alpha (internal): Core features complete, dogfood with team',
        'Closed Beta: 20 design partners, NPS > 30, <5 critical bugs',
        'GA: $10K MRR, 100 active users, 80% retention',
      ],
      badExamples: [
        '"Launch when ready" (no criteria)',
        '"Q3" (no specific milestones)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['GoToMarketStrategy', 'Milestone', 'Product', 'Epic'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW SOLUTION LAYER CLASSES - HIGH PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${solution}Release`,
    localName: 'Release',
    label: 'Release',
    domain: 'solution',
    description: 'A product release or version containing a set of features',
    properties: [
      { uri: `${solution}versionNumber`, localName: 'versionNumber', label: 'Version Number', type: 'data', domain: `${solution}Release`, range: 'xsd:string', description: 'Version number (e.g., v1.2.3)' },
      { uri: `${solution}releaseType`, localName: 'releaseType', label: 'Release Type', type: 'data', domain: `${solution}Release`, range: 'xsd:string', description: 'Major | Minor | Patch | Hotfix' },
      { uri: `${solution}releaseDate`, localName: 'releaseDate', label: 'Release Date', type: 'data', domain: `${solution}Release`, range: 'xsd:dateTime', description: 'When this release ships' },
      { uri: `${solution}releaseStatus`, localName: 'releaseStatus', label: 'Release Status', type: 'data', domain: `${solution}Release`, range: 'xsd:string', description: 'Planned | InDevelopment | Testing | Deployed | Archived' },
      { uri: `${solution}releaseNotes`, localName: 'releaseNotes', label: 'Release Notes', type: 'data', domain: `${solution}Release`, range: 'xsd:string', description: 'Customer-facing release notes' },
      { uri: `${solution}includesFeature`, localName: 'includesFeature', label: 'Includes Feature', type: 'object', domain: `${solution}Release`, range: `${solution}Feature`, description: 'Features included in this release' },
      { uri: `${solution}ofProduct`, localName: 'ofProduct', label: 'Of Product', type: 'object', domain: `${solution}Release`, range: `${solution}Product`, description: 'Product this release belongs to' },
      ...STATUS_PROPERTIES(`${solution}Release`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}Release`),
      ...TEMPORAL_PROPERTIES(`${solution}Release`),
    ],
    educational: {
      whatIsThis: 'A release is a versioned collection of features shipped to customers. Releases follow semantic versioning (major.minor.patch) and contain release notes describing changes.',
      whenToUse: [
        'Planning product roadmap and shipping schedule',
        'Communicating what\'s included in each version',
        'Managing feature delivery timelines',
      ],
      goodExamples: [
        'v2.0.0 - Major release: New AI engine, breaking changes, 15 features',
        'v1.5.0 - Minor release: Dashboard redesign, 5 features, backward compatible',
        'v1.4.1 - Patch release: Bug fixes, security updates, no new features',
      ],
      badExamples: [
        'No version numbers or release tracking',
        'Continuous deployment without release boundaries',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Product', 'Feature', 'Sprint', 'LaunchPlan'],
    },
  },
  {
    uri: `${solution}Sprint`,
    localName: 'Sprint',
    label: 'Sprint',
    domain: 'solution',
    description: 'An Agile sprint or iteration with defined goals and timeboxed duration',
    properties: [
      { uri: `${solution}sprintNumber`, localName: 'sprintNumber', label: 'Sprint Number', type: 'data', domain: `${solution}Sprint`, range: 'xsd:integer', description: 'Sprint sequence number' },
      { uri: `${solution}sprintGoal`, localName: 'sprintGoal', label: 'Sprint Goal', type: 'data', domain: `${solution}Sprint`, range: 'xsd:string', description: 'What the team commits to achieve this sprint' },
      { uri: `${solution}sprintDuration`, localName: 'sprintDuration', label: 'Sprint Duration', type: 'data', domain: `${solution}Sprint`, range: 'xsd:integer', description: 'Duration in days (typically 14)' },
      { uri: `${solution}velocity`, localName: 'velocity', label: 'Velocity', type: 'data', domain: `${solution}Sprint`, range: 'xsd:integer', description: 'Story points completed in this sprint' },
      { uri: `${solution}sprintStartDate`, localName: 'sprintStartDate', label: 'Sprint Start Date', type: 'data', domain: `${solution}Sprint`, range: 'xsd:dateTime', description: 'Sprint start date' },
      { uri: `${solution}sprintEndDate`, localName: 'sprintEndDate', label: 'Sprint End Date', type: 'data', domain: `${solution}Sprint`, range: 'xsd:dateTime', description: 'Sprint end date' },
      { uri: `${solution}containsStory`, localName: 'containsStory', label: 'Contains Story', type: 'object', domain: `${solution}Sprint`, range: `${solution}UserStory`, description: 'User stories in this sprint' },
      { uri: `${solution}containsTask`, localName: 'containsTask', label: 'Contains Task', type: 'object', domain: `${solution}Sprint`, range: `${solution}Task`, description: 'Tasks in this sprint' },
      ...STATUS_PROPERTIES(`${solution}Sprint`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}Sprint`),
    ],
    educational: {
      whatIsThis: 'A sprint is a timeboxed iteration (typically 2 weeks) where an Agile team commits to completing a set of user stories toward a defined goal. Velocity tracks team capacity.',
      whenToUse: [
        'Planning Agile development cycles',
        'Tracking team velocity and capacity',
        'Managing sprint commitments and burndown',
      ],
      goodExamples: [
        'Sprint 15: Goal = Complete user onboarding flow, 2 weeks, 32 story points planned',
        'Sprint 16: Goal = Dashboard performance optimization, velocity = 28 points',
      ],
      badExamples: [
        'Sprints without clear goals',
        'Variable sprint lengths (breaks velocity tracking)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['UserStory', 'Task', 'Epic', 'Feature'],
    },
  },
  {
    uri: `${solution}Task`,
    localName: 'Task',
    label: 'Task',
    domain: 'solution',
    description: 'A specific work item below user story level',
    properties: [
      { uri: `${solution}taskType`, localName: 'taskType', label: 'Task Type', type: 'data', domain: `${solution}Task`, range: 'xsd:string', description: 'Development | Testing | Design | Documentation | DevOps | Research' },
      { uri: `${solution}taskStatus`, localName: 'taskStatus', label: 'Task Status', type: 'data', domain: `${solution}Task`, range: 'xsd:string', description: 'Todo | InProgress | InReview | Done | Blocked' },
      { uri: `${solution}estimatedHours`, localName: 'estimatedHours', label: 'Estimated Hours', type: 'data', domain: `${solution}Task`, range: 'xsd:float', description: 'Estimated hours to complete' },
      { uri: `${solution}actualHours`, localName: 'actualHours', label: 'Actual Hours', type: 'data', domain: `${solution}Task`, range: 'xsd:float', description: 'Actual hours spent' },
      { uri: `${solution}implementsStory`, localName: 'implementsStory', label: 'Implements Story', type: 'object', domain: `${solution}Task`, range: `${solution}UserStory`, description: 'User story this task implements' },
      { uri: `${solution}inSprint`, localName: 'inSprint', label: 'In Sprint', type: 'object', domain: `${solution}Task`, range: `${solution}Sprint`, description: 'Sprint this task is in' },
      { uri: `${solution}blockedBy`, localName: 'blockedBy', label: 'Blocked By', type: 'object', domain: `${solution}Task`, range: `${solution}Dependency`, description: 'Dependency blocking this task' },
      ...STATUS_PROPERTIES(`${solution}Task`),
      ...OWNERSHIP_PROPERTIES(`${solution}Task`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}Task`),
    ],
    educational: {
      whatIsThis: 'A task is a granular work item that breaks down a user story into specific development, testing, or design activities. Tasks are tracked in hours, not story points.',
      whenToUse: [
        'Breaking down user stories into actionable work',
        'Tracking daily progress within a sprint',
        'Identifying blockers and dependencies',
      ],
      goodExamples: [
        'Development: Implement login API endpoint, 4 hours estimated',
        'Testing: Write unit tests for auth service, 2 hours',
        'Design: Create wireframes for dashboard, 6 hours',
      ],
      badExamples: [
        'Tasks larger than 8 hours (too big, needs breakdown)',
        'Tasks not linked to user stories',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['UserStory', 'Sprint', 'Dependency'],
    },
  },

  {
    uri: `${solution}ActionItem`,
    localName: 'ActionItem',
    label: 'Action Item',
    domain: 'solution',
    description: 'Specific action or task that needs to be completed, often from meetings or decisions',
    properties: [
      { uri: `${solution}assignee`, localName: 'assignee', label: 'Assignee', type: 'data', domain: `${solution}ActionItem`, range: 'xsd:string' },
      { uri: `${solution}fromDecision`, localName: 'fromDecision', label: 'From Decision', type: 'object', domain: `${solution}ActionItem`, range: `${business}Decision`, description: 'Decision that created this action' },
      { uri: `${solution}blocksTask`, localName: 'blocksTask', label: 'Blocks Task', type: 'object', domain: `${solution}ActionItem`, range: `${solution}Task` },
      { uri: `${solution}completed`, localName: 'completed', label: 'Completed', type: 'data', domain: `${solution}ActionItem`, range: 'xsd:boolean' },
      ...DESCRIPTIVE_PROPERTIES(`${solution}ActionItem`),
      ...STATUS_PROPERTIES(`${solution}ActionItem`),
      ...TEMPORAL_PROPERTIES(`${solution}ActionItem`),
      ...OWNERSHIP_PROPERTIES(`${solution}ActionItem`),
    ],
    educational: {
      whatIsThis: 'An action item is a specific, concrete action that someone needs to take. Action items typically come from meetings, decisions, or identified blockers.',
      whenToUse: [
        'Capturing next steps from meetings',
        'Tracking follow-up actions from decisions',
        'Managing unblocking tasks',
      ],
      goodExamples: [
        '"@john: Schedule kick-off meeting by Friday" (specific, assigned, dated)',
        '"@sarah: Get API key from vendor before sprint starts"',
        '"@team: Review security doc and provide feedback by EOD"',
      ],
      badExamples: [
        '"Follow up" (too vague)',
        '"Implement feature" (that\'s a Task/UserStory, not an action item)',
      ],
      discoveryPhase: 'Execution',
      discoveryRequired: false,
      relatedConcepts: ['Task', 'Decision', 'Milestone'],
    },
  },

  {
    uri: `${solution}Dependency`,
    localName: 'Dependency',
    label: 'Dependency',
    domain: 'solution',
    description: 'A dependency between features, stories, or tasks that creates blocking relationships',
    properties: [
      { uri: `${solution}dependencyType`, localName: 'dependencyType', label: 'Dependency Type', type: 'data', domain: `${solution}Dependency`, range: 'xsd:string', description: 'FinishToStart | StartToStart | FinishToFinish | Technical | Resource' },
      { uri: `${solution}dependencyStatus`, localName: 'dependencyStatus', label: 'Dependency Status', type: 'data', domain: `${solution}Dependency`, range: 'xsd:string', description: 'Blocked | Resolved | Mitigated | Accepted' },
      { uri: `${solution}impact`, localName: 'impact', label: 'Impact', type: 'data', domain: `${solution}Dependency`, range: 'xsd:string', description: 'Low | Medium | High | Critical' },
      { uri: `${solution}blocksEntity`, localName: 'blocksEntity', label: 'Blocks Entity', type: 'object', domain: `${solution}Dependency`, range: 'owl:Thing', description: 'Entity that is blocked (Feature, UserStory, or Task)' },
      { uri: `${solution}dependsOn`, localName: 'dependsOn', label: 'Depends On', type: 'object', domain: `${solution}Dependency`, range: 'owl:Thing', description: 'Entity this depends on completing first' },
      ...STATUS_PROPERTIES(`${solution}Dependency`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}Dependency`),
      ...TEMPORAL_PROPERTIES(`${solution}Dependency`),
    ],
    educational: {
      whatIsThis: 'A dependency represents a relationship where one item must complete before another can start or finish. Managing dependencies is critical for preventing blockers.',
      whenToUse: [
        'Identifying and tracking blockers in development',
        'Sequencing work that has technical dependencies',
        'Managing cross-team dependencies',
      ],
      goodExamples: [
        'Feature A (API) blocks Feature B (UI): FinishToStart, High impact',
        'Database migration blocks new features: Technical dependency, Critical',
        'Third-party integration blocks testing: Resource dependency, Medium impact',
      ],
      badExamples: [
        'Not tracking dependencies explicitly',
        'Circular dependencies (A depends on B depends on A)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Feature', 'UserStory', 'Task', 'Risk'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW SOLUTION LAYER CLASSES - MEDIUM PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  {
    uri: `${solution}AcceptanceCriteria`,
    localName: 'AcceptanceCriteria',
    label: 'Acceptance Criteria',
    domain: 'solution',
    description: 'Specific conditions that must be met for a story or feature to be considered complete',
    properties: [
      { uri: `${solution}criteriaText`, localName: 'criteriaText', label: 'Criteria Text', type: 'data', domain: `${solution}AcceptanceCriteria`, range: 'xsd:string', description: 'The acceptance criterion statement' },
      { uri: `${solution}criteriaType`, localName: 'criteriaType', label: 'Criteria Type', type: 'data', domain: `${solution}AcceptanceCriteria`, range: 'xsd:string', description: 'Functional | Performance | Security | Usability | Business' },
      { uri: `${solution}isMet`, localName: 'isMet', label: 'Is Met', type: 'data', domain: `${solution}AcceptanceCriteria`, range: 'xsd:boolean', description: 'Whether this criterion has been satisfied' },
      { uri: `${solution}forStory`, localName: 'forStory', label: 'For Story', type: 'object', domain: `${solution}AcceptanceCriteria`, range: `${solution}UserStory`, description: 'User story this criterion applies to' },
      { uri: `${solution}forFeature`, localName: 'forFeature', label: 'For Feature', type: 'object', domain: `${solution}AcceptanceCriteria`, range: `${solution}Feature`, description: 'Feature this criterion applies to' },
      ...STATUS_PROPERTIES(`${solution}AcceptanceCriteria`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}AcceptanceCriteria`),
    ],
    educational: {
      whatIsThis: 'Acceptance criteria define "done" for user stories and features. They are specific, testable conditions that must be satisfied before work is considered complete.',
      whenToUse: [
        'Defining what "done" means for user stories',
        'Creating clear expectations before development starts',
        'Enabling objective testing and validation',
      ],
      goodExamples: [
        'Given user is logged in, when they click logout, then session is cleared and redirected to login',
        'System must respond to API calls within 200ms for 95th percentile',
        'Password must be hashed using bcrypt with minimum 12 character length',
      ],
      badExamples: [
        '"Should work well" (not specific or testable)',
        '"Fast performance" (not measurable)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['UserStory', 'Feature', 'TestCase', 'Requirement'],
    },
  },
  {
    uri: `${solution}TestCase`,
    localName: 'TestCase',
    label: 'Test Case',
    domain: 'solution',
    description: 'A specific test case for QA validation and quality assurance',
    properties: [
      { uri: `${solution}testType`, localName: 'testType', label: 'Test Type', type: 'data', domain: `${solution}TestCase`, range: 'xsd:string', description: 'Unit | Integration | E2E | Performance | Security | Manual | Automated' },
      { uri: `${solution}testStatus`, localName: 'testStatus', label: 'Test Status', type: 'data', domain: `${solution}TestCase`, range: 'xsd:string', description: 'NotRun | Passed | Failed | Blocked | Skipped' },
      { uri: `${solution}testSteps`, localName: 'testSteps', label: 'Test Steps', type: 'data', domain: `${solution}TestCase`, range: 'xsd:string', description: 'Step-by-step test procedure' },
      { uri: `${solution}expectedResult`, localName: 'expectedResult', label: 'Expected Result', type: 'data', domain: `${solution}TestCase`, range: 'xsd:string', description: 'Expected outcome of test' },
      { uri: `${solution}actualResult`, localName: 'actualResult', label: 'Actual Result', type: 'data', domain: `${solution}TestCase`, range: 'xsd:string', description: 'Actual outcome when executed' },
      { uri: `${solution}validatesAcceptanceCriteria`, localName: 'validatesAcceptanceCriteria', label: 'Validates AC', type: 'object', domain: `${solution}TestCase`, range: `${solution}AcceptanceCriteria`, description: 'Acceptance criteria this test validates' },
      { uri: `${solution}coversFeature`, localName: 'coversFeature', label: 'Covers Feature', type: 'object', domain: `${solution}TestCase`, range: `${solution}Feature`, description: 'Feature this test covers' },
      ...STATUS_PROPERTIES(`${solution}TestCase`),
      ...OWNERSHIP_PROPERTIES(`${solution}TestCase`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}TestCase`),
    ],
    educational: {
      whatIsThis: 'Test cases define specific scenarios to validate functionality, performance, security, or usability. They ensure acceptance criteria are met and catch regressions.',
      whenToUse: [
        'Validating acceptance criteria are met',
        'Creating automated test suites',
        'Preventing regressions through continuous testing',
      ],
      goodExamples: [
        'Unit Test: Login validation rejects invalid email format, expects ValidationError',
        'E2E Test: User completes checkout flow, expects order confirmation email',
        'Performance Test: API handles 1000 concurrent requests, expects <500ms p95 latency',
      ],
      badExamples: [
        'Vague test steps without clear expected results',
        'No linkage between tests and acceptance criteria',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['AcceptanceCriteria', 'Feature', 'UserStory', 'Bug'],
    },
  },
  {
    uri: `${solution}TechnicalDebt`,
    localName: 'TechnicalDebt',
    label: 'Technical Debt',
    domain: 'solution',
    description: 'Technical shortcuts or issues that need addressing to maintain code quality',
    properties: [
      { uri: `${solution}debtType`, localName: 'debtType', label: 'Debt Type', type: 'data', domain: `${solution}TechnicalDebt`, range: 'xsd:string', description: 'Code | Architecture | Testing | Documentation | Infrastructure | Security' },
      { uri: `${solution}severity`, localName: 'severity', label: 'Severity', type: 'data', domain: `${solution}TechnicalDebt`, range: 'xsd:string', description: 'Low | Medium | High | Critical' },
      { uri: `${solution}interestRate`, localName: 'interestRate', label: 'Interest Rate', type: 'data', domain: `${solution}TechnicalDebt`, range: 'xsd:string', description: 'How fast cost increases: Low | Medium | High' },
      { uri: `${solution}estimatedPayoff`, localName: 'estimatedPayoff', label: 'Estimated Payoff', type: 'data', domain: `${solution}TechnicalDebt`, range: 'xsd:integer', description: 'Story points to resolve this debt' },
      { uri: `${solution}affectsComponent`, localName: 'affectsComponent', label: 'Affects Component', type: 'object', domain: `${solution}TechnicalDebt`, range: `${solution}Component`, description: 'Component affected by this debt' },
      ...STATUS_PROPERTIES(`${solution}TechnicalDebt`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}TechnicalDebt`),
      ...TEMPORAL_PROPERTIES(`${solution}TechnicalDebt`),
    ],
    educational: {
      whatIsThis: 'Technical debt represents shortcuts or compromises that save time now but create future maintenance costs. Interest rate shows how quickly the cost grows if not addressed.',
      whenToUse: [
        'Tracking known code quality issues',
        'Prioritizing refactoring and cleanup work',
        'Making informed build-vs-fix trade-offs',
      ],
      goodExamples: [
        'Legacy auth system: High severity, High interest (blocks security features), 40 points to migrate',
        'Missing test coverage: Medium severity, Low interest, 20 points to add tests',
        'Hardcoded config: Low severity, Medium interest, 5 points to externalize',
      ],
      badExamples: [
        'Not tracking technical debt explicitly',
        'All debt marked "critical" (no prioritization)',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Component', 'Bug', 'Feature', 'Epic'],
    },
  },
  {
    uri: `${solution}Component`,
    localName: 'Component',
    label: 'Component',
    domain: 'solution',
    description: 'A technical architecture component, module, or service in the solution',
    properties: [
      { uri: `${solution}componentType`, localName: 'componentType', label: 'Component Type', type: 'data', domain: `${solution}Component`, range: 'xsd:string', description: 'Frontend | Backend | Database | API | Service | Library | Infrastructure' },
      { uri: `${solution}technology`, localName: 'technology', label: 'Technology', type: 'data', domain: `${solution}Component`, range: 'xsd:string', description: 'Technology stack used (React, Node.js, PostgreSQL, etc.)' },
      { uri: `${solution}componentVersion`, localName: 'componentVersion', label: 'Version', type: 'data', domain: `${solution}Component`, range: 'xsd:string', description: 'Component version number' },
      { uri: `${solution}maintainer`, localName: 'maintainer', label: 'Maintainer', type: 'data', domain: `${solution}Component`, range: 'xsd:string', description: 'Team or person responsible for this component' },
      { uri: `${solution}implementsFeature`, localName: 'implementsFeature', label: 'Implements Feature', type: 'object', domain: `${solution}Component`, range: `${solution}Feature`, description: 'Features implemented by this component' },
      { uri: `${solution}dependsOnComponent`, localName: 'dependsOnComponent', label: 'Depends On Component', type: 'object', domain: `${solution}Component`, range: `${solution}Component`, description: 'Other components this depends on' },
      ...STATUS_PROPERTIES(`${solution}Component`),
      ...DESCRIPTIVE_PROPERTIES(`${solution}Component`),
    ],
    educational: {
      whatIsThis: 'Components are the building blocks of your technical architecture - services, modules, libraries, or infrastructure elements that together form the complete solution.',
      whenToUse: [
        'Documenting system architecture',
        'Managing component dependencies',
        'Planning technical refactoring or migrations',
      ],
      goodExamples: [
        'Auth Service: Backend microservice, Node.js + Express, v2.1, handles authentication',
        'Dashboard UI: Frontend component, React + TypeScript, v1.5, implements reporting features',
        'Payment Gateway: API integration, Stripe SDK, v3.0, processes transactions',
      ],
      badExamples: [
        '"The backend" (too vague, break into specific services)',
        'No dependency tracking between components',
      ],
      discoveryPhase: 'Solution Discovery',
      relatedConcepts: ['Feature', 'TechnicalDebt', 'Product', 'Dependency'],
    },
  },

  {
    uri: `${solution}Pattern`,
    localName: 'Pattern',
    label: 'Design Pattern',
    domain: 'solution',
    description: 'Reusable solution pattern, architectural approach, or design principle',
    properties: [
      { uri: `${solution}patternType`, localName: 'patternType', label: 'Pattern Type', type: 'data', domain: `${solution}Pattern`, range: 'xsd:string', description: 'Architectural/Design/Code/UX' },
      { uri: `${solution}problem`, localName: 'problem', label: 'Problem', type: 'data', domain: `${solution}Pattern`, range: 'xsd:string', description: 'What problem does this pattern solve?' },
      { uri: `${solution}solution`, localName: 'solution', label: 'Solution', type: 'data', domain: `${solution}Pattern`, range: 'xsd:string', description: 'How does the pattern solve it?' },
      { uri: `${solution}applicability`, localName: 'applicability', label: 'Applicability', type: 'data', domain: `${solution}Pattern`, range: 'xsd:string', description: 'When to use this pattern' },
      { uri: `${solution}usedInComponent`, localName: 'usedInComponent', label: 'Used In Component', type: 'object', domain: `${solution}Pattern`, range: `${solution}Component` },
      ...DESCRIPTIVE_PROPERTIES(`${solution}Pattern`),
    ],
    educational: {
      whatIsThis: 'A design pattern is a reusable solution to a commonly occurring problem in software design. Patterns help maintain consistency and best practices across the solution.',
      whenToUse: [
        'Documenting architectural decisions',
        'Establishing coding standards',
        'Sharing best practices across teams',
      ],
      goodExamples: [
        '"Use Repository Pattern for data access layer"',
        '"Implement Observer Pattern for event notifications"',
        '"Apply BEM naming convention for CSS"',
      ],
      badExamples: [
        '"Use design patterns" (too vague)',
        '"Write good code" (not a pattern)',
      ],
      discoveryPhase: 'Solution Design',
      discoveryRequired: false,
      relatedConcepts: ['Component', 'TechnicalDebt', 'Constraint'],
    },
  },
  // --------------------------------------------------------------------------
  // Solution Ontology Gap Fill (from Notion spec)
  // --------------------------------------------------------------------------
  {
    uri: `${solution}Capability`,
    localName: 'Capability',
    label: 'Capability',
    domain: 'solution',
    description: 'What the solution needs to be able to do. Functional groupings that decompose the Vision into buildable areas.',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Capability`),
      ...STATUS_PROPERTIES(`${solution}Capability`),
      { uri: `${solution}partOfVision`, localName: 'partOfVision', label: 'Part of Vision', type: 'object', domain: `${solution}Capability`, range: `${solution}SolutionVision` },
      { uri: `${solution}decomposesInto`, localName: 'decomposesInto', label: 'Decomposes Into', type: 'object', domain: `${solution}Capability`, range: `${solution}Feature` },
      { uri: `${solution}enablesProgressOn`, localName: 'enablesProgressOn', label: 'Enables Progress On', type: 'object', domain: `${solution}Capability`, range: `${market}CustomerJob` },
      { uri: `${solution}addressesNeed`, localName: 'addressesNeed', label: 'Addresses Need', type: 'object', domain: `${solution}Capability`, range: `${market}CustomerPain` },
    ],
    educational: {
      whatIsThis: 'A capability describes what the solution must be able to do at the epic level. Each capability maps to validated customer needs and decomposes into buildable features.',
      whenToUse: ['Structuring the product vision into deliverable areas', 'Grouping features by functional domain', 'Mapping customer needs to solution areas'],
      goodExamples: ['"Knowledge Capture — ingest and structure product intelligence"', '"Strategic Analysis — generate insights from knowledge graph"'],
      badExamples: ['"Do stuff" (no clear functional scope)', '"Feature X" (too specific for a capability)'],
      discoveryPhase: 'Solution Design',
      discoveryRequired: true,
      relatedConcepts: ['SolutionVision', 'Feature', 'CustomerJob'],
    },
  },
  {
    uri: `${solution}Service`,
    localName: 'Service',
    label: 'Service',
    domain: 'solution',
    description: 'A service offering that materializes a value proposition alongside or instead of a software product.',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}Service`),
      { uri: `${solution}serviceType`, localName: 'serviceType', label: 'Service Type', type: 'data', domain: `${solution}Service`, range: 'xsd:string', description: 'core_offering | supporting_service | touchpoint | experience' },
      { uri: `${solution}materializes`, localName: 'materializes', label: 'Materializes', type: 'object', domain: `${solution}Service`, range: `${business}ValueProposition` },
    ],
    educational: {
      whatIsThis: 'A service is a tangible offering that carries your value proposition, alongside or instead of software.',
      whenToUse: ['Defining consulting or implementation services', 'Mapping service components in the Value Proposition Canvas'],
      goodExamples: ['"Implementation consulting — onboarding workshops"', '"API integration service"'],
      badExamples: ['"Help people" (not specific)'],
      discoveryPhase: 'Solution Design',
      discoveryRequired: false,
      relatedConcepts: ['Product', 'ValueProposition', 'Feature'],
    },
  },
  {
    uri: `${solution}PainReliever`,
    localName: 'PainReliever',
    label: 'Pain Reliever',
    domain: 'solution',
    description: 'A mechanism that removes or reduces a specific validated customer pain.',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}PainReliever`),
      { uri: `${solution}mechanism`, localName: 'mechanism', label: 'Mechanism', type: 'data', domain: `${solution}PainReliever`, range: 'xsd:string', description: 'How: automation, simplification, elimination' },
      { uri: `${solution}intensity`, localName: 'intensity', label: 'Intensity', type: 'data', domain: `${solution}PainReliever`, range: 'xsd:string', description: 'eliminates | significantly_reduces | partially_reduces' },
      { uri: `${solution}relieves`, localName: 'relieves', label: 'Relieves', type: 'object', domain: `${solution}PainReliever`, range: `${market}CustomerPain` },
      { uri: `${solution}implementedBy`, localName: 'implementedBy', label: 'Implemented By', type: 'object', domain: `${solution}PainReliever`, range: `${solution}Capability` },
    ],
    educational: {
      whatIsThis: 'A pain reliever describes HOW your solution removes or reduces a specific customer pain. It bridges the problem space to the solution space.',
      whenToUse: ['Filling the Pain Relievers section of the Value Proposition Canvas', 'Mapping features to customer pains'],
      goodExamples: ['"Automated reconciliation eliminates manual spreadsheet matching (4h/week saved)"'],
      badExamples: ['"Fixes problems" (not specific about which pain or how)'],
      discoveryPhase: 'Solution Discovery',
      discoveryRequired: true,
      relatedConcepts: ['CustomerPain', 'Feature', 'ValueProposition'],
    },
  },
  {
    uri: `${solution}GainCreator`,
    localName: 'GainCreator',
    label: 'Gain Creator',
    domain: 'solution',
    description: 'A mechanism that enables or amplifies a validated customer gain.',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}GainCreator`),
      { uri: `${solution}mechanism`, localName: 'mechanism', label: 'Mechanism', type: 'data', domain: `${solution}GainCreator`, range: 'xsd:string', description: 'How: enablement, amplification, creation' },
      { uri: `${solution}intensity`, localName: 'intensity', label: 'Intensity', type: 'data', domain: `${solution}GainCreator`, range: 'xsd:string', description: 'enables_new | significantly_amplifies | partially_amplifies' },
      { uri: `${solution}creates`, localName: 'creates', label: 'Creates', type: 'object', domain: `${solution}GainCreator`, range: `${market}CustomerGain` },
      { uri: `${solution}implementedBy`, localName: 'implementedBy', label: 'Implemented By', type: 'object', domain: `${solution}GainCreator`, range: `${solution}Capability` },
    ],
    educational: {
      whatIsThis: 'A gain creator describes HOW your solution enables or amplifies a desired customer outcome. The positive counterpart of a pain reliever.',
      whenToUse: ['Filling the Gain Creators section of the Value Proposition Canvas', 'Mapping features to desired customer outcomes'],
      goodExamples: ['"Real-time dashboard enables instant portfolio visibility (vs. waiting for monthly reports)"'],
      badExamples: ['"Makes things better" (not specific about which gain)'],
      discoveryPhase: 'Solution Discovery',
      discoveryRequired: true,
      relatedConcepts: ['CustomerGain', 'Feature', 'ValueProposition'],
    },
  },
  {
    uri: `${solution}NonFunctionalRequirement`,
    localName: 'NonFunctionalRequirement',
    label: 'Non-Functional Requirement',
    domain: 'solution',
    description: 'A constraint or quality attribute the system must satisfy. Cuts across features and capabilities.',
    properties: [
      ...DESCRIPTIVE_PROPERTIES(`${solution}NonFunctionalRequirement`),
      ...STATUS_PROPERTIES(`${solution}NonFunctionalRequirement`),
      { uri: `${solution}nfrCategory`, localName: 'nfrCategory', label: 'NFR Category', type: 'data', domain: `${solution}NonFunctionalRequirement`, range: 'xsd:string', description: 'performance | scalability | security | reliability | usability | accessibility | maintainability | compliance' },
      { uri: `${solution}metric`, localName: 'metric', label: 'Metric', type: 'data', domain: `${solution}NonFunctionalRequirement`, range: 'xsd:string', description: 'Quantifiable measure (e.g., p95 response time)' },
      { uri: `${solution}threshold`, localName: 'threshold', label: 'Threshold', type: 'data', domain: `${solution}NonFunctionalRequirement`, range: 'xsd:string', description: 'Pass/fail boundary (e.g., < 200ms)' },
      { uri: `${solution}scope`, localName: 'scope', label: 'Scope', type: 'data', domain: `${solution}NonFunctionalRequirement`, range: 'xsd:string', description: 'system_wide | capability_scoped | feature_scoped' },
      { uri: `${solution}constrains`, localName: 'constrains', label: 'Constrains', type: 'object', domain: `${solution}NonFunctionalRequirement`, range: `${solution}Feature` },
    ],
    educational: {
      whatIsThis: 'A non-functional requirement defines quality attributes and constraints: performance, security, scalability, usability. They have measurable thresholds.',
      whenToUse: ['Defining system-wide quality targets', 'Specifying performance SLAs', 'Documenting security and compliance requirements'],
      goodExamples: ['"Performance: p95 API response < 200ms at 1000 concurrent users"', '"Security: All PII encrypted at rest AES-256"'],
      badExamples: ['"Should be fast" (no measurable threshold)', '"Needs to be secure" (no specific requirement)'],
      discoveryPhase: 'Solution Design',
      discoveryRequired: false,
      relatedConcepts: ['Feature', 'Capability', 'Requirement'],
    },
  },
]

// ----------------------------------------------------------------------------
// COMBINED SCHEMA
// ----------------------------------------------------------------------------

export const ALL_CLASSES: OntologyClass[] = [
  ...CORE_CLASSES,
  ...MARKET_CLASSES,
  ...BUSINESS_CLASSES,
  ...SOLUTION_CLASSES,
]

export function getClassesByDomain(domain: OntologyDomain): OntologyClass[] {
  return ALL_CLASSES.filter(c => c.domain === domain)
}

export function getClassByUri(uri: string): OntologyClass | undefined {
  return ALL_CLASSES.find(c => c.uri === uri)
}

export function getClassByLocalName(localName: string): OntologyClass | undefined {
  return ALL_CLASSES.find(c => c.localName === localName)
}

export function getAllProperties(): PropertyDefinition[] {
  return ALL_CLASSES.flatMap(c => c.properties)
}

export function getPropertiesByDomain(domainUri: string): PropertyDefinition[] {
  const cls = getClassByUri(domainUri)
  return cls?.properties ?? []
}

// ----------------------------------------------------------------------------
// ENTITY GROUPS - UI Organization for Ontology Browser
// ----------------------------------------------------------------------------

export interface EntityGroup {
  id: string
  label: string
  entities: string[] // localName references
}

export const ENTITY_GROUPS: Record<OntologyDomain, EntityGroup[]> = {
  core: [
    { id: 'infrastructure', label: 'Infrastructure', entities: ['Facet', 'Source', 'ValidationAnnotation'] },
  ],
  market: [
    { id: 'market-structure', label: 'Market Structure', entities: ['Industry', 'Market', 'MarketOpportunity', 'Regulation'] },
    { id: 'competition', label: 'Competition', entities: ['Competitor', 'CompetitiveForce', 'Alternative', 'AlternativeAdvantage', 'AlternativeDisadvantage'] },
    { id: 'market-signals', label: 'Market Signals', entities: ['Trend', 'MarketSignal'] },
    { id: 'customer-profiles', label: 'Customer Profiles', entities: ['CustomerSegment', 'Persona', 'Person', 'Stakeholder', 'Demographic', 'Psychographic'] },
    { id: 'customer-experience', label: 'Customer Experience', entities: ['CustomerJourney', 'JourneyStage', 'Touchpoint'] },
    { id: 'customer-needs', label: 'Customer Needs', entities: ['Problem', 'CustomerPain', 'CustomerGain', 'CustomerJob', 'CustomerNeed', 'Workaround'] },
  ],
  business: [
    { id: 'business-model-canvas', label: 'Business Model Canvas', entities: ['Company', 'BusinessModel', 'ValueProposition', 'Channel', 'CustomerRelationship', 'RevenueStream', 'CostStructure', 'KeyResource', 'KeyActivity', 'KeyPartnership', 'Partner', 'PricingModel', 'RetentionStrategy'] },
    { id: 'strategy-goals', label: 'Strategy & Goals', entities: ['Strategy', 'StrategicGoal', 'Decision', 'BusinessContext'] },
    { id: 'swot-analysis', label: 'SWOT Analysis', entities: ['Strength', 'Weakness', 'Opportunity', 'Threat'] },
    { id: 'go-to-market', label: 'Go-to-Market', entities: ['GoToMarketStrategy', 'Positioning', 'CustomerAcquisition'] },
    { id: 'tracking-validation', label: 'Tracking & Validation', entities: ['Milestone', 'Risk', 'Metric', 'SuccessMetric', 'Assumption', 'Experiment'] },
  ],
  solution: [
    { id: 'product-vision', label: 'Product Vision', entities: ['Product', 'Service', 'SolutionVision', 'SolutionStrategy', 'Capability', 'Feature', 'PainReliever', 'GainCreator'] },
    { id: 'product-requirements', label: 'Product Requirements', entities: ['Epic', 'UserStory', 'Requirement', 'NonFunctionalRequirement', 'Constraint', 'AcceptanceCriteria'] },
    { id: 'roadmap-planning', label: 'Roadmap & Planning', entities: ['Theme', 'LaunchPlan', 'Release'] },
    { id: 'quality-testing', label: 'Quality & Testing', entities: ['Bug', 'TestCase', 'TechnicalDebt'] },
    { id: 'architecture', label: 'Architecture', entities: ['Component', 'Pattern'] },
    { id: 'work-management', label: 'Work Management', entities: ['Sprint', 'Task', 'ActionItem', 'Dependency'] },
  ],
}

export function getGroupsForDomain(domain: OntologyDomain): EntityGroup[] {
  return ENTITY_GROUPS[domain] || []
}

export function getClassesByGroup(domain: OntologyDomain, groupId: string): OntologyClass[] {
  const group = ENTITY_GROUPS[domain]?.find(g => g.id === groupId)
  if (!group) return []
  return group.entities
    .map(name => ALL_CLASSES.find(c => c.localName === name))
    .filter((c): c is OntologyClass => c !== undefined)
}

export function getGroupForClass(localName: string): EntityGroup | undefined {
  for (const domain of Object.keys(ENTITY_GROUPS) as OntologyDomain[]) {
    const group = ENTITY_GROUPS[domain].find(g => g.entities.includes(localName))
    if (group) return group
  }
  return undefined
}

// ----------------------------------------------------------------------------
// CROSS-LAYER RELATIONSHIPS
// ----------------------------------------------------------------------------

export interface CrossLayerRelationship {
  from: { domain: OntologyDomain; className: string }
  to: { domain: OntologyDomain; className: string }
  relationship: string
  description: string
  traceabilityPhase?: 'Discovery' | 'Shape' | 'Launch'
}

export const CROSS_LAYER_RELATIONSHIPS: CrossLayerRelationship[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // MARKET → BUSINESS LAYER TRACEABILITY
  // Market insights inform business strategy and value creation
  // ═══════════════════════════════════════════════════════════════════════════
  { from: { domain: 'market', className: 'CustomerSegment' }, to: { domain: 'business', className: 'ValueProposition' }, relationship: 'targets', description: 'Value proposition targets a customer segment', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'CustomerSegment' }, to: { domain: 'business', className: 'GoToMarketStrategy' }, relationship: 'targetsSegment', description: 'GTM strategy targets specific segments', traceabilityPhase: 'Launch' },
  { from: { domain: 'market', className: 'CustomerPain' }, to: { domain: 'business', className: 'ValueProposition' }, relationship: 'solves', description: 'Value proposition solves customer pains', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'CustomerPain' }, to: { domain: 'solution', className: 'SolutionVision' }, relationship: 'addressesPain', description: 'Solution vision addresses customer pains', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Trend' }, to: { domain: 'business', className: 'Opportunity' }, relationship: 'linkedToTrend', description: 'Market trend creates business opportunity', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Competitor' }, to: { domain: 'business', className: 'Threat' }, relationship: 'linkedToCompetitor', description: 'Competitor creates business threat', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Competitor' }, to: { domain: 'business', className: 'Positioning' }, relationship: 'differentiatesFrom', description: 'Positioning differentiates from competitors', traceabilityPhase: 'Shape' },

  // ═══════════════════════════════════════════════════════════════════════════
  // BUSINESS → SOLUTION LAYER TRACEABILITY
  // Business strategy drives product development
  // ═══════════════════════════════════════════════════════════════════════════
  { from: { domain: 'business', className: 'ValueProposition' }, to: { domain: 'solution', className: 'Product' }, relationship: 'implements', description: 'Product implements value proposition', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'StrategicGoal' }, to: { domain: 'solution', className: 'Theme' }, relationship: 'operationalizedBy', description: 'Theme operationalizes strategic goal', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'StrategicGoal' }, to: { domain: 'business', className: 'Milestone' }, relationship: 'achieves', description: 'Milestone achieves strategic goal', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'KeyActivity' }, to: { domain: 'solution', className: 'Epic' }, relationship: 'realizes', description: 'Epic realizes key activity', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'GoToMarketStrategy' }, to: { domain: 'solution', className: 'LaunchPlan' }, relationship: 'executesGTM', description: 'Launch plan executes GTM strategy', traceabilityPhase: 'Launch' },
  { from: { domain: 'business', className: 'GoToMarketStrategy' }, to: { domain: 'business', className: 'Positioning' }, relationship: 'hasPositioning', description: 'GTM includes market positioning', traceabilityPhase: 'Launch' },
  { from: { domain: 'business', className: 'Milestone' }, to: { domain: 'solution', className: 'LaunchPlan' }, relationship: 'includesMilestone', description: 'Launch plan includes milestones', traceabilityPhase: 'Launch' },

  // ═══════════════════════════════════════════════════════════════════════════
  // SOLUTION LAYER INTERNAL TRACEABILITY
  // Vision → Strategy → Product → Features
  // ═══════════════════════════════════════════════════════════════════════════
  { from: { domain: 'solution', className: 'SolutionVision' }, to: { domain: 'solution', className: 'Product' }, relationship: 'guidesProduct', description: 'Vision guides product development', traceabilityPhase: 'Discovery' },
  { from: { domain: 'solution', className: 'SolutionVision' }, to: { domain: 'solution', className: 'SolutionStrategy' }, relationship: 'implementsVision', description: 'Strategy implements vision', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Theme' }, to: { domain: 'solution', className: 'Epic' }, relationship: 'contains', description: 'Theme contains epics', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Epic' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'delivers', description: 'Epic delivers features', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Feature' }, to: { domain: 'solution', className: 'UserStory' }, relationship: 'hasUserStory', description: 'Feature has user stories', traceabilityPhase: 'Shape' },

  // ═══════════════════════════════════════════════════════════════════════════
  // SOLUTION → MARKET (CLOSING THE LOOP)
  // Validating that solutions address market needs
  // ═══════════════════════════════════════════════════════════════════════════
  { from: { domain: 'solution', className: 'Feature' }, to: { domain: 'market', className: 'CustomerPain' }, relationship: 'mitigates', description: 'Feature mitigates customer pain', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'UserStory' }, to: { domain: 'market', className: 'Persona' }, relationship: 'forPersona', description: 'User story written for persona', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'SolutionVision' }, to: { domain: 'market', className: 'CustomerSegment' }, relationship: 'targetedAt', description: 'Vision targeted at customer segment', traceabilityPhase: 'Discovery' },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW RELATIONSHIPS - HIGH PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════

  // Market Layer relationships
  { from: { domain: 'market', className: 'Market' }, to: { domain: 'market', className: 'Industry' }, relationship: 'containsIndustry', description: 'Market contains industries', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'MarketOpportunity' }, to: { domain: 'market', className: 'Market' }, relationship: 'inMarket', description: 'Opportunity exists in market', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'MarketOpportunity' }, to: { domain: 'market', className: 'Trend' }, relationship: 'drivenByTrend', description: 'Opportunity driven by trend', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'CustomerJourney' }, to: { domain: 'market', className: 'CustomerSegment' }, relationship: 'forSegment', description: 'Journey defined for segment', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'JourneyStage' }, to: { domain: 'market', className: 'CustomerJourney' }, relationship: 'partOfJourney', description: 'Stage is part of journey', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Touchpoint' }, to: { domain: 'market', className: 'JourneyStage' }, relationship: 'inStage', description: 'Touchpoint occurs in stage', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'CustomerNeed' }, to: { domain: 'market', className: 'CustomerPain' }, relationship: 'manifestsAsPain', description: 'Unmet need manifests as pain', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Regulation' }, to: { domain: 'market', className: 'Industry' }, relationship: 'affectsIndustry', description: 'Regulation affects industry', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Demographic' }, to: { domain: 'market', className: 'CustomerSegment' }, relationship: 'describesSegment', description: 'Demographics describe segment', traceabilityPhase: 'Discovery' },
  { from: { domain: 'market', className: 'Psychographic' }, to: { domain: 'market', className: 'Persona' }, relationship: 'describesPersona', description: 'Psychographics describe persona', traceabilityPhase: 'Discovery' },

  // Business Layer relationships
  { from: { domain: 'business', className: 'Metric' }, to: { domain: 'business', className: 'StrategicGoal' }, relationship: 'measuresGoal', description: 'Metric measures strategic goal', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'Experiment' }, to: { domain: 'business', className: 'Assumption' }, relationship: 'testsAssumption', description: 'Experiment validates assumption', traceabilityPhase: 'Discovery' },
  { from: { domain: 'business', className: 'Assumption' }, to: { domain: 'business', className: 'ValueProposition' }, relationship: 'relatedToVP', description: 'Assumption about value proposition', traceabilityPhase: 'Discovery' },
  { from: { domain: 'business', className: 'Partner' }, to: { domain: 'business', className: 'KeyResource' }, relationship: 'providesResource', description: 'Partner provides key resource', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'Partner' }, to: { domain: 'business', className: 'KeyPartnership' }, relationship: 'partOfPartnership', description: 'Partner is part of partnership', traceabilityPhase: 'Shape' },
  { from: { domain: 'business', className: 'PricingModel' }, to: { domain: 'solution', className: 'Product' }, relationship: 'appliesTo', description: 'Pricing applies to product', traceabilityPhase: 'Launch' },
  { from: { domain: 'business', className: 'CustomerAcquisition' }, to: { domain: 'market', className: 'CustomerSegment' }, relationship: 'targetsSegment', description: 'Acquisition targets segment', traceabilityPhase: 'Launch' },
  { from: { domain: 'business', className: 'RetentionStrategy' }, to: { domain: 'market', className: 'CustomerSegment' }, relationship: 'forSegment', description: 'Retention strategy for segment', traceabilityPhase: 'Launch' },

  // Solution Layer relationships
  { from: { domain: 'solution', className: 'Release' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'includesFeature', description: 'Release includes features', traceabilityPhase: 'Launch' },
  { from: { domain: 'solution', className: 'Release' }, to: { domain: 'solution', className: 'Product' }, relationship: 'ofProduct', description: 'Release of product', traceabilityPhase: 'Launch' },
  { from: { domain: 'solution', className: 'Sprint' }, to: { domain: 'solution', className: 'UserStory' }, relationship: 'containsStory', description: 'Sprint contains user stories', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Sprint' }, to: { domain: 'solution', className: 'Task' }, relationship: 'containsTask', description: 'Sprint contains tasks', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Task' }, to: { domain: 'solution', className: 'UserStory' }, relationship: 'implementsStory', description: 'Task implements user story', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Task' }, to: { domain: 'solution', className: 'Sprint' }, relationship: 'inSprint', description: 'Task is in sprint', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Dependency' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'blocksEntity', description: 'Dependency blocks feature', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'AcceptanceCriteria' }, to: { domain: 'solution', className: 'UserStory' }, relationship: 'forStory', description: 'AC defines story completion', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'AcceptanceCriteria' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'forFeature', description: 'AC defines feature completion', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'TestCase' }, to: { domain: 'solution', className: 'AcceptanceCriteria' }, relationship: 'validatesAcceptanceCriteria', description: 'Test validates AC', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'TestCase' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'coversFeature', description: 'Test covers feature', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'TechnicalDebt' }, to: { domain: 'solution', className: 'Component' }, relationship: 'affectsComponent', description: 'Debt affects component', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Component' }, to: { domain: 'solution', className: 'Feature' }, relationship: 'implementsFeature', description: 'Component implements features', traceabilityPhase: 'Shape' },
  { from: { domain: 'solution', className: 'Component' }, to: { domain: 'solution', className: 'Component' }, relationship: 'dependsOnComponent', description: 'Component dependency', traceabilityPhase: 'Shape' },

  // Cross-layer relationships
  { from: { domain: 'market', className: 'Regulation' }, to: { domain: 'solution', className: 'Constraint' }, relationship: 'mandates', description: 'Regulation mandates constraint', traceabilityPhase: 'Discovery' },
  { from: { domain: 'business', className: 'MarketOpportunity' }, to: { domain: 'business', className: 'Strategy' }, relationship: 'informsStrategy', description: 'Opportunity informs strategy', traceabilityPhase: 'Discovery' },
]

// ----------------------------------------------------------------------------
// TRACEABILITY CHAINS
// End-to-end traceability paths through the ontology
// ----------------------------------------------------------------------------

export interface TraceabilityChain {
  name: string
  description: string
  phase: 'Discovery' | 'Shape' | 'Launch'
  steps: Array<{
    from: string
    relationship: string
    to: string
    explanation: string
  }>
}

export const TRACEABILITY_CHAINS: TraceabilityChain[] = [
  {
    name: 'Market Signal to Feature',
    description: 'How market signals drive feature development',
    phase: 'Discovery',
    steps: [
      { from: 'MarketSignal', relationship: 'indicatesTrend', to: 'Trend', explanation: 'Market signals indicate emerging trends' },
      { from: 'Trend', relationship: 'linkedToTrend', to: 'Opportunity', explanation: 'Trends create business opportunities' },
      { from: 'Opportunity', relationship: 'informs', to: 'StrategicGoal', explanation: 'Opportunities inform strategic goals' },
      { from: 'StrategicGoal', relationship: 'operationalizedBy', to: 'Theme', explanation: 'Goals become product themes' },
      { from: 'Theme', relationship: 'contains', to: 'Epic', explanation: 'Themes contain epics' },
      { from: 'Epic', relationship: 'delivers', to: 'Feature', explanation: 'Epics deliver features' },
    ],
  },
  {
    name: 'Customer Pain to Feature',
    description: 'How customer problems become product features',
    phase: 'Discovery',
    steps: [
      { from: 'Persona', relationship: 'hasPain', to: 'CustomerPain', explanation: 'Personas experience pains' },
      { from: 'CustomerPain', relationship: 'solves', to: 'ValueProposition', explanation: 'Value propositions address pains' },
      { from: 'ValueProposition', relationship: 'implements', to: 'Product', explanation: 'Products implement value props' },
      { from: 'Product', relationship: 'hasFeature', to: 'Feature', explanation: 'Products have features' },
      { from: 'Feature', relationship: 'mitigates', to: 'CustomerPain', explanation: 'Features mitigate the original pain' },
    ],
  },
  {
    name: 'Vision to Launch',
    description: 'How a solution vision becomes a launched product',
    phase: 'Shape',
    steps: [
      { from: 'SolutionVision', relationship: 'implementsVision', to: 'SolutionStrategy', explanation: 'Vision defines strategy' },
      { from: 'SolutionStrategy', relationship: 'guides', to: 'Product', explanation: 'Strategy guides product architecture' },
      { from: 'Product', relationship: 'hasFeature', to: 'Feature', explanation: 'Product defines features' },
      { from: 'Feature', relationship: 'partOf', to: 'Epic', explanation: 'Features organized into epics' },
      { from: 'Epic', relationship: 'tracksTo', to: 'Milestone', explanation: 'Epics track to milestones' },
      { from: 'Milestone', relationship: 'includesMilestone', to: 'LaunchPlan', explanation: 'Milestones form launch plan' },
    ],
  },
  {
    name: 'Strategy to Market',
    description: 'How business strategy reaches the market',
    phase: 'Launch',
    steps: [
      { from: 'Strategy', relationship: 'defines', to: 'StrategicGoal', explanation: 'Strategy defines goals' },
      { from: 'StrategicGoal', relationship: 'achieved', to: 'Milestone', explanation: 'Goals tracked via milestones' },
      { from: 'Positioning', relationship: 'partOf', to: 'GoToMarketStrategy', explanation: 'Positioning shapes GTM' },
      { from: 'GoToMarketStrategy', relationship: 'executesGTM', to: 'LaunchPlan', explanation: 'GTM executed via launch plan' },
      { from: 'LaunchPlan', relationship: 'targets', to: 'CustomerSegment', explanation: 'Launch reaches target segment' },
    ],
  },
]

// ----------------------------------------------------------------------------
// SWRL INFERENCE RULES
// Semantic Web Rule Language rules for automated reasoning
// ----------------------------------------------------------------------------

export interface SWRLRule {
  name: string
  description: string
  category: 'validation' | 'inference' | 'classification' | 'propagation'
  antecedent: string  // IF condition (human-readable)
  consequent: string  // THEN result (human-readable)
  swrlSyntax: string  // Actual SWRL rule syntax
  example?: string
}

export const SWRL_RULES: SWRLRule[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION RULES
  // Rules that ensure data integrity and completeness
  // ═══════════════════════════════════════════════════════════════════════════
  {
    name: 'Value Proposition Requires Customer Segment',
    description: 'Every value proposition must target at least one customer segment',
    category: 'validation',
    antecedent: 'ValueProposition(?vp) ∧ ¬targets(?vp, ?cs)',
    consequent: 'IncompleteValueProposition(?vp)',
    swrlSyntax: 'business:ValueProposition(?vp) ^ swrlx:notEmpty(?vp, business:targets) -> intellispot:IncompleteValueProposition(?vp)',
    example: 'If "AI-powered automation" VP has no customer segment, it is marked incomplete',
  },
  {
    name: 'Feature Must Mitigate Pain',
    description: 'Every feature should trace back to a customer pain it addresses',
    category: 'validation',
    antecedent: 'Feature(?f) ∧ ¬mitigates(?f, ?pain)',
    consequent: 'UnvalidatedFeature(?f)',
    swrlSyntax: 'solution:Feature(?f) ^ swrlx:notEmpty(?f, solution:mitigates) -> intellispot:UnvalidatedFeature(?f)',
    example: 'Feature "Dark mode" without linked pain is flagged for validation',
  },
  {
    name: 'User Story Needs Persona',
    description: 'User stories must be written for a specific persona',
    category: 'validation',
    antecedent: 'UserStory(?us) ∧ ¬forPersona(?us, ?p)',
    consequent: 'IncompleteUserStory(?us)',
    swrlSyntax: 'solution:UserStory(?us) ^ swrlx:notEmpty(?us, solution:forPersona) -> intellispot:IncompleteUserStory(?us)',
    example: '"As a user..." stories are flagged - need specific persona',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INFERENCE RULES
  // Rules that derive new facts from existing ones
  // ═══════════════════════════════════════════════════════════════════════════
  {
    name: 'High-Value Customer Pain',
    description: 'Pain with high severity across multiple personas is high-value',
    category: 'inference',
    antecedent: 'CustomerPain(?pain) ∧ hasSeverity(?pain, "High") ∧ Persona(?p1) ∧ Persona(?p2) ∧ hasPain(?p1, ?pain) ∧ hasPain(?p2, ?pain) ∧ differentFrom(?p1, ?p2)',
    consequent: 'HighValuePain(?pain)',
    swrlSyntax: 'market:CustomerPain(?pain) ^ market:hasSeverity(?pain, "High") ^ market:Persona(?p1) ^ market:Persona(?p2) ^ market:hasPain(?p1, ?pain) ^ market:hasPain(?p2, ?pain) ^ swrlb:notEqual(?p1, ?p2) -> intellispot:HighValuePain(?pain)',
    example: 'Manual data entry pain shared by CFO Clara and Finance Manager Fred → high-value',
  },
  {
    name: 'Competitive Threat Impact',
    description: 'Competitor with differentiation strategy in your segment creates feature pressure',
    category: 'inference',
    antecedent: 'Competitor(?c) ∧ employsStrategy(?c, "Differentiation") ∧ exertsPressure(?c, ?force) ∧ Threat(?t) ∧ linkedToCompetitor(?t, ?c)',
    consequent: 'RequiresFeatureResponse(?t)',
    swrlSyntax: 'market:Competitor(?c) ^ market:employsStrategy(?c, "Differentiation") ^ market:exertsPressure(?c, ?force) ^ business:Threat(?t) ^ business:linkedToCompetitor(?t, ?c) -> intellispot:RequiresFeatureResponse(?t)',
    example: 'HubSpot differentiating with AI features → threat requires feature response',
  },
  {
    name: 'GTM Readiness',
    description: 'Product with complete positioning and channel is GTM-ready',
    category: 'inference',
    antecedent: 'GoToMarketStrategy(?gtm) ∧ hasPositioning(?gtm, ?pos) ∧ usesChannel(?gtm, ?ch) ∧ targetsSegment(?gtm, ?seg)',
    consequent: 'GTMReady(?gtm)',
    swrlSyntax: 'business:GoToMarketStrategy(?gtm) ^ business:hasPositioning(?gtm, ?pos) ^ business:usesChannel(?gtm, ?ch) ^ business:targetsSegment(?gtm, ?seg) -> intellispot:GTMReady(?gtm)',
    example: 'GTM with positioning, channel, and segment is marked ready for launch',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLASSIFICATION RULES
  // Rules that categorize entities based on properties
  // ═══════════════════════════════════════════════════════════════════════════
  {
    name: 'Strategic Epic',
    description: 'Epic that realizes key activity and belongs to theme is strategic',
    category: 'classification',
    antecedent: 'Epic(?e) ∧ realizes(?e, ?ka) ∧ belongsTo(?e, ?theme) ∧ supports(?theme, ?goal)',
    consequent: 'StrategicEpic(?e)',
    swrlSyntax: 'solution:Epic(?e) ^ solution:realizes(?e, ?ka) ^ solution:belongsTo(?e, ?theme) ^ solution:supports(?theme, ?goal) -> intellispot:StrategicEpic(?e)',
    example: 'Invoice Automation epic linked to strategy → strategic priority',
  },
  {
    name: 'Launch-Critical Milestone',
    description: 'Milestone blocking launch plan is critical',
    category: 'classification',
    antecedent: 'Milestone(?m) ∧ LaunchPlan(?lp) ∧ includesMilestone(?lp, ?m) ∧ hasStatus(?m, "InProgress")',
    consequent: 'LaunchCriticalMilestone(?m)',
    swrlSyntax: 'business:Milestone(?m) ^ solution:LaunchPlan(?lp) ^ solution:includesMilestone(?lp, ?m) ^ business:hasStatus(?m, "InProgress") -> intellispot:LaunchCriticalMilestone(?m)',
    example: 'Beta milestone in launch plan and in progress → critical path',
  },
  {
    name: 'High-Risk Feature',
    description: 'Feature with associated high-impact risk is high-risk',
    category: 'classification',
    antecedent: 'Feature(?f) ∧ Risk(?r) ∧ impacts(?r, ?f) ∧ hasImpact(?r, "High")',
    consequent: 'HighRiskFeature(?f)',
    swrlSyntax: 'solution:Feature(?f) ^ business:Risk(?r) ^ intellispot:impacts(?r, ?f) ^ business:hasImpact(?r, "High") -> intellispot:HighRiskFeature(?f)',
    example: 'ML accuracy feature with high-impact risk → flagged for attention',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROPAGATION RULES
  // Rules that propagate properties through relationships
  // ═══════════════════════════════════════════════════════════════════════════
  {
    name: 'Pain Priority Propagation',
    description: 'High-severity pain propagates priority to features that mitigate it',
    category: 'propagation',
    antecedent: 'CustomerPain(?pain) ∧ hasSeverity(?pain, "High") ∧ Feature(?f) ∧ mitigates(?f, ?pain)',
    consequent: 'hasHighPriority(?f, true)',
    swrlSyntax: 'market:CustomerPain(?pain) ^ market:hasSeverity(?pain, "High") ^ solution:Feature(?f) ^ solution:mitigates(?f, ?pain) -> intellispot:hasHighPriority(?f, true)',
    example: 'High-severity "manual reconciliation" pain → high priority for auto-reconciliation feature',
  },
  {
    name: 'Strategic Goal to Epic Priority',
    description: 'Hard goals propagate urgency to their implementing epics',
    category: 'propagation',
    antecedent: 'StrategicGoal(?g) ∧ hasGoalType(?g, "HardGoal") ∧ Theme(?t) ∧ operationalizedBy(?g, ?t) ∧ Epic(?e) ∧ belongsTo(?e, ?t)',
    consequent: 'hasStrategicUrgency(?e, true)',
    swrlSyntax: 'business:StrategicGoal(?g) ^ business:hasGoalType(?g, "HardGoal") ^ solution:Theme(?t) ^ business:operationalizedBy(?g, ?t) ^ solution:Epic(?e) ^ solution:belongsTo(?e, ?t) -> intellispot:hasStrategicUrgency(?e, true)',
    example: 'Q4 revenue goal → urgent priority on revenue-driving epics',
  },
  {
    name: 'Trend Impact Propagation',
    description: 'Maturing trends propagate urgency through the value chain',
    category: 'propagation',
    antecedent: 'Trend(?t) ∧ hasMaturity(?t, "Maturing") ∧ Opportunity(?o) ∧ linkedToTrend(?o, ?t)',
    consequent: 'hasMarketUrgency(?o, true)',
    swrlSyntax: 'market:Trend(?t) ^ market:hasMaturity(?t, "Maturing") ^ business:Opportunity(?o) ^ business:linkedToTrend(?o, ?t) -> intellispot:hasMarketUrgency(?o, true)',
    example: 'Maturing AI trend → urgent to capture AI-related opportunities',
  },
]

// Helper to get rules by category
export function getSWRLRulesByCategory(category: SWRLRule['category']): SWRLRule[] {
  return SWRL_RULES.filter(r => r.category === category)
}
